import React, { useState, useEffect, Fragment, useRef, useMemo } from 'react';
import './App.css';
import Map from './Map';
import PublicMap from './PublicMap';
import LocationPicker from './LocationPicker';
import CompanyProfile from './CompanyProfile';
import JobProfile from './JobProfile';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import AdminPanel from './AdminPanel';
import AnnouncementsTab from './AnnouncementsTab';
import JobOffersTab from './JobOffersTab';
import BugReportTab from './BugReportTab';
import AiLogsViewer from './AiLogsViewer';
import CeidgImportModal from './CeidgImportModal';
import AgentManager from './AgentManager';
import AdminSeoAuditTab from './AdminSeoAuditTab';
import AdCampaignsTab from './AdCampaignsTab';
import PublishSkillCard from './PublishSkillCard';
import MySkillsTab from './MySkillsTab';
import GoogleMapsTab from './GoogleMapsTab';
import MCPServerTab from './MCPServerTab';
import AICrmManager from './AICrmManager';
import EventsManager from './EventsManager';
import AnalyticsDashboard from './AnalyticsDashboard';
import PhoneCallsTab from './PhoneCallsTab';
import AILandingVoiceCreator from './AILandingVoiceCreator';
import WebsiteBuilderTab from './WebsiteBuilderTab';
import MarketplaceFeed from './MarketplaceFeed';
import { speakTextWithNativeVoice, stopAllSpeech, speakAIResponse } from './ttsHelper';
import { useTranslation } from 'react-i18next';
import { PROFESSIONAL_TEMPLATES } from './EmailTemplates';
import { QRCodeSVG } from 'qrcode.react';
import DOMPurify from 'dompurify';
import { 
  AppShell, Tabs, Group, Title, Badge, Text, Button, ActionIcon, 
  TextInput, Textarea, Select, MultiSelect, TagsInput, Table, ScrollArea, Card, Grid, 
  Stack, Box, Flex, Pagination, Divider, Drawer, SimpleGrid, ThemeIcon, List,
  Modal, PinInput, NumberInput, Alert, Checkbox, Anchor, Container, Burger, Accordion, SegmentedControl, Affix, ColorInput, ColorPicker, Autocomplete, Paper, Avatar, Switch, Loader, Tooltip, FileInput, Image, Center
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconSearch, IconMail, IconMailForward, IconRefresh, IconDatabase,
  IconEdit, IconTrash, IconRocket, IconHistory, IconX, IconBan,
  IconChartBar, IconCloudDownload, IconMessages, IconSend, IconSettings, IconPassword,
  IconCalendar, IconCalendarEvent, IconPlus, IconClock, IconChevronLeft, IconChevronRight, IconLock, IconLogout,
  IconVariable, IconPlayerPlay, IconPlayerStop, IconBolt, IconWorld, IconCopy, IconCheck, IconCreditCard, IconPhoto, IconCode, IconRobot, IconSpeakerphone, IconMessage, IconBriefcase, IconBug, IconDatabaseImport, IconMapPin, IconCash, IconExternalLink, IconServer, IconFolder, IconFolderPlus, IconStar, IconInbox, IconFilter, IconTag, IconPalette, IconReceipt, IconBrandGoogle, IconBrandFacebook, IconBrandApple, IconShieldLock, IconUpload, IconVideo, IconShare, IconPlug, IconStethoscope, IconMicrophone, IconVolume, IconVolumeOff, IconShoppingCart, IconPhone, IconList, IconBrandWhatsapp, IconBrandYoutube, IconInfoCircle
} from '@tabler/icons-react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const EmailPreviewIframe = ({ html }) => {
  const styledHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <style>
          body { 
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; 
            padding: 15px; 
            margin: 0; 
            line-height: 1.6;
            word-wrap: break-word;
            overflow-x: hidden;
          }
          img { 
            max-width: 100% !important; 
            height: auto !important; 
            display: block;
            margin: 10px 0;
          }
          pre {
            white-space: pre-wrap;
            word-wrap: break-word;
            background: #f8fafc;
            padding: 10px;
            border-radius: 4px;
          }
          a { color: #4f46e5; }
        </style>
      </head>
      <body>${html}</body>
    </html>
  `;

  return (
    <iframe 
      srcDoc={styledHtml}
      title="Email Preview" 
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      style={{ width: '100%', height: '100%', border: 'none',  }} 
    />
  );
};

const NativeVisualEmailEditor = React.memo(({ value, onChange }) => {
  const iframeRef = useRef();
  const [editorReady, setEditorReady] = useState(false);
  const lastSentValue = useRef(value);

  useEffect(() => {
    if (iframeRef.current && !editorReady) {
      const doc = iframeRef.current.contentDocument;
      doc.open();
      // If value doesn't look like a full HTML doc, wrap it
      let htmlContent = value;
      if (!value || !value.includes('<html')) {
        htmlContent = `<!DOCTYPE html><html><head><style>body { font-family: sans-serif; padding: 20px; }</style></head><body>${value || ''}</body></html>`;
      }

      doc.write(htmlContent);
      doc.close();
      
      // Make body editable
      doc.body.contentEditable = 'true';
      doc.body.style.minHeight = '100vh';
      doc.body.style.cursor = 'text';

      const handleInput = () => {
        const fullHtml = `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
        lastSentValue.current = fullHtml;
        onChange(fullHtml);
      };

      doc.body.addEventListener('input', handleInput);
      setEditorReady(true);
    }
  }, [iframeRef.current]);

  // Sync external changes into editor (only if they are significant to avoid cursor jumps)
  useEffect(() => {
    if (editorReady && iframeRef.current && value !== lastSentValue.current) {
      const doc = iframeRef.current.contentDocument;
      const currentBody = doc.body.innerHTML;
      
      // Improved body extraction
      const match = value?.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      const newBody = match ? match[1] : (value || '');
      
      if (currentBody.trim() !== newBody.trim() && !doc.hasFocus()) {
         doc.body.innerHTML = newBody;
         lastSentValue.current = value;
      }
    }
  }, [value, editorReady]);

  const execCommand = (cmd, val = null) => {
    if (iframeRef.current) {
      iframeRef.current.contentDocument.execCommand(cmd, false, val);
      // Trigger change
      const doc = iframeRef.current.contentDocument;
      onChange(`<!DOCTYPE html>${doc.documentElement.outerHTML}`);
    }
  };

  return (
    <Box h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
      <Group gap={5} p={5} bg="#141517" style={{ borderBottom: '1px solid #2f3032' }}>
        <Button variant="subtle" size="compact-xs" onClick={() => execCommand('bold')}><b>B</b></Button>
        <Button variant="subtle" size="compact-xs" onClick={() => execCommand('italic')}><i>I</i></Button>
        <Button variant="subtle" size="compact-xs" onClick={() => execCommand('underline')}><u>U</u></Button>
        <Divider orientation="vertical" />
        <Button variant="subtle" size="compact-xs" onClick={() => execCommand('insertUnorderedList')}>List</Button>
        <Button variant="subtle" size="compact-xs" onClick={() => {
          const url = window.prompt("Enter URL:");
          if(url) execCommand('createLink', url);
        }}>Link</Button>
        <Button variant="subtle" size="compact-xs" color="red" onClick={() => execCommand('removeFormat')}>Clear</Button>
      </Group>
      <iframe 
        ref={iframeRef} 
        title="Visual Editor" 
        style={{ flex: 1, border: 'none',  }} 
      />
    </Box>
  );
}, (prevProps, nextProps) => prevProps.value === nextProps.value);

const WhatsAppComposeModal = ({ opened, onClose, onSend, data, setData, loading }) => {
  return (
    <Modal opened={opened} onClose={onClose} title={<Group gap="xs"><IconMessages size={20} color="#25D366"/><Text fw={700}>New WhatsApp Message</Text></Group>} radius="md">
      <Stack gap="md">
        <TextInput label="Phone Number" placeholder="e.g. 48123456789" required value={data.to} onChange={e => setData({...data, to: e.target.value})} />
        <Textarea label="Message" placeholder="Type your message here..." minRows={5} required value={data.text} onChange={e => setData({...data, text: e.target.value})} />
        <Group justify="flex-end">
          <Button variant="subtle" color="gray" onClick={onClose}>Cancel</Button>
          <Button color="green" onClick={onSend} loading={loading} leftSection={<IconSend size={16}/>}>Send WhatsApp</Button>
        </Group>
      </Stack>
    </Modal>
  );
};

const EmailComposeModal = ({ opened, onClose, onSend, isEmailConfigured, emailTemplates, initialData, targetLeadId, senderOptions, user, leads }) => {
  const { t } = useTranslation();
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [isComposeHtml, setIsComposeHtml] = useState(false);
  const [isComposePreview, setIsComposePreview] = useState(false);
  const [isRedacting, setIsRedacting] = useState(false);
  const [attachments, setAttachments] = useState([]);
  
  const suggestionData = useMemo(() => {
    if (!leads || !Array.isArray(leads)) return [];
    return leads
      .filter(l => l.email && l.email.trim() !== '')
      .map(l => {
        const name = l.companyName || l.contactPerson || '';
        return name ? `${name} <${l.email}>` : l.email;
      });
  }, [leads]);

  // Fix: We only want to initialize state when the modal opens or when initialData changes its reference, 
  // but NOT when senderOptions changes reference, to prevent form reset during typing/selecting.
  useEffect(() => {
    if (opened) {
      setEmailTo(initialData?.to || '');
      setEmailSubject(initialData?.subject || '');
      setEmailBody(initialData?.body || '');
      setSenderEmail(initialData?.senderEmail || (senderOptions && senderOptions.length > 0 ? senderOptions[0] : ''));
      setIsComposeHtml(initialData?.isHtml || false);
      setIsComposePreview(false);
      setAttachments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, initialData]);

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    let finalEmailTo = emailTo;
    const match = emailTo.match(/<([^>]+)>/);
    if (match) {
      finalEmailTo = match[1].trim();
    }
    onSend({ to: finalEmailTo, subject: emailSubject, body: emailBody, senderEmail, attachments });
  };

  const handleAIRedact = async () => {
    if (initialData?.draftReply && emailBody !== initialData.draftReply && !emailBody.includes(initialData.draftReply)) {
      let draft = initialData.draftReply;
      if (isComposeHtml && !draft.includes('<br')) {
        draft = draft.replace(/\n/g, '<br/>');
      }
      if (emailBody.includes('---')) {
        const parts = emailBody.split('---');
        setEmailBody(draft + '<br/><br/>---' + parts.slice(1).join('---'));
      } else {
        setEmailBody(draft);
      }
      return;
    }
    if (initialData?.msgId) {
      setIsRedacting(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/crm/messages/${initialData.msgId}/analyze?ownerEmail=${encodeURIComponent(user?.email || '')}`, {
          method: 'POST',
          headers: { 'X-Owner-Email': user?.email || '' }
        });
        if (res.ok) {
          const updated = await res.json();
          if (updated && updated.draftReply) {
            let draft = updated.draftReply;
            if (isComposeHtml && !draft.includes('<br')) {
              draft = draft.replace(/\n/g, '<br/>');
            }
            if (emailBody.includes('---')) {
              const parts = emailBody.split('---');
              setEmailBody(draft + '<br/><br/>---' + parts.slice(1).join('---'));
            } else {
              setEmailBody(draft);
            }
          } else {
            alert(t('ai_draft_empty'));
          }
        } else {
          alert(t('ai_draft_error'));
        }
      } catch (e) {
        console.error("AI redact fail", e);
        alert(t('ai_draft_error'));
      } finally {
        setIsRedacting(false);
      }
    } else {
      alert(t('ai_redact_no_incoming'));
    }
  };

  return (
    <Modal 
      opened={opened} 
      onClose={onClose} 
      title={t('compose_email_title')} 
      size="xl" 
      radius="md"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {senderOptions && senderOptions.length > 0 && (
            <Select 
              label={t('compose_from')} 
              data={senderOptions} 
              value={senderEmail} 
              onChange={setSenderEmail} 
            />
          )}
          <Autocomplete 
            label={t('compose_to')} 
            required 
            value={emailTo} 
            onChange={setEmailTo} 
            disabled={!!targetLeadId} 
            data={suggestionData}
            placeholder={t('compose_to_placeholder')}
            limit={8}
          />
          <TextInput label={t('compose_subject')} required value={emailSubject} onChange={e => setEmailSubject(e.target.value)} />
          
          <FileInput
            multiple
            clearable
            label={t('compose_attachments')}
            placeholder={t('compose_attachments_placeholder')}
            value={attachments}
            onChange={setAttachments}
          />
          
          <Box>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={500}>{t('compose_body')}</Text>
              <Group gap="xs">
                <Button 
                  size="xs" 
                  variant="gradient" 
                  gradient={{ from: 'indigo', to: 'violet', deg: 45 }} 
                  loading={isRedacting}
                  onClick={handleAIRedact}
                >
                  {t('btn_ai_redact_reply')}
                </Button>
                <Select
                  placeholder={t('compose_load_template')}
                  size="xs"
                  searchable
                  clearable
                  data={[
                    { group: 'Built-in', items: PROFESSIONAL_TEMPLATES.map(t => ({ value: 'sys_' + t.id, label: t.name })) },
                    { group: 'My Templates', items: emailTemplates.map(t => ({ value: 'usr_' + t.id, label: t.name })) }
                  ]}
                  onChange={(val) => {
                    if (!val) return;
                    let t = null;
                    if (val.startsWith('sys_')) t = PROFESSIONAL_TEMPLATES.find(x => x.id === val.replace('sys_', ''));
                    else t = emailTemplates.find(x => x.id.toString() === val.replace('usr_', ''));
                    if (t) {
                      setEmailBody(t.html || t.htmlContent);
                      if (t.subject) setEmailSubject(t.subject);
                    }
                  }}
                />
                <SegmentedControl 
                  size="xs" 
                  data={[{ label: 'Text', value: 'text' }, { label: 'HTML', value: 'html' }, { label: 'Preview', value: 'preview' }]} 
                  value={isComposePreview ? 'preview' : (isComposeHtml ? 'html' : 'text')} 
                  onChange={v => {
                    if (v === 'preview') setIsComposePreview(true);
                    else {
                      setIsComposePreview(false);
                      setIsComposeHtml(v === 'html');
                    }
                  }} 
                />
              </Group>
            </Group>
            
            {isComposePreview ? (
              <Box style={{ height: '400px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <EmailPreviewIframe html={emailBody} />
              </Box>
            ) : isComposeHtml ? (
              <Textarea spellCheck={false} minRows={15} value={emailBody} onChange={e => setEmailBody(e.target.value)} styles={{ input: { fontFamily: 'monospace' } }} />
            ) : (
              <Box style={{ height: '400px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <NativeVisualEmailEditor value={emailBody} onChange={setEmailBody} />
              </Box>
            )}
          </Box>
          
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>{t('btn_cancel')}</Button>
            <Button type="submit" color="indigo" leftSection={<IconSend size={16}/>} disabled={!isEmailConfigured()}>
              {isEmailConfigured() ? t('btn_send_message') : t('smtp_not_configured')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export function LivePreviewChat({ companyName, services, aboutUs, headerColor, customHtml }) {
  const [messages, setMessages] = useState([{ role: 'AI', content: `Hello! I'm the AI assistant for ${companyName || 'this company'}. How can I help you today?` }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    setMessages([{ role: 'AI', content: `Hello! I'm the AI assistant for ${companyName || 'this company'}. How can I help you today?` }]);
  }, [companyName, services, aboutUs]);

  const [previewSessionId, setPreviewSessionId] = useState(null);

  useEffect(() => {
      let interval;
      if (previewSessionId && loading) {
          interval = setInterval(async () => {
              try {
                  const res = await fetch(`${API_BASE_URL}/api/chat/history/${previewSessionId}`);
                  if (res.ok) {
                      const data = await res.json();
                      if (data && data.length > 0) {
                          const lastMsg = data[data.length - 1];
                          if (lastMsg.role === 'AI' || lastMsg.role === 'OWNER') {
                              setMessages(prev => {
                                  // Ensure we don't duplicate
                                  const alreadyExists = prev.find(p => p.content === lastMsg.content && p.role === lastMsg.role);
                                  if (!alreadyExists) {
                                      return [...prev, { role: lastMsg.role, content: lastMsg.content }];
                                  }
                                  return prev;
                              });
                              setLoading(false);
                              setPreviewSessionId(null);
                          }
                      }
                  }
              } catch(e) {}
          }, 1000);
      }
      return () => clearInterval(interval);
  }, [previewSessionId, loading]);

  const handleSend = () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'USER', content: userMsg }]);
    setLoading(true);
    
    fetch(`${API_BASE_URL}/api/chat/live-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            companyName,
            services,
            aboutUs,
            query: userMsg,
            ownerEmail: 'preview'
        })
    })
    .then(r => r.json())
    .then(data => {
        if (data.sessionId) {
            setPreviewSessionId(data.sessionId);
        } else {
            setMessages(prev => [...prev, { role: 'AI', content: data.message }]);
            setLoading(false);
        }
    })
    .catch(e => {
        setMessages(prev => [...prev, { role: 'AI', content: 'Connection error.' }]);
        setLoading(false);
    });
  };

  if (customHtml && customHtml.trim() !== '') {
    let html = customHtml;
    html = html.replace(/{{companyName}}/gi, companyName || '');
    html = html.replace(/{{description}}/gi, aboutUs || '');
    if (services) {
       html = html.replace(/{{services}}/gi, services.map(s => '<div><b>'+s.name+'</b><p>'+s.description+'</p></div>').join(''));
    }
    const cleanHtml = DOMPurify.sanitize(html);
    return <Box h="100%" w="100%" style={{ overflowY: 'auto',  }} dangerouslySetInnerHTML={{ __html: cleanHtml }} />;
  }

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%',  }}>
      <Box p="sm" bg={headerColor || 'indigo.6'} c="white">
        <Text fw={700} size="sm">AI Chat Preview</Text>
      </Box>
      <ScrollArea style={{ flex: 1 }} p="md" viewportRef={scrollRef}>
        <Stack gap="xs">
          {messages.map((m, i) => (
            <Box key={i} style={{ alignSelf: m.role === 'USER' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
              <Paper p="xs" radius="md" style={{ background: m.role === 'USER' ? '#eef2ff' : '#f1f5f9', color: '#334155' }}>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{m.content}</Text>
              </Paper>
            </Box>
          ))}
          {loading && <Text size="xs" c="dimmed">AI is typing...</Text>}
        </Stack>
      </ScrollArea>
      <Box p="xs" style={{ borderTop: '1px solid #e2e8f0' }}>
        <Group gap="xs">
          <TextInput 
            style={{ flex: 1 }} 
            placeholder="Type a message..." 
            value={input} 
            onChange={e => setInput(e.target.value)} 
            disabled={loading} 
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } }} 
          />
          <ActionIcon color="indigo" onClick={handleSend} loading={loading}>
            <IconSend size={16}/>
          </ActionIcon>
        </Group>
      </Box>
    </Box>
  );
}

function LeadSeoRowStats({ lead, user, apiBaseUrl, isExpanded }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isExpanded && lead.website) {
      setLoading(true);
      fetch(`${apiBaseUrl}/api/leads/${lead.id}/seo-stats?ownerEmail=${user?.email}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          setStats(data);
          setLoading(false);
        })
        .catch(e => {
          console.error(e);
          setError(e.message);
          setLoading(false);
        });
    }
  }, [isExpanded, lead.id, lead.website, user?.email, apiBaseUrl]);

  if (!isExpanded || !lead.website) return null;
  if (loading) return <Text size="xs" c="dimmed" mt="xs">Ładowanie statystyk SEO...</Text>;
  if (error) return null;

  if (stats && stats.hasSeo) {
    return (
      <Box mt="sm" p="sm" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
        <Group justify="space-between" mb="xs">
          <Text size="xs" fw={700} c="green.9" tt="uppercase">Wyniki Audytu SEO: {stats.score} / 100</Text>
          <Text size="xs" c="dimmed">{stats.auditedAt?.substring(0, 16).replace('T', ' ')}</Text>
        </Group>
        <Group gap="xs">
          <Badge size="xs" color={stats.googleStatus?.includes('Indexed') || stats.googleStatus?.includes('Zaindeksowana') ? 'green' : 'red'} variant="light">
            Google: {stats.googleStatus}
          </Badge>
          <Badge size="xs" color={stats.mobileStatus?.includes('Mobile') || stats.mobileStatus?.includes('mobile') ? 'green' : 'orange'} variant="light">
            Mobile: {stats.mobileStatus}
          </Badge>
          <Badge size="xs" color={stats.sitemap ? 'green' : 'red'} variant="light">Sitemap: {stats.sitemap ? 'Tak' : 'Nie'}</Badge>
          <Badge size="xs" color={stats.ssl ? 'green' : 'red'} variant="light">SSL: {stats.ssl ? 'Tak' : 'Nie'}</Badge>
        </Group>
        {(stats.titleIssue || stats.descIssue) && (
          <Box mt="xs">
            {stats.titleIssue && (
              <Text size="xs" c={(stats.titleIssue.startsWith('TAK') || stats.titleIssue.startsWith('Tak')) ? 'green.9' : 'red.9'}>
                {(stats.titleIssue.startsWith('TAK') || stats.titleIssue.startsWith('Tak')) ? '✅' : '❗'} {t('seo_title_label')}: {stats.titleIssue}
              </Text>
            )}
            {stats.descIssue && (
              <Text size="xs" c={(stats.descIssue.startsWith('TAK') || stats.descIssue.startsWith('Tak')) ? 'green.9' : 'red.9'}>
                {(stats.descIssue.startsWith('TAK') || stats.descIssue.startsWith('Tak')) ? '✅' : '❗'} {t('seo_desc_label')}: {stats.descIssue}
              </Text>
            )}
          </Box>
        )}
      </Box>
    );
  }
  return <Text size="xs" c="dimmed" mt="xs">Brak wyników audytu SEO dla tej strony.</Text>;
}

function CoreApp() {
  const { t, i18n } = useTranslation();
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  
  const [isWidgetMode, setIsWidgetMode] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get('token') !== null || p.get('calendarToken') !== null || p.get('order_token') !== null;
    } catch(e) { return false; }
  });

  // Widget Mode State
  const [widgetToken, setWidgetToken] = useState(null);
  const [widgetTemplateKey, setWidgetTemplateKey] = useState(null);
  const [widgetVisitorEmail, setWidgetVisitorEmail] = useState('');
  const [isVisitorIdentified, setIsVisitorIdentified] = useState(false);

  // Auto-identify moved below

  // Calendar Widget Mode State
  const [isProfileMode, setIsProfileMode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('profile') !== null; } catch(e) { return false; }
  });
  const [isJobProfileMode, setIsJobProfileMode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('job') !== null; } catch(e) { return false; }
  });
  const [isLivePreviewMode, setIsLivePreviewMode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('live_preview') === 'true'; } catch(e) { return false; }
  });
  const [isTermsMode, setIsTermsMode] = useState(() => {
    try { return window.location.pathname === '/terms' || window.location.pathname === '/terms/'; } catch(e) { return false; }
  });
  const [isCalendarWidgetMode, setIsCalendarWidgetMode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('calendarToken') !== null; } catch(e) { return false; }
  });
  const [calendarToken, setCalendarToken] = useState(null);
  const [isBookingSuccess, setIsBookingSuccess] = useState(false);
  const [bookingDuration, setBookingDuration] = useState('30'); 
  const [busySlots, setBusySlots] = useState([]);

  // Order Widget State
  const [isOrderWidgetMode, setIsOrderWidgetMode] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('order_token') !== null; } catch(e) { return false; }
  });
  const [orderToken, setOrderToken] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState(null);

  // Automation State
  const [campaigns, setCampaigns] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isCampaignEditing, setIsCampaignEditing] = useState(false);
  const [isTemplateGalleryOpen, setIsTemplateGalleryOpen] = useState(false);
  const [campaignForm, setCampaignForm] = useState({ 
    name: '', subject: '', htmlContent: '', frequency: 'ONCE', active: false, sendDelaySeconds: 60,
    ownerEmail: '', targetCategory: 'ALL', senderEmail: '', autoTranslate: false
  });
  const [campaignAttachments, setCampaignAttachments] = useState([]);
  const [templateForm, setTemplateForm] = useState({ id: null, name: '', subject: '', htmlContent: '' });
  const [isTemplateEditing, setIsTemplateEditing] = useState(false);
  const [isCampaignHtmlMode, setIsCampaignHtmlMode] = useState(false);
  const [isTemplateHtmlMode, setIsTemplateHtmlMode] = useState(false);
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
  const [currentPreviewHtml, setTemplatePreviewHtml] = useState('');

  const [user, setUser] = useState(JSON.parse(localStorage.getItem('ailinkb_user')));
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem('ailinkb_cart') || '[]'));
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  
  const addToCart = (item) => {
    const newCart = [...cart, item];
    setCart(newCart);
    localStorage.setItem('ailinkb_cart', JSON.stringify(newCart));
    setCartDrawerOpen(true);
  };
  
  const removeFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    localStorage.setItem('ailinkb_cart', JSON.stringify(newCart));
  };
  
  const clearCart = () => {
    setCart([]);
    localStorage.setItem('ailinkb_cart', '[]');
  };
  const [authEmail, setAuthEmail] = useState('');
  const [authPin, setAuthPin] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [isRegistering, setIsRegistering] = useState(() => { try { if (localStorage.getItem('editing_profile') === 'true' || new URLSearchParams(window.location.search).get('edit_profile') === 'true') return true; const params = new URLSearchParams(window.location.search); return params.get('register') !== null || params.get('affiliate') !== null; } catch(e) { return false; } });
  const [isEditingPublicProfile, setIsEditingPublicProfile] = useState(() => localStorage.getItem('editing_profile') === 'true' || new URLSearchParams(window.location.search).get('edit_profile') === 'true');
  const [authError, setAuthError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsModalOpened, setTermsModalOpened] = useState(false);
  const [socialModalOpened, setSocialModalOpened] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('Google');
  const [socialEmail, setSocialEmail] = useState('');
  const [plansModalOpened, setPlansModalOpened] = useState(false);

  // Onboarding State
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [onboardingStep, setOnboardingStep] = useState(() => { try { const params = new URLSearchParams(window.location.search); return (params.get('register') !== null || params.get('login') !== null || params.get('affiliate') !== null) ? 'form' : 'landing'; } catch(e) { return 'landing'; } });
  const [companyDesc, setCompanyDesc] = useState('');
  const [authCompanyName, setAuthCompanyName] = useState('');
  const [authLocation, setAuthLocation] = useState('');
  const [authMapLat, setAuthMapLat] = useState('');
  const [authMapLng, setAuthMapLng] = useState('');
  const [authCustomDomain, setAuthCustomDomain] = useState('');
  const [authAffiliateEnabled, setAuthAffiliateEnabled] = useState(true);
  const [authCalendarEnabled, setAuthCalendarEnabled] = useState(false);
  const [authAffiliateCode, setAuthAffiliateCode] = useState(() => { try { return new URLSearchParams(window.location.search).get('affiliate') || ''; } catch(e) { return ''; } });
  const [authHeaderColor, setAuthHeaderColor] = useState('#000000');
  const [authPageBg, setAuthPageBg] = useState('#0a0a0a');
  const [authServices, setAuthServices] = useState([{ name: '', description: '' }]);
  const [authCustomHtml, setAuthCustomHtml] = useState('');
  const [generatedToken, setGeneratedToken] = useState('');

  const [imagePrompt, setImagePrompt] = useState('');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  // Force onboarding step if affiliate is in URL
  useEffect(() => {
    if (window.location.search.includes('affiliate=') || window.location.href.includes('affiliate=')) {
      setIsRegistering(true);
      setOnboardingStep('form');
    }
  }, []);

  // COM Domain Redirect Logic
  const isComDomain = window.location.hostname === 'ailinkb.com' || window.location.hostname === 'www.ailinkb.com';
  
  useEffect(() => {
    if (isComDomain && onboardingStep === 'form') {
      window.location.href = isRegistering 
        ? 'https://www.ailinkb.shop/?register=true' 
        : 'https://www.ailinkb.shop/?login=true';
    }
  }, [onboardingStep, isRegistering, isComDomain]);

  useEffect(() => {
    if (isComDomain && user) {
      window.location.href = 'https://www.ailinkb.shop/';
    }
  }, [user, isComDomain]);

  const generateHeaderImage = async (email, promptText) => {
    if (!promptText || !promptText.trim()) return;
    setIsGeneratingImage(true);


    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/image/generate-header`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerEmail: email, prompt: promptText })
      });
      if (res.ok) {
        let attempts = 0;
        const interval = setInterval(async () => {
          attempts++;
          const cRes = await fetch(`${API_BASE_URL}/api/config?ownerEmail=${email}`);
          const cData = await cRes.json();
          if (cData['company.header.image']) {
            setConfig(prev => ({ ...prev, 'company.header.image': cData['company.header.image'] }));
            setIsGeneratingImage(false);
            clearInterval(interval);
          }
          if (attempts > 120) {
            setIsGeneratingImage(false);
            clearInterval(interval);
            alert(t('ai_image_timeout'));
          }
        }, 5000);
      }
    } catch (err) {
      console.error(err);
      setIsGeneratingImage(false);
    }
  };

  const [authHeaderImage, setAuthHeaderImage] = useState('');
  const [authLogo, setAuthLogo] = useState('');
  const [authHeaderImagePrompt, setAuthHeaderImagePrompt] = useState('');
  const [authLogoPrompt, setAuthLogoPrompt] = useState('');
  const [isAuthGeneratingImage, setIsAuthGeneratingImage] = useState(false);
  const [isAuthGeneratingLogo, setIsAuthGeneratingLogo] = useState(false);
  const [authImageModalOpen, setAuthImageModalOpen] = useState(false);
  const [authLogoModalOpen, setAuthLogoModalOpen] = useState(false);

    const generateAuthLogoImage = async (email, promptText) => {
    if (!promptText || !promptText.trim()) return;
    if (!email || !email.trim()) {
      alert(t('ai_image_email_req'));
      return;
    }
    setIsAuthGeneratingLogo(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/image/generate-logo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerEmail: email, prompt: promptText })
      });
      if (res.ok) {
        let attempts = 0;
        const interval = setInterval(async () => {
          attempts++;
          try {
            const cRes = await fetch(`${API_BASE_URL}/api/config?ownerEmail=${email}`);
            const cData = await cRes.json();
            if (cData['company.logo'] && cData['company.logo'] !== authLogo) {
              setAuthLogo(cData['company.logo']);
              setIsAuthGeneratingLogo(false);
              setAuthLogoModalOpen(false);
              clearInterval(interval);
            }
          } catch(e) {}
          if (attempts > 30) {
            clearInterval(interval);
            setIsAuthGeneratingLogo(false);
            alert("Logo generation took too long.");
          }
        }, 3000);
      } else {
        setIsAuthGeneratingLogo(false);
        alert("Failed to start generation");
      }
    } catch (e) {
      setIsAuthGeneratingLogo(false);
      alert("Error generating logo");
    }
  };

  const generateAuthHeaderImage = async (email, promptText) => {
    if (!promptText || !promptText.trim()) return;
    if (!email || !email.trim()) {
      alert(t('ai_image_email_req'));
      return;
    }
    setIsAuthGeneratingImage(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/image/generate-header`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerEmail: email, prompt: promptText })
      });
      if (res.ok) {
        let attempts = 0;
        const interval = setInterval(async () => {
          attempts++;
          const cRes = await fetch(`${API_BASE_URL}/api/config?ownerEmail=${email}`);
          const cData = await cRes.json();
          if (cData['company.header.image']) {
            setAuthHeaderImage(cData['company.header.image']);
            setIsAuthGeneratingImage(false);
            setAuthImageModalOpen(false);
            clearInterval(interval);
          }
          if (attempts > 120) {
            setIsAuthGeneratingImage(false);
            clearInterval(interval);
            alert(t('ai_image_timeout'));
          }
        }, 5000);
      }
    } catch (err) {
      console.error(err);
      setIsGeneratingImage(false);
    }
  };

  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  
  const [ceidgModalOpen, setCeidgModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [phoneCalls, setPhoneCalls] = useState([]);
  const [searchPhone, setSearchPhone] = useState('');
  const [selectedPhoneCall, setSelectedPhoneCall] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({ 
    unreadEmails: 0, unreadChats: 0, totalLeads: 0, todayMeetings: 0, todayOrders: 0,
    totalMessages: 0, vectorizedMessages: 0, vectorizedLeads: 0, aiRequestsTotal: 0, aiRequestsToday: 0
  });
  const [dashboardChatSource, setDashboardChatSource] = useState(['EMAIL', 'GENERAL']);
  
  const [serverOffline, setServerOffline] = useState(false);
  useEffect(() => {
    let interval = setInterval(() => {
      fetch(`${API_BASE_URL}/api/public/companies`)
        .then(() => setServerOffline(false))
        .catch(() => setServerOffline(true));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardStats = () => {
    if (!user) return;
    fetch(`${API_BASE_URL}/api/dashboard/stats?ownerEmail=${user.email}`)
      .then(r => r.json()).then(setDashboardStats).catch(e => console.error("Dash stats fail", e));
  };
  const calendarViewportRef = useRef(null);
  const todayButtonRef = useRef(null);

  useEffect(() => {
    if (activeTab === 'calendar' && todayButtonRef.current && calendarViewportRef.current) {
      setTimeout(() => {
        todayButtonRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }, 100);
    }
  }, [activeTab]);
  const [stats, setStats] = useState({ total: 0, scanned: 0, aiEnriched: 0, withEmail: 0 });

  const [affiliateStats, setAffiliateStats] = useState({ code: '', enabled: false, totalReferrals: 0, totalEarnings: 0 });
  
  // Meeting State
  const [meetings, setMeetings] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [meetingModalOpened, setMeetingModalOpened] = useState(false);
  const [newMeeting, setNewMeeting] = useState({ title: '', description: '', startTime: '', endTime: '', leadId: '' });
  const [meetingSearchLeads, setMeetingSearchLeads] = useState([]);
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());

  // WhatsApp Engine State
  const [waStatus, setWaStatus] = useState(null); // { status: 'open', ... }
  const [waQrCode, setWaQrCode] = useState(null);
  const [isWaLoading, setIsWaLoading] = useState(false);
  const [selectedWaChat, setSelectedWaChat] = useState(null);

  const fetchWaStatus = () => {
    fetch(`${API_BASE_URL}/api/whatsapp/status?email=${encodeURIComponent(user?.email || '')}`)
      .then(r => r.json())
      .then(data => {
        const state = data.instance?.state || data.status;
        const normalizedData = { status: state };
        setWaStatus(normalizedData);
        if (state !== 'open' && activeTab === 'settings') {
          fetchWaQr();
        }
        if (state === 'open') {
          // Auto-configure webhook if online
          fetch(`${API_BASE_URL}/api/whatsapp/configure-webhook?email=${encodeURIComponent(user?.email || '')}`, { method: 'POST' });
        }
      }).catch(() => setWaStatus({ status: 'disconnected' }));
  };

  const fetchWaQr = () => {
    fetch(`${API_BASE_URL}/api/whatsapp/qrcode?email=${encodeURIComponent(user?.email || '')}`)
      .then(r => r.json())
      .then(data => {
        if (data.base64) setWaQrCode(data.base64);
        else if (data.code) setWaQrCode(data.code);
      }).catch(() => {});
  };

  const createWaInstance = () => {
    setIsWaLoading(true);
    fetch(`${API_BASE_URL}/api/whatsapp/create?email=${encodeURIComponent(user?.email || '')}`, { method: 'POST' })
      .then(() => {
        setTimeout(fetchWaStatus, 2000);
        setIsWaLoading(false);
      }).catch(() => setIsWaLoading(false));
  };

  const resetWhatsApp = () => {
    if (!window.confirm("This will disconnect your phone and clear the current session. Continue?")) return;
    setIsWaLoading(true);
    fetch(`${API_BASE_URL}/api/whatsapp/reset?email=${encodeURIComponent(user?.email || '')}`, { method: 'POST' })
      .then(() => {
        setWaStatus(null);
        setWaQrCode(null);
        setIsWaLoading(false);
        alert("Session cleared. You can now initialize again.");
      }).catch(() => setIsWaLoading(false));
  };
  const [config, setConfig] = useState({});
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [availableModels, setAvailableModels] = useState([
    { value: 'llama3.1', label: 'Agentic AI' }
  ]);

  useEffect(() => {
    if (user && activeTab === 'settings') {
      fetchWaStatus();
      const interval = setInterval(fetchWaStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [activeTab, user]);

  useEffect(() => {
    if (user && activeTab === 'settings') {
      const provider = config['ai.provider'] || 'ollama';
      fetch(`${API_BASE_URL}/api/chat/models?ownerEmail=${user.email}&provider=${provider}`)
        .then(r => r.json())
        .then(data => {
          if (Array.isArray(data) && data.length > 0) setAvailableModels(data);
        })
        .catch(err => console.error("Error fetching models:", err));
    }
  }, [activeTab, user, config['ai.provider']]);
  
  // Chat State
  const [chatMessages, setChatMessages] = useState([]);

  const lastGreetingLangRef = useRef(null);
  useEffect(() => {
    if (!i18n.language) return;
    const currentLang = i18n.language;
    if (lastGreetingLangRef.current !== currentLang) {
      const isFirst = (lastGreetingLangRef.current === null);
      lastGreetingLangRef.current = currentLang;
      setChatMessages(prev => {
        if (prev.length === 0 || (prev.length === 1 && prev[0].role === 'AI')) {
          if (!isFirst && isWidgetMode) {
            setTimeout(() => {
              speakAIResponse(t('ai_greeting'), currentLang);
            }, 300);
          }
          return [{ role: 'AI', content: t('ai_greeting'), sentAt: new Date() }];
        }
        return prev;
      });
    }
  }, [i18n.language, t, isWidgetMode]);


  // Auto-identify anonymous visitors on company profiles (widgetToken exists)
  useEffect(() => {
    if (isWidgetMode && widgetToken && !isVisitorIdentified) {
      setWidgetVisitorEmail('anonymous');
      setIsVisitorIdentified(true);
      const compName = config['company.name'] || 'the company';
      setChatMessages([{ role: 'AI', content: t('ai_greeting'), sentAt: new Date() }]);
    }
  }, [isWidgetMode, widgetToken, config, isVisitorIdentified]);
  const [chatSessions, setChatSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatFallbackMessage, setChatFallbackMessage] = useState(null);
  const [chatImage, setChatInputImage] = useState(null); // Base64 image
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isVoiceReplyEnabled, setIsVoiceReplyEnabled] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const lastSpokenIndexRef = useRef(-1);

  const toggleListening = () => {
    stopAllSpeech();
    if (isListening) {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current.abort();
        } catch (e) {
          console.error(e);
        }
      }
      setIsListening(false);
    } else {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.lang = i18n.language;
        recognition.continuous = false;
        recognition.interimResults = false;
        
        recognition.onstart = () => setIsListening(true);
        recognition.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          setChatInput(prev => prev + (prev ? ' ' : '') + transcript);
        };
        recognition.onerror = (event) => {
          console.error("Speech recognition error", event.error);
          setIsListening(false);
        };
        recognition.onend = () => {
          setIsListening(false);
          setTimeout(() => {
            document.getElementById('widget-chat-submit-btn')?.click();
          }, 350);
        };
        
        recognitionRef.current = recognition;
        recognition.start();
      } else {
        alert(t('speech_not_supported'));
      }
    }
  };

  useEffect(() => {
    if (isWidgetMode && isVoiceReplyEnabled && chatMessages.length > 0) {
      const lastIndex = chatMessages.length - 1;
      const lastMsg = chatMessages[lastIndex];
      if (lastMsg.role === 'AI' && lastSpokenIndexRef.current < lastIndex) {
        lastSpokenIndexRef.current = lastIndex;
        speakAIResponse(lastMsg.content, i18n.language);
      }
    }
  }, [chatMessages, isVoiceReplyEnabled, isWidgetMode, i18n.language]);

  useEffect(() => {
    let interval;
    if (currentSessionId) {
      interval = setInterval(() => {
        fetchChatHistory(currentSessionId);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [currentSessionId]);
  
  const chatViewportRef = useRef(null);
  const isScrolledUpRef = useRef(false);
  const scrollToBottom = () => {
    if (chatViewportRef.current && !isScrolledUpRef.current) {
      chatViewportRef.current.scrollTo({ top: chatViewportRef.current.scrollHeight, behavior: 'smooth' });
    }
  };
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Ask AI State
  const [askAiModalOpened, setAskAiModalOpened] = useState(false);
  const [askAiQuery, setAskAiQuery] = useState('');
  const [askAiResponse, setAskAiResponse] = useState('');
  const [isAskAiLoading, setIsAskAiLoading] = useState(false);
  const [isTestingAi, setIsTestingAi] = useState(false);

  const handleAskAi = () => {
    if (!crmSearch.trim()) {
      alert(t('ai_search_empty_alert'));
      return;
    }
    setIsAskAiLoading(true);
    setAskAiResponse('');
    
    fetch(`${API_BASE_URL}/api/crm/search-semantic?ownerEmail=${user?.email}&query=${encodeURIComponent(crmSearch)}`)
      .then(r => r.json())
      .then(msgs => { setCrmMessages(msgs || []); setCrmTotalPages(1); })
      .catch(e => console.error(e));

    fetch(`${API_BASE_URL}/api/crm/ask-ai?ownerEmail=${user?.email}&query=${encodeURIComponent(crmSearch)}&language=${i18n.language}`, { method: 'POST' })
      .then(res => res.text())
      .then(text => {
        setAskAiResponse(text);
        setIsAskAiLoading(false);
      })
      .catch(e => {
        console.error(e);
        setAskAiResponse(t('ai_search_error'));
        setIsAskAiLoading(false);
      });
  };

  const [installModalOpened, setInstallModalOpened] = useState(false);
  const [installType, setInstallType] = useState('chat'); // 'chat' or 'calendar'
    const [installTemplateKey, setInstallTemplateKey] = useState('');


  useEffect(() => {
    if (isEditingPublicProfile && user) {
      setAuthEmail(user.email || '');
      setAuthCompanyName(user.companyName || '');
      
      fetch(`${API_BASE_URL}/api/config?ownerEmail=${user.email}`)
        .then(res => res.json())
        .then(data => {
            if (!Array.isArray(data)) return;
            const cfg = data.reduce((acc, curr) => ({ ...acc, [curr.key]: curr.value }), {});
            setCompanyDesc(cfg['company.description'] || '');
            setAuthLocation(cfg['company.address'] || '');
            setAuthMapLat(cfg['company.map.lat'] || '');
            setAuthMapLng(cfg['company.map.lng'] || '');
            setAuthCustomDomain(cfg['company.customDomain'] || '');
            setAuthAffiliateEnabled(cfg['company.affiliate.enabled'] === 'true');
            setAuthHeaderColor(cfg['company.bg.color'] || '#000000');
            setAuthPageBg(cfg['company.page.bg'] || '#0a0a0a');
            setAuthHeaderImage(cfg['company.header.image'] || '');
              setAuthLogo(cfg['company.logo'] || '');
        })
        .catch(e => console.error("Failed to load config for editor", e));
    }
  }, [isEditingPublicProfile, user]);

  useEffect(() => {
    if (orderToken) {
      const email = window.atob(orderToken);
      fetch(`${API_BASE_URL}/api/services?ownerEmail=${email}`)
        .then(r => r.json())
        .then(setServices);
    }
  }, [orderToken]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    
    if (code && user) {
      const redirectUri = window.location.origin + '/';
      fetch(`${API_BASE_URL}/api/social/tiktok/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerEmail: user.email, code, redirectUri })
      }).then(res => res.json()).then(data => {
        if (data.status === 'SUCCESS') {
          alert('Sukces! Konto TikTok zostało połączone pomyślnie!');
          window.location.href = '/';
        } else {
          alert('Błąd podczas logowania do TikToka: ' + data.message);
        }
      });
    }

    const token = params.get('token');
    const calToken = params.get('calendarToken');
    const ordToken = params.get('order_token');
    const sessionIdParam = params.get('session');
    const emailParam = params.get('email');
    const profileParam = params.get('profile');
    const templateParam = params.get('template');

    const host = window.location.hostname;
    const isCustomDomain = host !== 'localhost' && host !== '127.0.0.1' && !host.includes('ailinkb.com') && !host.includes('ailinkb.shop') && !host.includes('railway.app');
    
    if (isCustomDomain && !profileParam && !params.get('token') && !params.get('calendarToken')) {
      fetch(`${API_BASE_URL}/api/public/resolveDomain?domain=${host}`)
        .then(res => {
          if (res.ok) return res.json();
          throw new Error('Domain not mapped');
        })
        .then(data => {
           if (data.profile) {
              window.location.href = window.location.pathname + "?profile=" + data.profile;
           }
        })
        .catch(e => console.error(e));
    }

    if (profileParam) {
      setIsProfileMode(true);
      return;
    }

    if (params.get('live_preview') === 'true') {
      setIsLivePreviewMode(true);
      return;
    }
    if (params.get('subscribe') === 'true') {
      const pinParam = params.get('pin');
      if (emailParam && pinParam) {
        fetch(`${API_BASE_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailParam, pin: pinParam })
        })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            localStorage.setItem('ailinkb_user', JSON.stringify(data));
            setUser(data);
          }
          setPlansModalOpened(true);
        })
        .catch(e => {
          console.error(e);
          setPlansModalOpened(true);
        });
      } else {
        setPlansModalOpened(true);
      }
    }

    if (token) {
      setIsWidgetMode(true);
      setWidgetToken(token);
      if (templateParam) {
        setWidgetTemplateKey(templateParam);
      }
      if (sessionIdParam && emailParam) {
        setWidgetChatSessionId(sessionIdParam);
        setWidgetVisitorEmail(emailParam);
        setIsVisitorIdentified(true);
      }
    }
    if (calToken) {
      setIsCalendarWidgetMode(true);
      setCalendarToken(calToken);
    }
    if (ordToken) {
      setIsOrderWidgetMode(true);
      setOrderToken(ordToken);
    }
  }, []);
  const handleCalendarBooking = () => {
    if (!widgetVisitorEmail || !newMeeting.startTime || !newMeeting.title) {
      alert(t('cal_error_fields', "Proszę wypełnić wszystkie wymagane pola (tytuł, data, e-mail)."));
      return;
    }
    
    const start = new Date(newMeeting.startTime);
    if (isNaN(start.getTime())) {
      alert(t('cal_error_format', "Nieprawidłowy format daty lub czasu. Upewnij się, że wpisałeś godzinę i minuty."));
      return;
    }
    
    // Calculate end time
    const end = new Date(start.getTime() + parseInt(bookingDuration) * 60 * 1000);
    // Format to local ISO-like string for backend
    const offset = end.getTimezoneOffset() * 60000;
    const localEnd = new Date(end.getTime() - offset).toISOString().slice(0, 16);

    setIsChatLoading(true);
    fetch(`${API_BASE_URL}/api/meetings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...newMeeting,
        token: calendarToken,
        visitorEmail: widgetVisitorEmail,
        endTime: localEnd
      })
    }).then(() => {
      setIsBookingSuccess(true);
      setIsChatLoading(false);
    }).catch(() => {
      setIsChatLoading(false);
      alert(t('cal_error_server', "Wystąpił błąd podczas zapisywania wizyty."));
    });
  };

  const fetchBusySlots = (date) => {
    if (!calendarToken) return;
    fetch(`${API_BASE_URL}/api/meetings/busy-slots?token=${calendarToken}&date=${date}`)
      .then(r => r.json())
      .then(setBusySlots);
  };

  const widgetDebounceTimerRef = useRef(null);
  const widgetPendingMessagesRef = useRef([]);

  const handleSendWidgetMessage = () => {
    if (!chatInput.trim() || !widgetVisitorEmail) return;
    isScrolledUpRef.current = false;
    const msgText = chatInput;
    const imgData = chatImage;
    setChatInput('');
    setChatInputImage(null);
    setIsChatLoading(true);
    setChatFallbackMessage(null);

    // Timeout fallback after 60 seconds
    setTimeout(() => {
      setIsChatLoading(currentLoading => {
        if (currentLoading) {
          setChatFallbackMessage("Thank you for your inquiry, we will try to answer it as soon as possible.");
          return false;
        }
        return currentLoading;
      });
    }, 60000);

    if ('speechSynthesis' in window) {
       window.speechSynthesis.cancel();
    }

    // Add visitor email to identify them
    setChatMessages(prev => [...prev, { role: 'USER', content: msgText, sentAt: new Date() }]);
    widgetPendingMessagesRef.current.push(msgText);

    if (widgetDebounceTimerRef.current) {
      clearTimeout(widgetDebounceTimerRef.current);
    }

    widgetDebounceTimerRef.current = setTimeout(() => {
      const combinedMessage = widgetPendingMessagesRef.current.join('\n');
      widgetPendingMessagesRef.current = [];

      fetch(`${API_BASE_URL}/api/chat/external/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            content: combinedMessage, 
            token: widgetToken, 
            visitorEmail: widgetVisitorEmail,
            sessionId: currentSessionId,
            image: imgData,
            templateKey: widgetTemplateKey,
            language: i18n.language
          })
      })
      .then(r => r.json())
      .then(data => {
        if (!currentSessionId) setCurrentSessionId(data.sessionId);
      })
      .catch(e => {
        console.error(e);
        setIsChatLoading(false);
        setChatMessages(prev => [...prev, { role: 'AI', content: t('chat_error_generic', 'Przepraszamy, wystąpił błąd sieci lub serwer AI jest chwilowo przeciążony. Spróbuj ponownie.'), sentAt: new Date() }]);
      });
    }, 2000);
  };
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(10);

  // CRM State
  const [crmMessages, setCrmMessages] = useState([]);
  const [crmSearch, setCrmSearch] = useState('');
  const [crmPage, setCrmPage] = useState(1);
  const [crmTotalPages, setCrmTotalPages] = useState(0);
  const [crmTotalElements, setCrmTotalElements] = useState(0);
  const [crmPageSize] = useState(100);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const [isWaComposing, setIsWaComposing] = useState(false);
  const [waComposeData, setWaComposeData] = useState({ to: '', text: '' });
  const [isWaSending, setIsWaSending] = useState(false);

  const handleSendWhatsApp = () => {
    const target = waComposeData.to || selectedWaChat;
    const messageText = waComposeData.text;
    if (!target || !messageText) return;

    setIsWaSending(true);
    
    // Optimistic UI Update: Add message to local list immediately
    const tempId = Date.now();
    const newMessage = {
      id: tempId,
      sender: user.email,
      recipient: target,
      body: messageText,
      incoming: false,
      channel: 'WHATSAPP',
      receivedAt: new Date().toISOString(),
      read: true
    };
    
    setCrmMessages(prev => [newMessage, ...prev]);
    setWaComposeData({ ...waComposeData, text: '' }); // Clear input immediately

    fetch(`${API_BASE_URL}/api/whatsapp/send-message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        number: target,
        text: messageText,
        ownerEmail: user.email
      })
    }).then(res => res.json()).then(data => {
      setIsWaSending(false);
      if (data.status === 'error') {
        console.error("WA send error", data.message);
        // Rollback on error? Maybe just show a small red indicator
      } else {
        setIsWaComposing(false);
        fetchCRM(); // Refresh to get the actual server-saved record
      }
    }).catch(e => {
      setIsWaSending(false);
      console.error("WA tech error", e);
    });
  };
  const [crmSelectedCategories, setCrmSelectedCategories] = useState([]);
  const [crmAccountFilter, setCrmAccountFilter] = useState('ALL');
  useEffect(() => {
    if (crmAccountFilter === 'ALL' && config['mail.username']) {
      setCrmAccountFilter(config['mail.username']);
    }
  }, [config, crmAccountFilter]);

  const [filterUnreadOnly, setFilterUnreadOnly] = useState(false);
  const [crmFilter, setCrmFilter] = useState('INBOX'); // ALL, INBOX, SENT, STARRED, SPAM, TRASH, CUSTOM:x
  const [folderCounts, setFolderCounts] = useState({ INBOX: 0, SENT: 0, STARRED: 0, SPAM: 0, TRASH: 0, ALL: 0 });
  const [customMailFolders, setCustomMailFolders] = useState(() => {
    try {
      const saved = localStorage.getItem('ailinkb_custom_mail_folders');
      return saved ? JSON.parse(saved) : [
        { id: 'faktury', name: 'Faktury i Rachunki', keywords: 'faktura, vat, płatność, rachunek, invoice, receipt, payment', color: '#f76707' },
        { id: 'oferty', name: 'Oferty i Zlecenia', keywords: 'oferta, zlecenie, projekt, zapytanie, wycena, quote, deal', color: '#1c7ed6' },
        { id: 'allegro', name: 'Powiadomienia Allegro', keywords: 'allegro, oferta zakończyła, wpłata, kupujący', color: '#ae3ec9' }
      ];
    } catch (e) {
      return [];
    }
  });
  const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderKeywords, setNewFolderKeywords] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#228be6');

  const saveCustomFoldersToStorage = (folders) => {
    setCustomMailFolders(folders);
    try {
      localStorage.setItem('ailinkb_custom_mail_folders', JSON.stringify(folders));
      if (user && user.email) {
        fetch(`${API_BASE_URL}/api/config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ configKey: 'mail.custom.folders', configValue: JSON.stringify(folders), ownerEmail: user.email })
        }).catch(() => {});
      }
    } catch (e) {}
  };

  // Email State
  const [composeInitialData, setComposeInitialData] = useState({ to: '', subject: '', body: '', isHtml: false });
  const [targetLeadId, setTargetLeadId] = useState(null);

  // Knowledge State
  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceDesc, setNewServiceDesc] = useState('');
  const [newServicePrice, setNewServicePrice] = useState(0);
  const [newServiceCurrency, setNewServiceCurrency] = useState('PLN');
  const [newServiceHidden, setNewServiceHidden] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [isAddingNewService, setIsAddingNewService] = useState(false);
  const [knowledgeAccordionValue, setKnowledgeAccordionValue] = useState(null);
  const [chatAccordionValue, setChatAccordionValue] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [newTemplateKey, setNewTemplateKey] = useState('');
  const [newTemplatePrompt, setNewTemplatePrompt] = useState('');
  const [draftAccordionValue, setDraftAccordionValue] = useState(null);
  const [calendarAccordionValue, setCalendarAccordionValue] = useState(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
  const [selectedMeetingDetails, setSelectedMeetingDetails] = useState(null);
  const [socialPosts, setSocialPosts] = useState([]);
  const [socialSubTab, setSocialSubTab] = useState('upload');
  const [socialSkillVideos, setSocialSkillVideos] = useState([]);
  const [socialMediaFile, setSocialMediaFile] = useState(null);
  const [socialMediaPreview, setSocialMediaPreview] = useState(null);
  const [socialHistoryPreview, setSocialHistoryPreview] = useState(null);
  const [socialMediaType, setSocialMediaType] = useState('IMAGE');
  const [socialCaption, setSocialCaption] = useState('');
  const [selectedSocialPlatforms, setSelectedSocialPlatforms] = useState(['TikTok', 'Facebook', 'Instagram', 'YouTube']);
  const [socialConnectors, setSocialConnectors] = useState({
      tiktokToken: '', tiktokAccount: '', tiktokKey: '', tiktokSecret: '',
      facebookToken: '', facebookPageId: '',
      instagramToken: '', instagramAccountId: '',
      youtubeToken: '', youtubeChannelId: ''
  });
  const [socialPublishing, setSocialPublishing] = useState(false);
  const [socialComments, setSocialComments] = useState([]);
  const [commentsScanning, setCommentsScanning] = useState(false);
  const [socialAutoBan, setSocialAutoBan] = useState(false);
  const [socialRequireApproval, setSocialRequireApproval] = useState(true);
  const [socialCommentsView, setSocialCommentsView] = useState('active');
  const [reports, setReports] = useState([]);
  const [editedDrafts, setEditedDrafts] = useState({});
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedDraftId, setSelectedDraftId] = useState(null);

  // History State
  const [historyOpened, setHistoryOpened] = useState(false);
  const [historyLead, setHistoryLead] = useState(null);
  const [emailHistory, setEmailHistory] = useState([]);
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Customer State
  const [leadsAccordionValue, setLeadsAccordionValue] = useState(null);
  const [leadModalOpened, setLeadModalOpened] = useState(false);
  const [leadsCategoryFilter, setLeadsCategoryFilter] = useState('ALL');
  const [editingLead, setEditingLead] = useState(null);
  const [leadSeoStats, setLeadSeoStats] = useState(null);
  const [leadSeoStatsLoading, setLeadSeoStatsLoading] = useState(false);
  const [auditingLeadId, setAuditingLeadId] = useState(null);
  const [initiateChatModalOpened, setInitiateChatModalOpened] = useState(false);
  const [initiateChatTargetLead, setInitiateChatTargetLead] = useState(null);
  const [initiateChatMessage, setInitiateChatMessage] = useState('');
  const [isInitiatingChat, setIsInitiatingChat] = useState(false);

  useEffect(() => {
    if (editingLead && editingLead.id) {
      setLeadSeoStatsLoading(true);
      fetch(`${API_BASE_URL}/api/leads/${editingLead.id}/seo-stats?ownerEmail=${user?.email}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          setLeadSeoStats(data);
          setLeadSeoStatsLoading(false);
        })
        .catch(e => {
          console.error("Error fetching SEO stats", e);
          setLeadSeoStats(null);
          setLeadSeoStatsLoading(false);
        });
    } else {
      setLeadSeoStats(null);
    }
  }, [editingLead, user?.email]);

  const handleInitiateChat = async () => {
    if (!initiateChatMessage.trim() || !initiateChatTargetLead) return;
    setIsInitiatingChat(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ownerEmail: user.email,
          leadEmail: initiateChatTargetLead.email,
          content: initiateChatMessage
        })
      });
      if (res.ok) {
        setInitiateChatModalOpened(false);
        setInitiateChatMessage('');
        alert(t('chat_initiated_success'));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsInitiatingChat(false);
    }
  };
  const [leadForm, setLeadForm] = useState({ 
    companyName: '', contactPerson: '', email: '', phone: '', website: '', 
    facebook: '', linkedin: '', instagram: '', address: '', status: 'NEW',
    detectedNeeds: '', matchedServices: '', projectDetails: '',
    ownerEmail: '', category: 'GENERAL', taxId: ''
  });

  const leadsAbortControllerRef = useRef(null);

  const fetchLeads = (overridePage = null, overrideQuery = null) => {
    if (!user) return;
    
    if (leadsAbortControllerRef.current) {
      leadsAbortControllerRef.current.abort();
    }
    leadsAbortControllerRef.current = new AbortController();

    const catParam = leadsCategoryFilter && leadsCategoryFilter !== 'ALL' ? `&category=${leadsCategoryFilter}` : '';
    const activeQuery = overrideQuery !== null ? overrideQuery : query;
    const activePage = overridePage !== null ? overridePage : page;

    const url = activeQuery 
      ? `${API_BASE_URL}/api/leads/search?query=${encodeURIComponent(activeQuery)}&page=${activePage-1}&size=${pageSize}&ownerEmail=${user.email}${catParam}`
      : `${API_BASE_URL}/api/leads?page=${activePage-1}&size=${pageSize}&ownerEmail=${user.email}${catParam}`;
      
    fetch(url, { signal: leadsAbortControllerRef.current.signal }).then(r => r.json()).then(data => {
      const content = data.content || [];
      setLeads(content);
      
      // Support both standard Page structure and 'via-dto' (data.page) structure
      const totalEls = data.totalElements !== undefined ? data.totalElements : (data.page?.totalElements !== undefined ? data.page.totalElements : content.length);
      const totalPgs = data.totalPages !== undefined ? data.totalPages : (data.page?.totalPages !== undefined ? data.page.totalPages : 1);
      
      setTotalElements(totalEls);
      setTotalPages(totalPgs);
    }).catch(e => {
      if (e.name !== 'AbortError') console.error("Leads fail", e);
    });
  };

  const handleSaveLead = () => {
    const method = editingLead ? 'PUT' : 'POST';
    const url = editingLead ? `${API_BASE_URL}/api/leads/${editingLead.id}` : `${API_BASE_URL}/api/leads`;
    
    const payload = { ...leadForm, ownerEmail: user.email };

    fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(() => {
      setLeadModalOpened(false);
      setEditingLead(null);
      setLeadForm({
        companyName: '', contactPerson: '', email: '', phone: '', website: '',
        facebook: '', linkedin: '', instagram: '', address: '', status: 'NEW',
        detectedNeeds: '', matchedServices: '', projectDetails: '',
        ownerEmail: '', category: 'GENERAL', taxId: ''
      });
      fetchLeads();
      fetchStats();
    });
  };

  const fetchServices = () => {
    if (!user) return;
    fetch(`${API_BASE_URL}/api/services?ownerEmail=${user.email}`).then(r => r.json()).then(data => {
      setServices(data);
      if (isEditingPublicProfile && data.length > 0) {
        setAuthServices(data.map(d => ({ name: d.name, description: d.description || '' })));
      }
    }).catch(e => console.error("Serv fail", e));
  };

  useEffect(() => {
    if (activeTab === 'crm-email' || activeTab === 'crm-whatsapp') {
      setCrmPage(1);
    }
  }, [activeTab, crmFilter]);

  const crmAbortControllerRef = useRef(null);

  const fetchCRM = () => {
    if (!user) return;
    let channelParam = '';
    let incomingParam = '';
    let categoryParam = '';
    
    if (activeTab === 'crm-email') {
      channelParam = '&channel=EMAIL';
      if (crmFilter === 'INBOX') incomingParam = '&incoming=true';
      else if (crmFilter === 'SENT') incomingParam = '&incoming=false';
      else if (crmFilter === 'STARRED') categoryParam = '&category=STARRED';
      else if (crmFilter === 'SPAM') categoryParam = '&category=SPAM';
      else if (crmFilter === 'TRASH') categoryParam = '&category=TRASH';
      else if (crmFilter.startsWith('CUSTOM:')) categoryParam = `&category=${crmFilter.replace('CUSTOM:', '')}`;
      
      fetch(`${API_BASE_URL}/api/crm/folder-counts?ownerEmail=${encodeURIComponent(user.email)}&channel=EMAIL&accountEmail=${encodeURIComponent(crmAccountFilter)}`)
        .then(r => r.json())
        .then(counts => {
          if (counts && typeof counts === 'object') setFolderCounts(counts);
        })
        .catch(e => console.error("Folder counts fetch fail:", e));
    } else if (activeTab === 'crm-whatsapp') {
      channelParam = '&channel=WHATSAPP';
      incomingParam = ''; 
    }
    
    console.log(`Fetching CRM messages: Tab=${activeTab}, Page=${crmPage}, Search=${crmSearch}, Channel=${channelParam}, Incoming=${incomingParam}, Category=${categoryParam}`);
    
    if (crmAbortControllerRef.current) {
      crmAbortControllerRef.current.abort();
    }
    crmAbortControllerRef.current = new AbortController();

    fetch(`${API_BASE_URL}/api/crm/messages?page=${crmPage-1}&size=${crmPageSize}&search=${crmSearch}&ownerEmail=${user.email}${channelParam}${incomingParam}${categoryParam}&accountEmail=${encodeURIComponent(crmAccountFilter)}`, {
      signal: crmAbortControllerRef.current.signal
    })
      .then(r => r.json()).then(data => {
        const msgs = data.content || [];
        console.log(`Received ${msgs.length} messages from backend`);
        setCrmMessages(msgs);
        setCrmTotalPages(data.totalPages || 0);
        setCrmTotalElements(data.totalElements || 0);

        if (msgs.length > 0 && !selectedMsg && (activeTab === 'crm-email' || activeTab === 'crm-whatsapp')) {
          setSelectedMsg(msgs[0]);
        }
        }).catch(e => {
          if (e.name !== 'AbortError') {
            console.error("CRM fail", e);
          }
        });

  };

  const fetchSocialPosts = () => {
    const targetEmail = user ? user.email : '';
    fetch(`${API_BASE_URL}/api/social/drafts?ownerEmail=${targetEmail}`).then(r => r.json()).then(setSocialPosts).catch(e => console.error("Social fail", e));
    fetch(`${API_BASE_URL}/api/social/comments?ownerEmail=${targetEmail}`).then(r => r.json()).then(setSocialComments).catch(e => console.error("Comments fail", e));
    fetch(`${API_BASE_URL}/api/skills/my?ownerEmail=${targetEmail}`).then(r => r.json()).then(data => {
      if (Array.isArray(data)) setSocialSkillVideos(data.filter(s => s.videoUrl));
    }).catch(e => console.error("Skills fail", e));
    if (user && user.email) {
      fetch(`${API_BASE_URL}/api/social/connectors?ownerEmail=${user.email}`)
        .then(r => r.json())
        .then(data => { if (data && typeof data === 'object') setSocialConnectors(prev => ({...prev, ...data})); })
        .catch(e => console.error("Connectors fail", e));
    }
  };

  const handleToggleAutoBan = (checked) => {
    setSocialAutoBan(checked);
    if (user && user.email) {
      fetch(`${API_BASE_URL}/api/social/comments/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerEmail: user.email, autoBan: checked ? "true" : "false" })
      }).catch(console.error);
    }
  };

  const handleToggleRequireApproval = (checked) => {
    setSocialRequireApproval(checked);
    if (user && user.email) {
      fetch(`${API_BASE_URL}/api/social/comments/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerEmail: user.email, requireApproval: checked ? "true" : "false" })
      }).catch(console.error);
    }
  };

  const handleScanSocialComments = () => {
    if (!user || !user.email) return;
    setCommentsScanning(true);
    fetch(`${API_BASE_URL}/api/social/comments/scan?ownerEmail=${user.email}&autoBan=${socialAutoBan}&requireApproval=${socialRequireApproval}`, {
      method: 'POST'
    })
    .then(r => r.json())
    .then(data => {
      setCommentsScanning(false);
      if (Array.isArray(data)) {
        setSocialComments(data);
        alert(t('scan_success_msg'));
      }
    })
    .catch(e => {
      setCommentsScanning(false);
      alert("Error scanning comments: " + e.message);
    });
  };

  const handleApproveCommentReply = (id, text) => {
    fetch(`${API_BASE_URL}/api/social/comments/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customReply: text })
    })
    .then(r => r.json())
    .then(data => {
      alert(data.message || 'Zatwierdzono i wysłano pomyślnie!');
      fetchSocialPosts();
    })
    .catch(e => alert(e.message));
  };

  const handleExecuteCommentBan = (id) => {
    fetch(`${API_BASE_URL}/api/social/comments/${id}/ban`, {
      method: 'POST'
    })
    .then(r => r.json())
    .then(data => {
      alert(data.message || 'Komentarz usunięty, a konto autora zablokowane na stałe!');
      fetchSocialPosts();
    })
    .catch(e => alert(e.message));
  };

  const handleSaveSocialConnectors = () => {
    if (!user || !user.email) return;
    fetch(`${API_BASE_URL}/api/social/connectors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...socialConnectors, ownerEmail: user.email })
    })
    .then(r => r.json())
    .then(data => {
      alert(t('save_success'));
      fetchSocialPosts();
    })
    .catch(e => alert("Error saving connectors: " + e.message));
  };

  const handleOmniPublish = () => {
    if (!user || !user.email) return;
    if (selectedSocialPlatforms.length === 0) {
      alert(t('select_platform_warning'));
      return;
    }
    setSocialPublishing(true);
    fetch(`${API_BASE_URL}/api/social/publish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ownerEmail: user.email,
        content: socialCaption,
        mediaType: socialMediaType,
        mediaData: socialMediaPreview,
        platforms: selectedSocialPlatforms
      })
    })
    .then(r => r.json())
    .then(data => {
      setSocialPublishing(false);
      if (data.status === 'SUCCESS') {
        alert(data.message || 'Opublikowano pomyślnie na połączonych kontach!');
        setSocialMediaFile(null);
        setSocialMediaPreview(null);
        setSocialCaption('');
        setSocialSubTab('history');
        fetchSocialPosts();
      } else {
        alert(data.message || 'Błąd publikacji');
      }
    })
    .catch(e => {
      setSocialPublishing(false);
      alert("Error: " + e.message);
    });
  };

  const fetchReports = () => {
    fetch(`${API_BASE_URL}/api/reports`).then(r => r.json()).then(setReports).catch(e => console.error("Report fail", e));
  };
  const fetchStats = () => {
    if (!user) return;
    
    fetch(`${API_BASE_URL}/api/config/affiliate-stats?ownerEmail=${user.email}`)
      .then(r => r.json())
      .then(data => setAffiliateStats(data))
      .catch(e => console.error("Affiliate stats fail", e));
      
    fetch(`${API_BASE_URL}/api/leads/stats?ownerEmail=${user.email}`).then(r => r.json()).then(setStats).catch(e => console.error("Stats fail", e));
  };

  const fetchChatHistory = (sid) => {
    if (!sid) { setChatMessages([]); return; }
    fetch(`${API_BASE_URL}/api/chat/history/${sid}`).then(r => r.json()).then(data => {
      setChatMessages(prev => {
        const hasGreeting = prev.length > 0 && prev[0].role === 'AI' && !prev[0].id;
        return hasGreeting ? [prev[0], ...data] : data;
      });
      if (data.length > 0 && (data[data.length - 1].role === 'AI' || data[data.length - 1].role === 'OWNER')) {
         setIsChatLoading(false);
         setChatFallbackMessage(null);
      }
    }).catch(e => console.error("Chat fail", e));
  };

  const fetchChatSessions = () => {
    if (!user) return;
    fetch(`${API_BASE_URL}/api/chat/sessions?ownerEmail=${user.email}`).then(r => r.json()).then(data => {
      setChatSessions(data || []);
      if (data.length > 0 && !currentSessionId) {
        setCurrentSessionId(data[0].id);
        fetchChatHistory(data[0].id);
      }
    }).catch(e => console.error("Sessions fail", e));
  };

  const handleSendChatMessage = () => {
    if (!chatInput.trim()) return;
    isScrolledUpRef.current = false;
    const msgText = chatInput;
    const imgData = chatImage;
    setChatInput('');
    setChatInputImage(null);
    setIsChatLoading(true);

    if ('speechSynthesis' in window) {
       window.speechSynthesis.cancel();
    }

    const sessionObj = (Array.isArray(chatSessions) ? chatSessions : []).find(s => s.id === currentSessionId);

    // Optimistically add user message
    setChatMessages(prev => [...prev, { role: 'USER', content: msgText, sentAt: new Date() }]);

    fetch(`${API_BASE_URL}/api/chat/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content: msgText,
        sessionId: currentSessionId,
        sessionTitle: sessionObj?.title,
        ownerEmail: user.email,
        image: imgData,
        source: dashboardChatSource
      })

    })
    .then(r => r.json())
    .then(data => {
      if (!currentSessionId) {
        setCurrentSessionId(data.sessionId);
        fetchChatSessions();
      }
      setIsChatLoading(false);
    })
    .catch(e => {
      console.error(e);
      setIsChatLoading(false);
      setChatMessages(prev => [...prev, { role: 'AI', content: t('chat_error_generic', 'Przepraszamy, wystąpił błąd sieci lub serwer AI jest chwilowo przeciążony. Spróbuj ponownie.'), sentAt: new Date() }]);
    });
  };

  const fetchMeetings = () => {
    if (!user) return;
    fetch(`${API_BASE_URL}/api/meetings?ownerEmail=${user.email}`).then(r => r.json()).then(setMeetings).catch(e => console.error("Meeting fail", e));
    fetch(`${API_BASE_URL}/api/reminders?ownerEmail=${user.email}`).then(r => r.json()).then(setReminders).catch(e => console.error("Reminders fail", e));
  };

  const isEmailConfigured = () => {
    if (!isConfigLoaded) return true; // Don't show alert while loading
    return config['mail.smtp.host'] && config['mail.username'] && config['mail.password'];
  };

  const fetchCampaigns = () => {
    if (!user) return;
    fetch(`${API_BASE_URL}/api/campaigns?ownerEmail=${user.email}`).then(r => r.json()).then(setCampaigns).catch(e => console.error("Campaign fail", e));
  };

  const fetchPhoneCalls = () => {
    if (!user) return;
    fetch(`${API_BASE_URL}/api/phone-calls?ownerEmail=${user.email}${searchPhone ? '&callerNumber=' + encodeURIComponent(searchPhone) : ''}`)
      .then(r => r.json())
      .then(data => setPhoneCalls(Array.isArray(data) ? data : (data.content || [])))
      .catch(e => console.error("Error fetching phone calls:", e));
  };

  const fetchEmailTemplates = () => {
    if (!user) return;
    fetch(`${API_BASE_URL}/api/email-templates?ownerEmail=${user.email}`)
      .then(r => r.json())
      .then(data => setEmailTemplates(Array.isArray(data) ? data : []))
      .catch(e => console.error("Templates fail", e));
  };

  const handleSaveEmailTemplate = () => {
    fetch(`${API_BASE_URL}/api/email-templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...templateForm, ownerEmail: user.email })
    })
    .then(r => r.json())
    .then((savedTemplate) => {
      setTemplateForm(savedTemplate);
      fetchEmailTemplates();
    })
    .catch(e => console.error("Template save fail", e));
  };

  const handleDeleteEmailTemplate = (id) => {
    if (window.confirm("Delete template?")) {
      fetch(`${API_BASE_URL}/api/email-templates/${id}?ownerEmail=${user.email}`, { method: 'DELETE' })
        .then(() => {
          setIsTemplateEditing(false);
          setTemplateForm({ id: null, name: '', subject: '', htmlContent: '' });
          fetchEmailTemplates();
        });
    }
  };

  const fetchOrders = () => {
    if (!user) return;
    fetch(`${API_BASE_URL}/api/orders?ownerEmail=${user.email}`).then(r => r.json()).then(setOrders).catch(e => console.error("Orders fail", e));
  };

  const handleSaveCampaign = () => {
    const formData = new FormData();
    const payload = { ...campaignForm, ownerEmail: user.email };
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    formData.append('campaign', blob);
    if (campaignAttachments && campaignAttachments.length > 0) {
      campaignAttachments.forEach(f => formData.append('files', f));
    }
    fetch(`${API_BASE_URL}/api/campaigns`, {
      method: 'POST',
      body: formData
    }).then(r => r.json()).then(() => {
      setIsCampaignEditing(false);
      setCampaignForm({ name: '', subject: '', htmlContent: '', frequency: 'ONCE', active: false, sendDelaySeconds: 60, ownerEmail: '', autoTranslate: false });
      setCampaignAttachments([]);
      fetchCampaigns();
    });
  };

  const toggleCampaign = (id) => {
    fetch(`${API_BASE_URL}/api/campaigns/${id}/toggle?ownerEmail=${user.email}`, { method: 'POST' })
      .then(res => res.json())
      .then(updatedCampaign => {
        fetchCampaigns();
        if (campaignForm.id === id) {
          setCampaignForm(prev => ({ ...prev, active: updatedCampaign.active }));
        }
      });
  };

  const handleAddMeeting = () => {
    if (!user) return;
    
    if (!newMeeting.title || !newMeeting.title.trim()) {
      alert(t('cal_error_title', "Podaj tytuł spotkania."));
      return;
    }
    if (!newMeeting.startTime || !newMeeting.endTime) {
      alert(t('cal_error_dates', "Musisz podać pełną datę i godzinę od-do (z minutami)."));
      return;
    }
    const dStart = new Date(newMeeting.startTime);
    const dEnd = new Date(newMeeting.endTime);
    if (isNaN(dStart.getTime()) || isNaN(dEnd.getTime())) {
      alert(t('cal_error_format', "Nieprawidłowy format daty lub czasu. Upewnij się, że wpisałeś minuty."));
      return;
    }
    if (dEnd <= dStart) {
      alert(t('cal_error_logic', "Data i godzina zakończenia musi być późniejsza niż godzina rozpoczęcia."));
      return;
    }

    // Encode owner email as token (Base64) to match backend requirement
    const payload = { 
      ...newMeeting, 
      token: btoa(user.email) 
    };

    fetch(`${API_BASE_URL}/api/meetings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(res => {
      if (res.ok) {
        alert(t('cal_scheduled_success', "Spotkanie zostało dodane!"));
        setMeetingModalOpened(false);
        setNewMeeting({ title: '', description: '', startTime: '', endTime: '', leadId: '' });
        fetchMeetings();
      } else {
        alert(t('cal_scheduled_fail', "Nie udało się zapisać spotkania w bazie."));
      }
    }).catch(err => {
      console.error("Meeting save fail", err);
      alert(t('cal_error_server', "Błąd połączenia z serwerem."));
    });
  };

  const fetchConfig = () => {
    if (!user) return;
    fetch(`${API_BASE_URL}/api/config?ownerEmail=${user.email}`)
      .then(r => r.json())
      .then(data => {
        if (isEditingPublicProfile) {
          setAuthEmail(user.email || '');
          setAuthCompanyName(data['company.name'] || '');
          setCompanyDesc(data['company.description'] || '');
          setAuthLocation(data['company.address'] || '');
          setAuthMapLat(data['company.map.lat'] || '');
          setAuthMapLng(data['company.map.lng'] || '');
          setAuthAffiliateEnabled(data['company.affiliate.enabled'] === 'true');
          setAuthHeaderColor(data['company.bg.color'] || '#000000');
          setAuthPageBg(data['company.page.bg'] || '#0a0a0a');
          setAuthHeaderImage(data['company.header.image'] || '');
            setAuthLogo(data['company.logo'] || '');
        }
        if (!data['ai.system.prompt']) {
          data['ai.system.prompt'] = "You are a professional AI Assistant representing the company. Your absolute priority is to answer questions ONLY using the information provided in the Context.\nGUIDELINES:\n1. Answer ONLY based on the provided Context (which contains the company's description and services). Do NOT make up information.\n2. If the answer is not in the context, politely state that you do not have that information.\n3. If the user asks how you can help, what you do, or what services are available, always explicitly LIST the available services from the Context in a clear, friendly manner.\n4. Respond in the user's language.";
        }
        if (!data['ai.greeting.message']) {
          data['ai.greeting.message'] = t('ai_greeting');
        }
        setConfig(data);
        setIsConfigLoaded(true);
      })
      .catch(e => console.error("Config fail", e));
  };

  const handleSaveConfig = () => {
    if (!user) return;
    setIsSavingConfig(true);
    fetch(`${API_BASE_URL}/api/config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...config, ownerEmail: user.email })
    }).then(() => {
      alert(t("settings_saved", "Settings saved!"));
      setIsSavingConfig(false);
    }).catch(e => {
      console.error("Save config fail", e);
      setIsSavingConfig(false);
    });
  };

  const handleOpenLivePreview = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const previewConfig = {
      'company.name': authCompanyName || '',
      'company.address': authLocation || '',
      'company.description': companyDesc || '',
      'company.bg.color': authHeaderColor || '#000000',
      'company.page.bg': authPageBg || '#0a0a0a',
      'company.logo': authLogo || '',
      'company.header.image': authHeaderImage || '',
      'company.phone': '',
      'company.website': ''
    };
    try {
      localStorage.setItem('live_preview_config', JSON.stringify(previewConfig));
      localStorage.setItem('live_preview_services', JSON.stringify(authServices || []));
      localStorage.setItem('live_preview_email', (user ? user.email : authEmail) || 'kontakt@twojafirma.pl');
    } catch(err) {
      console.error("Błąd zapisu podglądu:", err);
    }
    window.open('/?live_preview=true', '_blank');
  };

  const handleOnboard = async (e) => {
    e.preventDefault();
    if (!termsAccepted && !isEditingPublicProfile) { setAuthError(t("err_accept_terms", "Please accept the Terms of Service")); return; }
    
    // Filter empty services
    const validServices = authServices.filter(s => s.name.trim() !== '').map(s => ({...s, imageUrl: s.imageUrl || null}));
    if (validServices.length === 0) {
      setAuthError("Please add at least one service you provide.");
      return;
    }
    
    if (isEditingPublicProfile && user) {
       setAuthError("Updating profile...");
       try {
         const res = await fetch(`${API_BASE_URL}/api/auth/updateProfile`, {
           method: 'POST',
           headers: { 'Content-Type': 'application/json' },
           body: JSON.stringify({ 
             email: user.email, 
             companyDesc: companyDesc,
             companyName: authCompanyName,
             location: authLocation,
             mapLat: authMapLat,
             mapLng: authMapLng,
             headerColor: authHeaderColor,
             pageBg: authPageBg,
             customHtml: authCustomHtml,
             affiliateEnabled: authAffiliateEnabled,
             services: validServices
           })
         });
         if (res.ok) {
           localStorage.removeItem('editing_profile');
           window.location.href = window.location.pathname;
         } else {
           const data = await res.json();
           setAuthError(data.message || "Failed to update.");
         }
       } catch(err) {
         setAuthError("Server error during update");
       }
       return;
    }

    setAuthError(t("setting_up_ai", "Setting up your AI..."));
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: authEmail, 
          companyDesc: companyDesc,
          companyName: authCompanyName,
          location: authLocation,
          mapLat: authMapLat,
          mapLng: authMapLng,
          headerColor: authHeaderColor,
          pageBg: authPageBg,
             customHtml: authCustomHtml,
          referredBy: authAffiliateCode,
          services: validServices
        })
      });
      const data = await res.json();
      if (res.ok) {
        setGeneratedToken(data.token);
        setOnboardingStep('success');
        setAuthError('');
      } else {
        setAuthError(data.message);
      }
    } catch (err) { setAuthError(t('auth_server_error')); }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    const endpoint = isRegistering ? 'register' : 'login';
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authEmail, pin: authPin })
      });
      const data = await res.json();
      if (res.ok) {
        if (isRegistering) {
          setIsRegistering(false);
          setAuthPin('');
          setAuthError(t('register_success_pin_sent'));
        } else {
          localStorage.setItem('ailinkb_user', JSON.stringify(data));
          
          window.location.href = '/';
        }
      } else {
        setAuthError(data.message || 'Auth failed');
      }
    } catch (e) {
      setAuthError(t('auth_server_error'));
    }
  };

  const triggerSocialLogin = (providerName) => {
    setSelectedProvider(providerName);
    setSocialModalOpened(true);
  };

  const executeSocialLogin = async () => {
    if (!socialEmail.trim()) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/social-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: socialEmail.trim(), provider: selectedProvider })
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('ailinkb_user', JSON.stringify(data));
        if (!data.companyName || isRegistering || window.location.search.includes('register=')) {
          localStorage.setItem('editing_profile', 'true');
        }
        window.location.href = '/';
      } else {
        alert(t('social_login_error'));
      }
    } catch (e) {
      console.error("Social login error:", e);
      alert(t('auth_server_error'));
    }
  };

  const handleUpgradePlan = async (newPlan) => {
    if (!user) return;
    console.log("Rozpoczynamy aktywację pakietu:", newPlan);
    setIsUpgrading(true);

    try {
      const stripeRes = await fetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, plan: newPlan })
      });
      
      console.log("Stripe API response status:", stripeRes.status);
      
      if (stripeRes.ok) {
        const stripeData = await stripeRes.json();
        console.log("Otrzymany link ze Stripe:", stripeData);
        if (stripeData && stripeData.url) {
          window.location.assign(stripeData.url);
          return;
        }
      } else {
        const errText = await stripeRes.text();
        console.error("Szczegóły błędu backendu (body):", errText);
      }
      
      // Fallback if Stripe is not configured or error occurs
      console.warn("Brak URL ze Stripe, zatrzymuję przekierowanie do debugowania...");
      alert("Błąd backendu! Otwórz konsolę (F12) i zrób zrzut ekranu komunikatu.");
    } catch(e) { 
      console.error("Upgrade plan error (Network or CORS):", e); 
      alert("Błąd połączenia/CORS! Otwórz konsolę (F12) i skopiuj błąd.");
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ailinkb_user');
    window.location.href = 'https://ailinkb.shop';
  };

  const handleForgotPin = async () => {
    if (!authEmail) { setAuthError(t("err_enter_email", "Please enter your email first")); return; }
    setAuthError(t("err_sending_pin", "Sending new PIN..."));
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authEmail })
      });
      const data = await res.json();
      setAuthError(data.message);
    } catch (e) { setAuthError(t("err_server", "Server error")); }
  };

  // 1. Initial / One-time load per user
  useEffect(() => {
    // FORCE_NEW_BUILD_HASH_20260802_TRASH_EMPTY_FIX
    if (!user || isWidgetMode) return;
    fetch(`${API_BASE_URL}/api/crm/auto-sort?ownerEmail=${encodeURIComponent(user.email)}`, {method:'POST'}).catch(() => {});
    fetchServices(); fetchSocialPosts(); fetchReports(); fetchConfig();
    fetchEmailTemplates(); 
    
    fetch(`${API_BASE_URL}/api/crm/folder-counts?ownerEmail=${encodeURIComponent(user.email)}&channel=EMAIL&accountEmail=${encodeURIComponent(crmAccountFilter)}`)
      .then(r => r.json())
      .then(counts => {
        if (counts && typeof counts === 'object') setFolderCounts(counts);
      }).catch(() => {});

    // Background vectorization of missing data (run only once per session)
    fetch(`${API_BASE_URL}/api/crm/index-missing?ownerEmail=${user.email}`, {method:'POST'}).catch(() => {});
    fetch(`${API_BASE_URL}/api/leads/index-missing?ownerEmail=${user.email}`, {method:'POST'}).catch(() => {});
  }, [user, isWidgetMode]);

  // 2. Leads Data (Triggered by pagination or category changes)
  useEffect(() => {
    if (!user || isWidgetMode) return;
    fetchLeads();
  }, [page, leadsCategoryFilter, user, isWidgetMode]);

  // 3. Tab-specific Data
  useEffect(() => {
    if (!user || isWidgetMode) return;
    if (activeTab === 'ai-chat') fetchChatSessions();
    if (activeTab === 'calendar') fetchMeetings();
    if (activeTab === 'automations') fetchCampaigns();
    if (activeTab === 'phone-calls') fetchPhoneCalls();
    if (activeTab === 'orders') fetchOrders();
  }, [activeTab, user, isWidgetMode]);

  // 4. Polling for Dashboard Stats
  useEffect(() => {
    if (!user || isWidgetMode) return;
    
    fetchDashboardStats();
    fetchStats();
    
    const interval = setInterval(() => {
        fetchDashboardStats();
        fetchStats();
        if (activeTab === 'ai-chat') fetchChatSessions();
    }, 5000); // Every 5 seconds

    return () => clearInterval(interval);
  }, [user, isWidgetMode, activeTab, currentSessionId]);

  useEffect(() => {
    if (!user || isWidgetMode) return;
    const isCrmTab = activeTab === 'crm-email' || activeTab === 'crm-whatsapp';
    
    if (isCrmTab) {
      fetchCRM();
    }

    // AUTO-REFRESH CRM (Polling)
    const interval = setInterval(() => {
      if (isCrmTab && !crmSearch) { // Only auto-refresh if not actively searching
        fetchCRM();
      }
    }, 5000); // Every 5 seconds

    return () => clearInterval(interval);
  }, [activeTab, crmPage, crmSearch, crmFilter, user, isWidgetMode, crmAccountFilter]);

  if (isOrderWidgetMode) {
    return (
      <Box p="md" style={{ maxWidth: '500px', margin: 'auto', borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
        <Stack gap="md">
          <Group justify="center" mb="md">
            <ThemeIcon size={50} radius="xl" color="indigo" variant="light"><IconCreditCard size={30}/></ThemeIcon>
          </Group>
          <Title order={3} ta="center" c="indigo">Service Order Form</Title>
          <Text size="sm" ta="center" c="dimmed">Choose a service and enter your email to proceed to payment.</Text>
          
          <Divider />

          <Select 
            label="Select Service" 
            placeholder="Choose one..."
            data={services.map(s => ({ value: s.id.toString(), label: `${s.name} (${s.price} ${s.currency})` }))}
            value={selectedServiceId}
            onChange={setSelectedServiceId}
          />

          <TextInput 
            label="Your Email" 
            placeholder="email@example.com" 
            required
            value={widgetVisitorEmail}
            onChange={e => setWidgetVisitorEmail(e.target.value)}
          />

          <Box>
            <Text size="xs" fw={500} mb={5}>Attachment (Optional)</Text>
            {chatImage && (
              <Box mb="xs" style={{ position: 'relative', display: 'inline-block' }}>
                <img src={chatImage} style={{ height: '60px', borderRadius: '4px' }} alt="Attachment" />
                <ActionIcon size="xs" color="red" variant="unstyled" style={{ position: 'absolute', top: -5, right: -5 }} onClick={() => setChatInputImage(null)}>
                  <IconX size={10} />
                </ActionIcon>
              </Box>
            )}
            <Button 
              variant="light" 
              color="gray" 
              size="xs" 
              fullWidth 
              leftSection={<IconPlus size={14}/>}
              onClick={() => document.getElementById('order-file-input').click()}
            >
              {chatImage ? 'Change Image' : 'Add Image'}
            </Button>
            <input 
              type="file" 
              id="order-file-input" 
              hidden 
              accept="image/*" 
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => setChatInputImage(reader.result);
                  reader.readAsDataURL(file);
                }
              }} 
            />
          </Box>

          <Button 
            fullWidth 
            color="indigo" 
            size="lg" 
            disabled={!selectedServiceId || !widgetVisitorEmail.includes('@')}
            onClick={() => {
              setIsChatLoading(true);
              fetch(`${API_BASE_URL}/api/orders/public/create-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                  token: orderToken, 
                  visitorEmail: widgetVisitorEmail, 
                  serviceId: selectedServiceId,
                  attachment: chatImage
                })
              })
              .then(r => r.json())
              .then(data => {
                if (data.url) window.top.location.href = data.url;
              })
              .finally(() => setIsChatLoading(false));
            }}
            loading={isChatLoading}
          >
            Purchase with Stripe
          </Button>
          
          <Text size="xs" ta="center" c="dimmed">Secure payment powered by Stripe.</Text>
        </Stack>
      </Box>
    );
  }

  if (isCalendarWidgetMode) {
    const year = currentCalendarDate.getFullYear();
    const month = currentCalendarDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const monthName = currentCalendarDate.toLocaleString('default', { month: 'long' });

    return (
      <Box h="100vh"  p="xs" style={{ display: 'flex', flexDirection: 'column', backgroundColor: '#1a1b1e', color: 'white' }}>
        <Group justify="space-between" mb="xs" p="xs" style={{ borderBottom: '1px solid #333' }}>
          <img src="/brand/ailinkb-logo.svg" alt="AILinkB" style={{ height: '20px' }} />
          <Text size="xs" fw={700} c="indigo">Book a Meeting</Text>
        </Group>

        {isBookingSuccess ? (
          <Stack align="center" justify="center" style={{ flex: 1 }} p="xl">
            <ThemeIcon size={60} radius="xl" color="green" variant="light"><IconCheck size={36}/></ThemeIcon>
            <Title order={3}>Booking Sent!</Title>
            <Text ta="center" c="dimmed">Thank you. We have received your request for {new Date(newMeeting.startTime).toLocaleDateString()}. We will contact you soon.</Text>
            <Button variant="subtle" onClick={() => setIsBookingSuccess(false)}>Book another</Button>
          </Stack>
        ) : (
          <ScrollArea style={{ flex: 1 }} p="xs">
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={700}>{monthName} {year}</Text>
                <Group gap={5}>
                  <ActionIcon size="sm" onClick={() => setCurrentCalendarDate(new Date(year, month - 1, 1))}><IconChevronLeft size={14}/></ActionIcon>
                  <ActionIcon size="sm" onClick={() => setCurrentCalendarDate(new Date(year, month + 1, 1))}><IconChevronRight size={14}/></ActionIcon>
                </Group>
              </Group>

              <SimpleGrid cols={7} spacing={2}>
                {['M','T','W','T','F','S','S'].map((d,i) => <Text key={i} size="xs" ta="center" fw={700} c="dimmed">{d}</Text>)}
                {Array.from({ length: firstDay }).map((_, i) => <Box key={`e-${i}`} />)}
                {days.map(day => (
                  <Button 
                    key={day} 
                    variant={newMeeting.startTime?.includes(`-${String(day).padStart(2,'0')}T`) ? 'filled' : 'light'} 
                    size="compact-xs" 
                    color="indigo"
                    onClick={() => {
                      const dStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                      setNewMeeting({...newMeeting, startTime: dStr + 'T10:00'});
                      fetchBusySlots(dStr);
                    }}
                  >
                    {day}
                  </Button>
                ))}
              </SimpleGrid>

              {newMeeting.startTime && (
                <Card withBorder p="sm" bg="#141517">
                  <Stack gap="xs">
                    <Text size="xs" fw={700}>Select Time & Details:</Text>
                    
                    <SimpleGrid cols={4} spacing="xs">
                      {['09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00'].map(time => {
                        const isBusy = busySlots.some(s => s.startTime.includes('T'+time));
                        return (
                          <Button 
                            key={time} 
                            size="compact-xs" 
                            variant={newMeeting.startTime.includes('T'+time) ? 'filled' : 'outline'}
                            color={isBusy ? 'red' : 'indigo'}
                            disabled={isBusy}
                            onClick={() => setNewMeeting({...newMeeting, startTime: newMeeting.startTime.split('T')[0] + 'T' + time})}
                          >
                            {time} {isBusy ? '🔒' : ''}
                          </Button>
                        );
                      })}
                    </SimpleGrid>

                    <TextInput spellCheck={false} 
                      label="Your Email" 
                      size="xs" 
                      required 
                      placeholder="you@email.com"
                      value={widgetVisitorEmail} 
                      onChange={e => setWidgetVisitorEmail(e.target.value)} 
                    />
                    <TextInput spellCheck={false} 
                      label="Meeting Topic" 
                      size="xs" 
                      required 
                      placeholder="e.g. Discuss new project"
                      value={newMeeting.title} 
                      onChange={e => setNewMeeting({...newMeeting, title: e.target.value})} 
                    />
                    <SimpleGrid cols={2}>
                      <Select 
                        label="Duration" 
                        size="xs" 
                        data={[
                          { value: '15', label: '15 min' },
                          { value: '30', label: '30 min' },
                          { value: '60', label: '1 hour' }
                        ]}
                        value={bookingDuration}
                        onChange={setBookingDuration}
                      />
                      <TextInput spellCheck={false} 
                        label="Start Time" 
                        type="time" 
                        size="xs" 
                        value={newMeeting.startTime.split('T')[1]} 
                        onChange={e => setNewMeeting({...newMeeting, startTime: newMeeting.startTime.split('T')[0] + 'T' + e.target.value})} 
                      />
                    </SimpleGrid>
                    <Button fullWidth size="xs" mt="sm" color="indigo" onClick={handleCalendarBooking} loading={isChatLoading}>Confirm Booking</Button>
                  </Stack>
                </Card>
              )}
            </Stack>
          </ScrollArea>
        )}
      </Box>
    );
  }

  if (isJobProfileMode) {
    return <JobProfile />;
  }

  if (isProfileMode) {
    return <CompanyProfile />;
  }

  if (isLivePreviewMode) {
    let prevConfig = {};
    let prevServices = [];
    let prevEmail = 'preview@twojafirma.pl';
    try {
      prevConfig = JSON.parse(localStorage.getItem('live_preview_config') || '{}');
      prevServices = JSON.parse(localStorage.getItem('live_preview_services') || '[]');
      prevEmail = localStorage.getItem('live_preview_email') || 'preview@twojafirma.pl';
    } catch(e) {}

    return (
      <Box style={{ position: 'relative', minHeight: '100vh' }}>
        <Box p="xs" bg="#1e293b" c="white" style={{ position: 'sticky', top: 0, zIndex: 1000, display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.3)', paddingLeft: '20px', paddingRight: '20px' }}>
          <Group gap="sm">
            <Badge color="yellow" variant="unstyled">{t('preview_banner_title')}</Badge>
            <Text size="sm" c="gray.3" span>{t('preview_banner_desc')}</Text>
          </Group>
          <Button size="xs" variant="unstyled" color="red" onClick={() => window.close()}>{t('preview_banner_close')}</Button>
        </Box>
        <CompanyProfile email={prevEmail} config={prevConfig} services={prevServices} announcements={[]} />
      </Box>
    );
  }

  if (isTermsMode) {
    return (
      <Container size="md" py="xl" style={{ minHeight: '100vh' }}>
        <Group justify="space-between" mb="xl">
          <img src="/brand/ailinkb-logo.svg" style={{ height: 40, width: 'auto', objectFit: 'contain' }} alt="AILinkB" />
          <Group>
            <Select 
              data={[
                { value: 'en', label: '🇬🇧 EN' },
                { value: 'pl', label: '🇵🇱 PL' },
                { value: 'de', label: '🇩🇪 DE' },
                { value: 'fr', label: '🇫🇷 FR' }
              ]}
              value={i18n.language.split('-')[0] || 'en'}
              onChange={(val) => i18n.changeLanguage(val)}
              w={100}
              size="sm"
            />
            <Button variant="light" color="indigo" onClick={() => window.location.href = '/'}>{t('back_to_home')}</Button>
          </Group>
        </Group>
        <Paper p="xl" radius="md" withBorder>
          <Title order={2} mb="xl">{t('privacy_policy_title')}</Title>
          <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{t('privacy_policy_content')}</Text>
        </Paper>
      </Container>
    );
  }

  if (isWidgetMode) {
    return (
      <Box h="100vh"  p="xs" style={{ display: 'flex', flexDirection: 'column', backgroundColor: '#1a1b1e', color: 'white' }}>
        <Group justify="space-between" mb="xs" p="xs" style={{ borderBottom: '1px solid #333' }}>
          <Group gap="xs">
            <img src="/brand/ailinkb-logo.svg" alt="AILinkB" style={{ height: '20px' }} />
            <Text size="xs" fw={700} c="indigo">{t('widget_title', 'AI Assistant')}</Text>
          </Group>
          {isVisitorIdentified && (
            <ActionIcon size="sm" variant={isVoiceReplyEnabled ? "filled" : "light"} color="indigo" onClick={() => {
              if (isVoiceReplyEnabled && 'speechSynthesis' in window) window.speechSynthesis.cancel();
              setIsVoiceReplyEnabled(!isVoiceReplyEnabled);
            }}>
              {isVoiceReplyEnabled ? <IconVolume size={16} /> : <IconVolumeOff size={16} />}
            </ActionIcon>
          )}
        </Group>

        {!isVisitorIdentified ? (
          <Stack p="md" gap="md" align="center" justify="center" style={{ flex: 1 }}>
            <ThemeIcon size={40} radius="xl" color="indigo" variant="light"><IconMail size={24}/></ThemeIcon>
            <Text size="sm" ta="center" fw={500} c="white">{t('widget_greeting', 'How can we help you today?')}<br/><Text span size="xs" c="dimmed">{t('widget_email_prompt', 'Please enter your email to start chatting.')}</Text></Text>
            <TextInput spellCheck={false} 
              placeholder={t('email_address', 'your@email.com')} 
              fullWidth 
              value={widgetVisitorEmail} 
              onChange={e => setWidgetVisitorEmail(e.target.value)} 
            />
            <Checkbox 
              size="xs"
              label={<Text size="xs">{t('chat_consent_label', 'I accept the ')} <Anchor size="xs" onClick={() => window.open('https://www.ailinkb.com/terms', '_blank')}>{t('privacy_policy_link', 'Terms of Service')}</Anchor></Text>}
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.currentTarget.checked)}
            />
            <Button 
              fullWidth 
              color="indigo" 
              disabled={!termsAccepted || !widgetVisitorEmail.includes('@')}
              onClick={() => {
                setIsChatLoading(true);
                if (!widgetToken) {
                  // Send PIN / Create potential account if email is new ONLY for AILinkB main site
                  fetch(`${API_BASE_URL}/api/auth/forgot-pin`, { 
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: widgetVisitorEmail })
                  }).finally(() => {
                    setIsVisitorIdentified(true);
                    setIsChatLoading(false);
                    const compName = config['company.name'] || 'the company';
                    setChatMessages([{ role: 'AI', content: t('ai_greeting'), sentAt: new Date() }]);
                  });
                } else {
                  // External widget mode - just identify the user as a lead
                  setIsVisitorIdentified(true);
                  setIsChatLoading(false);
                  const compName = config['company.name'] || 'the company';
                  setChatMessages([{ role: 'AI', content: t('ai_greeting'), sentAt: new Date() }]);
                }
              }}
              loading={isChatLoading}
            >
              Start Chat
            </Button>
          </Stack>
        ) : (
          <>
            <ScrollArea style={{ flex: 1 }} p="xs" viewportRef={chatViewportRef} onScrollPositionChange={(pos) => { if (chatViewportRef.current) { isScrolledUpRef.current = (chatViewportRef.current.scrollHeight - pos.y - chatViewportRef.current.clientHeight) > 150; } }}>
              <Stack gap="xs">
                {(Array.isArray(chatMessages) ? chatMessages : []).map((msg, idx) => (
                  <Flex key={idx} justify={msg.role === 'USER' ? 'flex-end' : 'flex-start'}>
                    <Box p="sm" bg={msg.role === 'USER' ? 'indigo.6' : '#2C2E33'} c='white' style={{ borderRadius: '10px', maxWidth: '85%' }}>
                      <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{String(msg.content)}</Text>
                    </Box>
                  </Flex>
                ))}
                {chatFallbackMessage && (
                  <Flex justify="flex-start">
                    <Box p="sm" bg="dark.8" c="white" style={{ borderRadius: '10px', maxWidth: '85%' }}>
                      <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{chatFallbackMessage}</Text>
                    </Box>
                  </Flex>
                )}
                {isChatLoading && !chatFallbackMessage && <Text size="xs" c="dimmed" ml="xs">AI is thinking...</Text>}
              </Stack>
            </ScrollArea>
            <Stack gap={0} style={{ borderTop: '1px solid #333' }}>
              {chatImage && (
                <Box p="xs" style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                  <img src={chatImage} style={{ height: '50px', borderRadius: '4px' }} alt="Attachment" />
                  <ActionIcon size="xs" color="red" variant="unstyled" style={{ position: 'absolute', top: 0, right: 0 }} onClick={() => setChatInputImage(null)}>
                    <IconX size={10} />
                  </ActionIcon>
                </Box>
              )}
              <Group gap="xs" p="xs">
                <ActionIcon color="gray" variant="subtle" onClick={() => document.getElementById('widget-file-input').click()}>
                  <IconPlus size={18}/>
                </ActionIcon>
                <input 
                  type="file" 
                  id="widget-file-input" 
                  hidden 
                  accept="image/*" 
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => setChatInputImage(reader.result);
                      reader.readAsDataURL(file);
                    }
                  }} 
                />
                <TextInput spellCheck={false}
                  placeholder="Napisz wiadomość..."
                  style={{ flex: 1 }}
                  size="lg"
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendWidgetMessage()}
                />
                <ActionIcon 
                  size={50} 
                  radius="xl" 
                  color={isListening ? "red" : "gray"} 
                  variant={isListening ? "filled" : "light"} 
                  onClick={toggleListening}
                  style={{ boxShadow: isListening ? '0 0 15px rgba(239,68,68,0.5)' : 'none' }}
                >
                  <IconMicrophone size={28}/>
                </ActionIcon>
                <ActionIcon 
                  size={50} 
                  radius="xl" 
                  color="indigo"
                  variant="unstyled"
                  onClick={() => {
                    if (isVoiceReplyEnabled && 'speechSynthesis' in window) window.speechSynthesis.cancel();
                    setIsVoiceReplyEnabled(!isVoiceReplyEnabled);
                  }}
                  title={isVoiceReplyEnabled ? "Wycisz Chat" : "Włącz Głos Chatu"}
                >
                  {isVoiceReplyEnabled ? <IconVolume size={28}/> : <IconVolumeOff size={28}/>}
                </ActionIcon>
                <ActionIcon id="widget-chat-submit-btn" size={50} radius="xl" color="indigo" variant="unstyled" onClick={handleSendWidgetMessage} loading={isChatLoading}><IconSend size={24}/></ActionIcon>
              </Group>
            </Stack>

          </>
        )}
      </Box>
    );
  }

  if (!user && !isEditingPublicProfile && onboardingStep === 'landing') {
      return (
        <AppShell
          header={{ height: 70 }}
          footer={{ height: 40 }}
          padding={0}
        >
          {/* Landing Header */}
          <AppShell.Header px="md" py="xs" style={{ background: '#000', borderBottom: '1px solid #333', display: 'flex', flexDirection: 'column', height: 'auto', minHeight: '70px' }}>
            {serverOffline && (
              <Box bg="red.1" p="xs" ta="center" w="100%" mb="xs" style={{ borderRadius: '8px', border: '1px solid #fecaca' }}>
                <Text c="red.9" size="sm" fw={600}>{t('chat_update_offline')}</Text>
              </Box>
            )}
            <Group justify="space-between" align="center" h="100%">
              <Group gap="xs">
                <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
                <img src="/brand/ailinkb-logo.svg" alt="AILinkB Logo" style={{ height: '32px' }} />
                <Box visibleFrom="sm">
                  <Title order={4} c="indigo.7" style={{ lineHeight: 1 }}>AILinkB<Text span c="dimmed" size="xs"> Intelligence</Text></Title>
                </Box>
              </Group>
              <Group gap="sm">
                <LanguageSwitcher />
                <Button variant="outline" color="gray" c="white" onClick={() => setCartDrawerOpen(true)} leftSection={<IconShoppingCart size={18}/>} style={{ borderColor: 'rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  {t('cart_btn')} ({cart.length})
                </Button>
                <Button className="animated-gradient-btn" size="md" radius="xl" onClick={() => { setIsRegistering(true); setOnboardingStep('form'); }}>
                  Start Selling Prompts Now
                </Button>
              </Group>
            </Group>
          </AppShell.Header>

          

          <AppShell.Main bg="#000">
            <Box style={{ flex: 1, height: 'calc(100vh - 110px)', width: '100%' }}>
              <MarketplaceFeed addToCart={addToCart} />
            </Box>
          </AppShell.Main>

          <AppShell.Footer px="md" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', borderTop: '1px solid #333', color: '#fff' }}>
            <Text size="xs" c="dimmed">
              Copyright © {new Date().getFullYear()} AILinkB. QR-SYSTEM sp. z.o.o. Płaza ul. Graniczna 13B Kapitał zakładowy 10 000 PLN.
            </Text>
          </AppShell.Footer>
        <Drawer opened={cartDrawerOpen} onClose={() => setCartDrawerOpen(false)} title={<Title order={4} c="indigo">{t('your_cart')}</Title>} position="right" padding="md">
          {cart.length === 0 ? (
            <Text c="dimmed" ta="center" mt="xl">{t('cart_empty')}</Text>
          ) : (
            <Stack gap="md">
              {cart.map((item, idx) => (
                <Card key={idx} withBorder shadow="sm" radius="md">
                  <Text fw={500}>{item.serviceName}</Text>
                  <Text size="sm" c="dimmed">Sprzedawca: {item.ownerEmail}</Text>
                  <Group justify="space-between" mt="sm">
                    <Text fw={700} c="indigo">{item.amount} {item.currency || 'PLN'}</Text>
                    <Button size="xs" color="red" variant="subtle" onClick={() => removeFromCart(idx)}>Usuń</Button>
                  </Group>
                </Card>
              ))}
              <Divider my="sm" />
              <Group justify="space-between">
                <Text fw={700} size="lg">Suma:</Text>
                <Text fw={700} size="xl" c="indigo">{cart.reduce((sum, i) => sum + i.amount, 0).toFixed(2)} PLN</Text>
              </Group>
              <Button fullWidth size="lg" color="green" mt="md" onClick={() => {
                const visitorEmail = prompt('Podaj swój adres e-mail, aby dokończyć zamówienie:');
                if (visitorEmail) {
                  fetch(API_BASE_URL + '/api/stripe/create-global-checkout', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ visitorEmail, items: cart })
                  }).then(res => res.json()).then(data => {
                    if (data.url) window.location.href = data.url;
                  }).catch(e => alert(t('payment_error')));
                }
              }}>{t('checkout_cart')}</Button>
              <Button fullWidth variant="subtle" color="gray" onClick={clearCart}>{t('clear_cart')}</Button>
            </Stack>
          )}
        </Drawer>
      </AppShell>
    );
    }


  if (!user && onboardingStep === 'form') {
      return (
        <Flex align="center" justify="center" style={{ minHeight: '100vh' }} py="xl" >
          <Card withBorder shadow="xl" p={40} radius="lg" w={520} style={{ margin: 'auto', position: 'relative' }}>
            <Box style={{ position: 'absolute', top: 15, right: 15 }}>
              <Select 
                size="xs"
                w={100}
                value={i18n.language.split('-')[0]}
                onChange={(val) => i18n.changeLanguage(val)}
                data={[
                  { value: 'en', label: 'English' },
                  { value: 'pl', label: 'Polski' },
                  { value: 'de', label: 'Deutsch' },
                  { value: 'fr', label: 'Français' },
                  { value: 'es', label: 'Español' },
                  { value: 'it', label: 'Italiano' },
                  { value: 'pt', label: 'Português' },
                  { value: 'nl', label: 'Nederlands' },
                  { value: 'sv', label: 'Svenska' },
                  { value: 'cs', label: 'Čeština' }
                ]}
              />
            </Box>
            <Stack gap="xl">
              <Box style={{ textAlign: 'center' }}>
                <img src="/brand/ailinkb-logo.svg" alt="AILinkB" style={{ height: '60px', marginBottom: '10px' }} />
                <Title order={3} c="indigo.8">{isRegistering ? t('register_card_title') : t('welcome_back')}</Title>
                <Text size="sm" c="dimmed" mt="xs">{isRegistering ? t('register_card_desc') : t('login_first_to_create_card')}</Text>
                <Box mt="md">
                  {isRegistering ? (
                    <Text size="sm">{t('have_account_login')} <Anchor fw={700} onClick={() => { setIsRegistering(false); setAuthError(''); setAuthPin(''); }}>{t("login_action")}</Anchor></Text>
                  ) : (
                    <Text size="sm">{t('need_account_start')} <Anchor fw={700} onClick={() => { setIsRegistering(true); setAuthError(''); setAuthPin(''); }}>{t("create_account_action")}</Anchor></Text>
                  )}
                </Box>
              </Box>

              {/* <Box>
                <Text size="xs" fw={700} c="dimmed" ta="center" mb="md">{t('social_login_title')}</Text>
                <Stack gap="sm">
                  <Button size="md" radius="md" variant="default" onClick={() => triggerSocialLogin('Google')} leftSection={<IconBrandGoogle size={20} color="#EA4335" />} style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)', fontWeight: 600 }}>
                    {t('login_with_google')}
                  </Button>
                  <Button size="md" radius="md" variant="unstyled" color="#1877F2" onClick={() => triggerSocialLogin('Facebook')} leftSection={<IconBrandFacebook size={20} />} style={{ boxShadow: '0 2px 4px rgba(24, 119, 242, 0.2)', fontWeight: 600 }}>
                    {t('login_with_facebook')}
                  </Button>
                  <Button size="md" radius="md" variant="unstyled" color="black" onClick={() => triggerSocialLogin('Apple')} leftSection={<IconBrandApple size={20} />} style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.2)', fontWeight: 600 }}>
                    {t('login_with_apple')}
                  </Button>
                </Stack>
              </Box>

              <Divider label={isRegistering ? t('or_register_email') : t('or_pin_login')} labelPosition="center" /> */}
              
              <form onSubmit={handleAuth}>
                <Stack gap="md">
                  <TextInput spellCheck={false} 
                    label={t('email_address')} placeholder="name@company.com" required value={authEmail} onChange={e => setAuthEmail(e.target.value)} />
                  {isRegistering && (
                    <Checkbox 
                      label={<Text size="sm">{t('chat_consent_label')} <Anchor size="sm" onClick={(e) => { e.preventDefault(); setTermsModalOpened(true); }}>{t('privacy_policy_link')}</Anchor></Text>}
                      checked={termsAccepted} onChange={(event) => setTermsAccepted(event.currentTarget.checked)}
                      mb="md"
                    />
                  )}
                  {!isRegistering && (
                    <Box>
                      <Group justify="space-between" mb={5}>
                        <Text size="sm" fw={500}>{t('security_pin')}</Text>
                        <Anchor size="xs" onClick={handleForgotPin} c="indigo">{t('forgot_pin')}</Anchor>
                      </Group>
                      <Group justify="center">
                        <PinInput length={6} type="number" mask oneTimeCode value={authPin} onChange={setAuthPin} onComplete={() => setTimeout(() => document.getElementById('auth-submit-btn').click(), 50)} size="lg" />
                      </Group>
                    </Box>
                  )}
                  {authError && <Text size="xs" c={authError.includes('🎉') ? 'green.8' : 'red.6'} fw={700} ta="center" p="xs" bg={authError.includes('🎉') ? '#ecfdf5' : '#fef2f2'} style={{ borderRadius: '6px', border: authError.includes('🎉') ? '1px solid #34d399' : '1px solid #f87171' }}>{authError}</Text>}
                  <Button id="auth-submit-btn" fullWidth size="md" color="indigo" type="submit" leftSection={!isRegistering ? <IconLock size={18}/> : <IconRocket size={18}/>} disabled={(!isRegistering && authPin.length < 6) || (isRegistering && !termsAccepted)}>{isRegistering ? t('btn_send_register_pin') : t('login_with_pin')}</Button>
                </Stack>
              </form>

              <Divider label={t('or')} labelPosition="center" />
              <Button variant="subtle" color="gray" size="xs" onClick={() => setOnboardingStep('landing')}>{t('back_to_home')}</Button>
            </Stack>
          </Card>

          <Modal opened={socialModalOpened} onClose={() => setSocialModalOpened(false)} title={`${t('social_login_title')} - ${selectedProvider}`} centered radius="md">
            <Stack gap="md" p="sm">
              <Group justify="center">
                {selectedProvider === 'Google' && <IconBrandGoogle size={48} color="#EA4335" />}
                {selectedProvider === 'Facebook' && <IconBrandFacebook size={48} color="#1877F2" />}
                {selectedProvider === 'Apple' && <IconBrandApple size={48} color="#000000" />}
              </Group>
              <Text size="sm" ta="center" c="dimmed">
                {i18n.language === 'pl' ? `Autoryzujesz logowanie za pomocą konta ${selectedProvider}. Wpisz lub potwierdź swój adres e-mail, aby kontynuować lub automatycznie utworzyć nowe konto .` : `Authorizing login via ${selectedProvider}. Please confirm your email address to sign in .`}
              </Text>
              <TextInput label={t('email_address')} placeholder="twoj-email@domain.com" value={socialEmail} onChange={(e) => setSocialEmail(e.target.value)} required />
              <Button fullWidth size="md" color={selectedProvider === 'Facebook' ? '#1877F2' : selectedProvider === 'Apple' ? 'black' : 'red'} onClick={executeSocialLogin}>
                {t('sign_in')} ({selectedProvider})
              </Button>
            </Stack>
          </Modal>

          <Modal opened={termsModalOpened} onClose={() => setTermsModalOpened(false)} title={t('privacy_policy_title')} size="lg">
            <ScrollArea h={400} p="md"><Stack gap="md">
              <Title order={4}>{t('privacy_policy_title')}</Title>
              <Text size="sm" style={{ whiteSpace: 'pre-line' }}>{t('privacy_policy_content')}</Text>
            </Stack></ScrollArea>
            <Button fullWidth mt="md" onClick={() => { setTermsAccepted(true); setTermsModalOpened(false); }}>Accept</Button>
          </Modal>
        </Flex>
      );
    }

    if (onboardingStep === 'success') {
      return (
        <Box  style={{ minHeight: '100vh', overflowY: 'auto' }}>
          <Container size="lg" py={50}>
            <Stack gap={40}>
              <Box style={{ textAlign: 'center' }}>
                <ThemeIcon size={80} radius="xl" color="green" variant="light" mb="md"><IconCheck size={40}/></ThemeIcon>
                <Title order={1}>{t('onboard_success_title')}</Title>
                <Text size="xl" c="dimmed">{t('onboard_success_desc', 'We\'ve sent your access PIN to')} <strong>{authEmail}</strong>. {t('onboard_success_check_inbox')}</Text>
              </Box>
              <Grid gutter="xl">
                <Grid.Col span={{ base: 12, md: 7 }}>
                  <Card withBorder shadow="sm" radius="md" p="xl">
                    <Text mb="xl" size="md">{t("onboard_success_instructions", "You will receive the installation instructions and widget code to your email after your subscription is activated. You can also access it later in your dashboard.")}</Text>
                    <Button fullWidth mt="xl" size="lg" color="indigo" onClick={() => setOnboardingStep('form')}>{t("onboard_success_btn_login", "Go to Login & Full Dashboard")}</Button>
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 5 }}>
                  <Card withBorder shadow="sm" radius="md" p={0} style={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
                    <Box p="md" bg="indigo.7" c="white"><Text fw={700}>{t("onboard_live_test", "Live Test Widget")}</Text></Box>
                    <Box style={{ flex: 1 }}><iframe src={`/widget?token=${generatedToken}`} style={{ width: '100%', height: '100%', border: 'none' }}/></Box>
                  </Card>
                </Grid.Col>
              </Grid>
            </Stack>
          </Container>
        </Box>
      );
    }


  const handleManualEmail = (data) => {
    const { to, subject, body, senderEmail, attachments } = data;
    const endpoint = targetLeadId
      ? `${API_BASE_URL}/api/leads/${targetLeadId}/send-email`
      : `${API_BASE_URL}/api/leads/direct-send`;

    const formData = new FormData();
    formData.append('to', to);
    formData.append('subject', subject);
    formData.append('body', body);
    formData.append('ownerEmail', user.email);
    if (senderEmail) formData.append('senderEmail', senderEmail);
    if (attachments && attachments.length > 0) {
      attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }

    fetch(endpoint, {
      method: 'POST',
      body: formData
    }).then(res => {
      // Check for 200, 201, 204 or any other success code
      if (res.status >= 200 && res.status < 300) {
        alert("Email sent!");
        setIsComposing(false);
        setComposeInitialData({ to: '', subject: '', body: '', isHtml: false });
        setTargetLeadId(null);
        fetchCRM();
      } else {
        res.text().then(text => {
          console.error("Backend error response:", text);
          let errMsg = "Status: " + res.status;
          try {
            const parsed = JSON.parse(text);
            if (parsed.message) errMsg = parsed.message;
          } catch(e) {}
          alert("Failed to send email. " + errMsg);
        });
      }
    }).catch(err => {
      console.error("Send error:", err);
      alert("Error connecting to server.");
    });
  };

  const selectLeadForEmail = (lead) => {
    setTargetLeadId(lead.id);
    setComposeInitialData({
      to: lead.email,
      subject: `Proposal for ${lead.companyName}`,
      body: PROFESSIONAL_TEMPLATE(lead.companyName, lead.address),
      isHtml: true
    });
    setActiveTab('crm-email');
    setIsComposing(true);
    setSelectedMsg(null);
  };

  const PROFESSIONAL_TEMPLATE = (company, address) => `
    <p>Dear ${company} Team,</p>
    <p>I noticed your business at ${address || 'your location'} and I'm impressed by your work.
    We specialize in helping companies like yours grow through technology and automation.</p>
    <p>Would you be open to a 5-minute chat about how we can support your growth?</p>
    <p>Best regards,<br/>Your AI Assistant</p>
  `;

  const getSenderName = (sender) => {
    if (!sender) return 'Unknown';
    const match = sender.match(/([^<]+)/);
    return match ? match[1].trim() : sender;
  };

  const replyToMessage = (msg) => {
    if (msg.channel === 'WHATSAPP') {
      setWaComposeData({ to: msg.sender, text: '' });
      setIsWaComposing(true);
      setSelectedMsg(null);
      return;
    }
    setComposeInitialData({
      to: msg.sender,
      subject: "Re: " + msg.subject,
      body: "<br/><br/>---<br/>" + msg.body,
      isHtml: true,
      senderEmail: msg.recipient,
      msgId: msg.id,
      draftReply: msg.draftReply
    });
    setIsComposing(true);
    setSelectedMsg(null);
  };
  const handleAddService = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newServiceName) return;
    const method = editingServiceId ? 'PUT' : 'POST';
    const url = editingServiceId ? `${API_BASE_URL}/api/services/${editingServiceId}` : `${API_BASE_URL}/api/services`;
    fetch(url, {
      method: method, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: editingServiceId, name: newServiceName, description: newServiceDesc, price: newServicePrice, currency: newServiceCurrency, hidden: newServiceHidden, ownerEmail: user.email }),
    }).then(async (res) => {
      if (!res.ok) {
        if (res.status === 403) {
          const errData = await res.json();
          if (errData.error === 'LIMIT_REACHED') {
            if (window.confirm(errData.message + '\n\nCzy chcesz teraz wykupić pakiet +10 produktów za 10 PLN?')) {
              fetch(`${API_BASE_URL}/api/stripe/create-addon-checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: user.email })
              }).then(r => r.json()).then(data => {
                if (data.url) window.location.href = data.url;
              });
            }
            return;
          }
        }
        throw new Error('Failed to save service');
      }
      setNewServiceName(''); setNewServiceDesc(''); setNewServicePrice(0); setNewServiceCurrency('PLN'); setNewServiceHidden(false); setEditingServiceId(null);
      setIsAddingNewService(false);
      setKnowledgeAccordionValue(null);
      fetchServices();
    }).catch(e => {
        if(e.message !== 'Failed to save service') console.error(e);
    });
  };

  const startEdit = (service) => {
    setEditingServiceId(service.id);
    setNewServiceName(service.name);
    setNewServiceDesc(service.description || '');
    setNewServicePrice(service.price || 0);
    setNewServiceCurrency(service.currency || 'PLN');
    setNewServiceHidden(service.hidden || false);
  };

  const isAdmin = user && (user.role === 'ADMIN' || user.email === 'office@ailinkb.com' || user.email === 'nowojowski@nu-solid.com');
  const isProUser = isAdmin || (user && user.subscriptionPlan === 'PRO_2000');

  const trialDaysLeft = 0;
  
  const isTrialExpired = false;
  const isAccountDeactivated = !isAdmin && user && (user.subscriptionStatus === 'CANCELED' || user.subscriptionStatus === 'PENDING' || isTrialExpired);

  const isProTab = (tab) => {
    const proTabs = ['crm-email', 'crm-whatsapp', 'automations', 'ad-campaigns', 'google-maps', 'templates', 'blacklist', 'reports', 'services', 'social-media'];
    return proTabs.includes(tab);
  };

  return (
    <AppShell
      header={{ height: 70 }}
      navbar={{ width: 250, breakpoint: 'sm', collapsed: { mobile: !mobileOpened } }}
      footer={{ height: 30 }}
      padding="md"
    >
      <AppShell.Header px="md" py="xs">
        <Group justify="space-between" align="center" h="100%">
          <Group gap="xs">
            <Burger opened={mobileOpened} onClick={toggleMobile} hiddenFrom="sm" size="sm" />
            <img src="/brand/ailinkb-logo.svg" alt="AILinkB Logo" style={{ height: '32px' }} />
            <Box visibleFrom="sm">
              <Title order={4} c="indigo.7" style={{ lineHeight: 1 }}>AILinkB<Text span c="dimmed" size="xs"> Intelligence</Text></Title>
            </Box>
          </Group>

          <Group gap="xs">
            {isAdmin ? (
              <Badge size="lg" color="yellow" variant="unstyled" style={{ cursor: 'pointer' }} onClick={() => setPlansModalOpened(true)}>👑 {t('admin_full_access')}</Badge>
            ) : isProUser ? (
              <Badge size="lg" color="indigo" variant="unstyled" style={{ cursor: 'pointer', boxShadow: '0 2px 8px rgba(79, 70, 229, 0.3)' }} onClick={() => setPlansModalOpened(true)}>🥇 {t('plan_pro_title')}</Badge>
            ) : user.subscriptionStatus === 'TRIAL' ? (
              <Group gap={6}>
                <Badge size="md" color="yellow" variant="light" leftSection={<IconClock size={14}/>}>
                  FREE ACCOUNT
                </Badge>
                <Button size="compact-xs" color="indigo" variant="gradient" gradient={{ from: 'indigo', to: 'violet' }} onClick={() => setPlansModalOpened(true)}>
                  ⚡ {t('btn_activate_subscription')}
                </Button>
              </Group>
            ) : (
              <Badge size="lg" color="blue" variant="light" style={{ cursor: 'pointer' }} onClick={() => setPlansModalOpened(true)}>🥉 {t('plan_basic_title')}</Badge>
            )}
            <Select 
              value={i18n.language?.substring(0, 2) || 'en'}
              onChange={(val) => i18n.changeLanguage(val)}
              data={[
                { value: 'en', label: 'English' },
                { value: 'pl', label: 'Polski' },
                { value: 'de', label: 'Deutsch' },
                { value: 'fr', label: 'Français' },
                { value: 'es', label: 'Español' },
                { value: 'it', label: 'Italiano' },
                { value: 'pt', label: 'Português' },
                { value: 'nl', label: 'Nederlands' },
                { value: 'sv', label: 'Svenska' },
                { value: 'cs', label: 'Čeština' }
              ]}
              size="xs"
              style={{ width: '100px' }}
              visibleFrom="sm"
            />
            <Badge 
              color={user.subscriptionStatus === 'ACTIVE' ? 'green' : 'blue'} 
              variant="unstyled" 
              size="xs"
              visibleFrom="xs"
            >
              {user.subscriptionStatus || 'TRIAL'}
            </Badge>
            <Divider orientation="vertical" visibleFrom="xs" />
            <ThemeIcon variant="light" color="indigo" size="sm" radius="xl" visibleFrom="xs"><IconLock size={12}/></ThemeIcon>
            <Text size="xs" fw={700} c="indigo.9" visibleFrom="xs">{user.email}</Text>
            <ActionIcon variant="outline" color="red" size="sm" onClick={handleLogout} title="Logout">
              <IconLogout size={16}/>
            </ActionIcon>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        
          <Button 
            color="red" 
            fullWidth 
            mb="md" 
            size="md" 
            leftSection={<IconUpload size={18} />}
            onClick={() => { setActiveTab('publish'); if (mobileOpened) toggleMobile(); }}
            style={{ boxShadow: '0 4px 14px 0 rgba(224, 49, 49, 0.39)', transition: 'transform 0.2s ease' }}
          >
            Publish Skill
          </Button>
<ScrollArea h="100%">
          <Tabs 
            value={activeTab} 
            onChange={(val) => { setActiveTab(val); if (mobileOpened) toggleMobile(); }} 
            variant="pills" 
            orientation="vertical"
            styles={{
              list: { width: '100%', borderRight: 'none' },
              tab: { 
                width: '100%', 
                justifyContent: 'flex-start', 
                padding: '12px 15px',
                fontSize: '15px',
                fontWeight: 600
              }
            }}
          >
            <Tabs.List>
              <Text size="xs" fw={700} c="dimmed" mt="xs" mb={4} ml="xs" tt="uppercase" w="100%">{t('label_basic_package')}</Text>
              <Tabs.Tab value="dashboard" leftSection={<IconChartBar size={16}/>}>{t('tab_dashboard')}</Tabs.Tab>
              <Tabs.Tab value="leads" leftSection={<IconDatabase size={16}/>}>{t('tab_customers')}</Tabs.Tab>

                            <Tabs.Tab value="publish" leftSection={<IconUpload size={16} color="violet"/>}>
                {t('tab_publish_sell', 'Opublikuj / Sprzedawaj')}
              </Tabs.Tab>
              <Tabs.Tab value="knowledge-base" leftSection={<IconRocket size={16}/>}>{t('tab_my_skills', 'Moje Skille')}</Tabs.Tab>
                  <Tabs.Tab value="mcp-server" leftSection={<IconServer size={16} color="indigo"/>}>{t('tab_mcp')}</Tabs.Tab>
              {isProUser && <Tabs.Tab value="orders" leftSection={<IconChartBar size={16}/>}>{t('tab_orders')}</Tabs.Tab>}
              <Tabs.Tab 
                value="calendar" 
                leftSection={<IconCalendar size={16}/>}
                rightSection={dashboardStats.todayMeetings > 0 ? (
                  <Badge size="xs" color="blue" variant="unstyled" circle>
                    {dashboardStats.todayMeetings}
                  </Badge>
                ) : null}
              >
                {t('tab_appointments')}
              </Tabs.Tab>
              
              <Tabs.Tab value="website" leftSection={<IconWorld size={16} color="teal"/>}>{t('tab_website')}</Tabs.Tab>

              <Text size="xs" fw={700} c="dimmed" mt="md" mb={4} ml="xs" tt="uppercase" w="100%">{t('label_premium_modules')}</Text>
              <Tabs.Tab 
                value="ai-chat" 
                leftSection={<IconMessages size={16} color="#6366f1"/>}
                rightSection={dashboardStats.unreadChats > 0 ? (
                  <Badge size="xs" color="green" variant="unstyled">
                    {dashboardStats.unreadChats}
                  </Badge>
                ) : null}
              >
                {t('tab_visitor_chats', 'Czaty')}
              </Tabs.Tab>
              <Tabs.Tab value="social-media" leftSection={<IconShieldLock size={16} color="#6366f1"/>} onClick={() => setSocialSubTab('upload')}>{t('tab_social_studio')}</Tabs.Tab>
              {activeTab.startsWith('social') && (
                <Stack gap={0} ml="md" mt={2} mb="xs" style={{ borderLeft: '2px solid #e2e8f0' }}>
                  <Tabs.Tab value="social-media" onClick={() => setSocialSubTab('upload')} leftSection={<IconUpload size={14} color="#6366f1"/>} style={{ padding: '8px 12px', fontSize: '13px', background: socialSubTab === 'upload' ? '#f0f4ff' : 'transparent', color: socialSubTab === 'upload' ? '#4f46e5' : '#475569' }}>
                    {t('tab_social_sub_upload', 'Wrzutka & Wideo')}
                  </Tabs.Tab>
                  <Tabs.Tab value="social-media" onClick={() => setSocialSubTab('skill_videos')} leftSection={<IconVideo size={14} color="#6366f1"/>} style={{ padding: '8px 12px', fontSize: '13px', background: socialSubTab === 'skill_videos' ? '#f0f4ff' : 'transparent', color: socialSubTab === 'skill_videos' ? '#4f46e5' : '#475569' }}>
                    {t('tab_social_sub_skill_videos', 'Wideo (Skille)')}
                  </Tabs.Tab>
                  <Tabs.Tab value="social-media" onClick={() => setSocialSubTab('connectors')} leftSection={<IconPlug size={14} color="#6366f1"/>} style={{ padding: '8px 12px', fontSize: '13px', background: socialSubTab === 'connectors' ? '#f0f4ff' : 'transparent', color: socialSubTab === 'connectors' ? '#4f46e5' : '#475569' }}>
                    {t('tab_social_sub_connectors', 'Konektory API')}
                  </Tabs.Tab>
                  <Tabs.Tab value="social-media" onClick={() => setSocialSubTab('comments')} leftSection={<IconShieldLock size={14} color="#6366f1"/>} style={{ padding: '8px 12px', fontSize: '13px', background: socialSubTab === 'comments' ? '#f0f4ff' : 'transparent', color: socialSubTab === 'comments' ? '#4f46e5' : '#475569' }}>
                    {t('tab_social_sub_comments', 'Komentarze & AI Moderacja')}
                  </Tabs.Tab>
                  <Tabs.Tab value="social-media" onClick={() => setSocialSubTab('history')} leftSection={<IconList size={14} color="#6366f1"/>} style={{ padding: '8px 12px', fontSize: '13px', background: socialSubTab === 'history' ? '#f0f4ff' : 'transparent', color: socialSubTab === 'history' ? '#4f46e5' : '#475569' }}>
                    {t('tab_social_sub_history', 'Historia Publikacji')}
                  </Tabs.Tab>
                </Stack>
              )}
{isProUser && (
<Tabs.Tab 
                value="crm-email" 
                leftSection={<IconMail size={16}/>}
                rightSection={dashboardStats.unreadEmails > 0 ? (
                  <Badge size="xs" color="red" variant="unstyled">
                    {dashboardStats.unreadEmails}
                  </Badge>
                ) : null}
              >
                {t('tab_email')}
              </Tabs.Tab>
)}
                            
              {(user?.subscriptionPlan?.startsWith('PRO') || user?.subscriptionPlan === 'ADMIN' || user?.email === 'office@ailinkb.shop') && (
                <Tabs.Tab 
                  value="crm-whatsapp" 
                  leftSection={<IconBrandWhatsapp size={16} color="#25D366" />}
                  rightSection={dashboardStats.unreadWhatsApp > 0 ? (
                    <Badge size="xs" color="green" variant="unstyled">
                      {dashboardStats.unreadWhatsApp}
                    </Badge>
                  ) : null}
                >
                  WhatsApp (PRO)
                </Tabs.Tab>
              )}
                {isProUser && (
                <>
                  <Tabs.Tab value="automations" leftSection={<IconBolt size={16}/>}>{t('tab_mail_campaigns')}</Tabs.Tab>
                  <Tabs.Tab value="ad-campaigns" leftSection={<IconSpeakerphone size={16}/>}>{t('tab_ad_campaigns')}</Tabs.Tab>
                  <Tabs.Tab value="blacklist" leftSection={<IconBan size={16}/>}>{t('tab_blacklist')}</Tabs.Tab>
                  <Tabs.Tab value="events" leftSection={<IconCalendarEvent size={16}/>}>{t('tab_events')}</Tabs.Tab>
                  <Tabs.Tab value="affiliate" leftSection={<IconCash size={16}/>}>{t('tab_affiliate')}</Tabs.Tab>
                </>
              )}

              <Text size="xs" fw={700} c="dimmed" mt="md" mb={4} ml="xs" tt="uppercase" w="100%">{t('label_system')}</Text>
              <Tabs.Tab value="settings" leftSection={<IconSettings size={16}/>}>{t('tab_settings')}</Tabs.Tab>
              {(user?.role?.includes('ADMIN') || user?.email === 'office@ailinkb.com') && (
                <>
                  <Tabs.Tab value="admin" leftSection={<IconLock size={16}/>}>{t('tab_admin_panel')}</Tabs.Tab>
                  {/* <Tabs.Tab value="agents" leftSection={<IconRobot size={16}/>}>Agent Manager</Tabs.Tab> */}
                  <Tabs.Tab value="seo-audit" leftSection={<IconChartBar size={16}/>}>{t('tab_seo_audit')}</Tabs.Tab>
                </>
              )}
              <Tabs.Tab value="system-logs" leftSection={<IconList size={16} color="orange"/>} color="orange">{t('tab_system_logs')}</Tabs.Tab>
              <Tabs.Tab value="bug_report" leftSection={<IconBug size={16} color="red"/>} color="red">{t('tab_report_bug')}</Tabs.Tab>
            </Tabs.List>
          </Tabs>

          <Divider my="md" />
          
          <Stack gap="xs" px="xs">
            <Badge color="indigo" variant="light" size="xs" fullWidth>Auto-Discovery Active</Badge>
            <Badge color="green" variant="light" size="xs" fullWidth>📊 Total Leads: {totalElements}</Badge>
            <Button 
              size="xs" 
              variant="light" 
              color="violet" 
              leftSection={<IconWorld size={14}/>} 
              onClick={() => { setInstallType('chat'); setInstallTemplateKey(''); setInstallModalOpened(true); }}
              fullWidth
            >
              {t('install_widget')}
            </Button>
          </Stack>
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        <ScrollArea h="100%" type="auto" offsetScrollbars style={{ flex: 1 }}>
        {isProTab(activeTab) && !isProUser ? (
          <Card withBorder shadow="md" p={40} radius="lg"  style={{ textAlign: 'center', maxWidth: 750, margin: '40px auto', borderTop: '4px solid #4f46e5' }}>
            <ThemeIcon size={64} radius="xl" color="indigo" variant="light" mx="auto" mb="md">
              <IconShieldLock size={36} />
            </ThemeIcon>
            <Title order={3} mb="sm">{t('pro_locked_title')}</Title>
            <Text c="dimmed" mb="xl" size="md">{t('pro_locked_desc')}</Text>
            
            <Paper withBorder p="lg" radius="md"  mb="xl" style={{ textAlign: 'left', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <Title order={5} mb="md" c="indigo.8" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                ✨ {t('plan_pro_title')} — <Text span c="indigo.6" fw={800}>{t('plan_pro_price')}</Text>
              </Title>
              <Stack gap="xs">
                <Text size="sm">✅ {t('plan_pro_f1')}</Text>
                <Text size="sm" fw={600} c="indigo.9">⚡ {t('plan_pro_f2')}</Text>
                <Text size="sm" fw={600} c="indigo.9">⚡ {t('plan_pro_f3')}</Text>
                <Text size="sm" fw={600} c="indigo.9">⚡ {t('plan_pro_f4')}</Text>
                <Text size="sm" fw={600} c="indigo.9">⚡ {t('plan_pro_f5')}</Text>
              </Stack>
            </Paper>

            <Group justify="center">
              <Button size="lg" color="indigo" variant="light" disabled leftSection={<IconRocket size={20}/>}>
                {t('btn_pro_coming_soon')}
              </Button>
            </Group>
          </Card>
        ) : (
          <>
            {activeTab === 'website' && (
          <WebsiteBuilderTab 
            authPageBg={authPageBg} handleOnboard={handleOnboard} t={t} handleOpenLivePreview={handleOpenLivePreview} i18n={i18n} 
            authLogo={authLogo} setAuthLogo={setAuthLogo} authLogoModalOpen={authLogoModalOpen} setAuthLogoModalOpen={setAuthLogoModalOpen} 
            authLogoPrompt={authLogoPrompt} setAuthLogoPrompt={setAuthLogoPrompt} isAuthGeneratingLogo={isAuthGeneratingLogo} 
            generateAuthLogoImage={generateAuthLogoImage} authEmail={user?.email || authEmail} authImageModalOpen={authImageModalOpen} 
            setAuthImageModalOpen={setAuthImageModalOpen} authHeaderImagePrompt={authHeaderImagePrompt} setAuthHeaderImagePrompt={setAuthHeaderImagePrompt} 
            isAuthGeneratingImage={isAuthGeneratingImage} generateAuthHeaderImage={generateAuthHeaderImage} authHeaderColor={authHeaderColor} 
            authHeaderImage={authHeaderImage} authCompanyName={authCompanyName} setAuthCompanyName={setAuthCompanyName} 
            authLocation={authLocation} setAuthLocation={setAuthLocation} authServices={authServices} setAuthServices={setAuthServices} 
            companyDesc={companyDesc} setCompanyDesc={setCompanyDesc} 
            authMapLat={authMapLat} setAuthMapLat={setAuthMapLat} authMapLng={authMapLng} setAuthMapLng={setAuthMapLng} authCalendarEnabled={authCalendarEnabled} setAuthCalendarEnabled={setAuthCalendarEnabled} authAffiliateEnabled={authAffiliateEnabled} setAuthAffiliateEnabled={setAuthAffiliateEnabled} authAffiliateCode={authAffiliateCode} setAuthAffiliateCode={setAuthAffiliateCode} termsModalOpened={termsModalOpened} setTermsModalOpened={setTermsModalOpened} 
            termsAccepted={termsAccepted} setTermsAccepted={setTermsAccepted} authError={authError} user={user} setIsRegistering={setIsRegistering} 
            setAuthError={setAuthError} setAuthPin={setAuthPin}
            authCustomHtml={authCustomHtml} setAuthCustomHtml={setAuthCustomHtml}
          />
        )}
        {activeTab === 'dashboard' && (
              <Box p="md">
                {/* <AnalyticsDashboard user={user} apiBaseUrl={API_BASE_URL} /> */}

                <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="lg" mb="xl">
                  <Card shadow="sm" padding="lg" radius="md" withBorder style={{  }}>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm" fw={700} c="dimmed" tt="uppercase">{t('unread_emails')}</Text>
                      <ThemeIcon color="red" variant="unstyled" size="lg"><IconMail size={20}/></ThemeIcon>
                    </Group>
                    <Text size="36px" fw={900} c="red.7">{dashboardStats.unreadEmails || 0}</Text>
                <Button variant="subtle" size="xs" mt="xs" onClick={() => setActiveTab('crm-email')} fullWidth>{t('go_to_inbox')}</Button>
              </Card>

              <Card shadow="sm" padding="lg" radius="md" withBorder style={{  }}>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={700} c="dimmed" tt="uppercase">{t('unread_chats')}</Text>
                  <ThemeIcon color="orange" variant="light" size="lg"><IconMessage size={20}/></ThemeIcon>
                </Group>
                <Text size="36px" fw={900} c="orange.7">{dashboardStats.unreadChats || 0}</Text>
                <Button variant="subtle" size="xs" mt="xs" onClick={() => setActiveTab('ai-chat')} fullWidth>{t('go_to_chats')}</Button>
              </Card>

              <Card shadow="sm" padding="lg" radius="md" withBorder style={{  }}>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={700} c="dimmed" tt="uppercase">{t('remaining_appointments')}</Text>
                  <ThemeIcon color="orange" variant="light" size="lg"><IconCalendar size={20}/></ThemeIcon>
                </Group>
                <Text size="36px" fw={900}>{dashboardStats.todayMeetings || 0}</Text>
                <Button variant="subtle" size="xs" mt="xs" onClick={() => setActiveTab('calendar')} fullWidth>{t('go_to_calendar')}</Button>
              </Card>

              <Card shadow="sm" padding="lg" radius="md" withBorder style={{  }}>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={700} c="dimmed" tt="uppercase">{t('total_customers')}</Text>
                  <ThemeIcon color="blue" variant="light" size="lg"><IconDatabase size={20}/></ThemeIcon>
                </Group>
                <Text size="36px" fw={900}>{dashboardStats.totalLeads || 0}</Text>
                <Button variant="subtle" size="xs" mt="xs" onClick={() => setActiveTab('leads')} fullWidth>{t('view_database')}</Button>
              </Card>

              <Card shadow="sm" padding="lg" radius="md" withBorder style={{  }}>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={700} c="dimmed" tt="uppercase">{t('ai_requests_stat_title')}</Text>
                  <ThemeIcon color="violet" variant="light" size="lg"><IconRobot size={20}/></ThemeIcon>
                </Group>
                <Group align="baseline" gap="xs">
                  <Text size="36px" fw={900} c="violet.7">{dashboardStats.aiRequestsTotal || 0}</Text>
                  <Text size="sm" c="dimmed" fw={600}>({t('ai_requests_today')} {dashboardStats.aiRequestsToday || 0})</Text>
                </Group>
                <Button variant="subtle" color="violet" size="xs" mt="xs" onClick={() => setActiveTab('ai-chat')} fullWidth>{t('ai_requests_btn')}</Button>
              </Card>
            </SimpleGrid>

            <Paper p="lg" radius="md" withBorder mb="xl" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', borderLeft: '5px solid #228be6' }}>
              <Group justify="space-between" align="center" wrap="wrap">
                <Box>
                  <Group gap="xs" mb={4}>
                    <ThemeIcon size="md" color="blue" variant="unstyled"><IconWorld size={18}/></ThemeIcon>
                    <Text fw={800} size="lg" c="gray.9">{t('company_public_profile_title')}</Text>
                  </Group>
                  <Text size="sm" c="dimmed">{t('company_public_profile_desc')}</Text>
                </Box>
                <Group gap="sm">
                  <Button 
                    variant="unstyled" 
                    color="blue" 
                    leftSection={<IconWorld size={16}/>} 
                    onClick={() => window.open('/?profile=' + window.btoa(user.email), '_blank')}
                    style={{ boxShadow: '0 2px 6px rgba(34, 139, 230, 0.2)' }}
                  >
                    {t('btn_view_public_profile')}
                  </Button>

                  <Button 
                    variant="outline" 
                    color="gray" 
                    leftSection={<IconCopy size={16}/>}
                    onClick={() => {
                      navigator.clipboard.writeText(`https://www.ailinkb.com/?profile=${window.btoa(user.email)}`);
                      alert(t('link_copied'));
                    }}
                  >
                    {t('btn_copy_link')}
                  </Button>
                </Group>
              </Group>
            </Paper>

            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Title order={4} mb="md">{t('dashboard_quick_actions')}</Title>
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="md">
                <Button variant="light" color="blue" size="md" leftSection={<IconWorld size={20}/>} onClick={() => window.open('/?profile=' + window.btoa(user.email), '_blank')}>
                  {t('tab_public_profile')}
                </Button>
                <Button variant="light" color="indigo" size="md" leftSection={<IconServer size={20}/>} onClick={() => setActiveTab('mcp-server')}>
                  {t('tab_mcp')}
                </Button>
                <Button variant="light" color="violet" size="md" leftSection={<IconSpeakerphone size={20}/>} onClick={() => setActiveTab('ad-campaigns')}>
                  {t('tab_ad_campaigns')}
                </Button>
                <Button variant="light" color="grape" size="md" leftSection={<IconShieldLock size={20}/>} onClick={() => setActiveTab('social-media')}>
                  {t('tab_social_studio')}
                </Button>
                <Button variant="light" color="green" size="md" leftSection={<IconDatabase size={20}/>} onClick={() => setActiveTab('leads')}>
                  {t('tab_visitor_chats')}
                </Button>
              </SimpleGrid>
            </Card>
          </Box>
        )}
        {activeTab === 'leads' && (
          <Flex direction="column" h="100%" gap="md">
            <Tabs defaultValue="crm-database" radius="md" variant="pills" color="indigo" keepMounted={false}>
              <Tabs.List mb="md">
                <Tabs.Tab value="crm-database" leftSection={<IconDatabase size={16}/>}>
                  <Text fw={600} size="sm">{t('tab_crm_database')}</Text>
                </Tabs.Tab>
                <Tabs.Tab value="public-discovery" leftSection={<IconSearch size={16} color="#10b981"/>}>
                  <Text fw={600} size="sm">{t('tab_public_discovery')}</Text>
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="crm-database">
                <Flex direction="column" gap="md">
                  {/* Real-time Enrichment Stats Bar - Responsive columns */}
                  <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
                    <Card withBorder padding="sm" radius="md" bg="#0a0a0a">
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase">{t('leads_stat_total')}</Text>
                        <IconChartBar size={16} color="blue" />
                      </Group>
                      <Text size="xl" fw={700}>{stats.total}</Text>
                    </Card>
                    <Card withBorder padding="sm" radius="md" bg="#0a0a0a">
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase">{t('leads_stat_scanned')}</Text>
                        <IconRefresh size={16} color="indigo" />
                      </Group>
                      <Text size="xl" fw={700}>{stats.scanned}</Text>
                      <Text size="xs" c="dimmed">{(stats.scanned / (stats.total || 1) * 100).toFixed(1)}% complete</Text>
                    </Card>
                    <Card withBorder padding="sm" radius="md" bg="#0a0a0a">
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase">{t('leads_stat_enriched')}</Text>
                        <ThemeIcon variant="light" color="violet" size="sm">🤖</ThemeIcon>
                      </Group>
                      <Text size="xl" fw={700}>{stats.aiEnriched}</Text>
                      <Text size="xs" c="dimmed">Last resort successes</Text>
                    </Card>
                    <Card withBorder padding="sm" radius="md" bg="#0a0a0a">
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase">{t('leads_stat_email')}</Text>
                        <IconMail size={16} color="green" />
                      </Group>
                      <Text size="xl" fw={700} c="green.7">{stats.withEmail}</Text>
                      <Text size="xs" c="dimmed">Contactable leads</Text>
                    </Card>
                  </SimpleGrid>

                  <Card shadow="sm" padding="md" radius="md" withBorder bg="#0a0a0a">
                    <Group wrap="wrap" gap="sm">
                      <TextInput spellCheck={false} 
                        placeholder={t('leads_search_placeholder')} 
                        value={query} onChange={(e) => {
                          const val = e.target.value;
                          setQuery(val);
                          if (val === '') {
                            setPage(1);
                            fetchLeads(1, '');
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setPage(1);
                            fetchLeads(1, query);
                          }
                        }}
                        style={{ flex: 1, minWidth: '200px' }}
                        leftSection={<IconSearch size={16} />}
                      />
                      <Select 
                  placeholder="Target Group"
                  searchable
                  clearable
                  data={Array.from(new Set(['ALL', 'GENERAL', 'TEST', 'VIP', 'PARTNER', 'real_estate_customers', 'IT_EMPLOYEES', 'poland_lead', 'SEO_MEDIUM_OR_POOR', 'BRAK_STRONY', ...(Array.isArray(leads) ? leads : []).map(l => l.category).filter(Boolean)]))
                    .map(c => ({ value: c, label: c === 'BRAK_STRONY' ? t('cat_no_website') : (c === 'SEO_MEDIUM_OR_POOR' ? t('cat_seo_poor') : c) }))}
                  value={leadsCategoryFilter}
                  onChange={(val) => { setLeadsCategoryFilter(val || 'ALL'); setPage(1); }}
                  maxDropdownHeight={250}
                  w={{ base: '100%', sm: 150 }}
                />
                <Button 
                  color="indigo" 
                  leftSection={<IconRocket size={16}/>} 
                  onClick={() => { setPage(1); fetchLeads(1, query); }}
                  fullWidth={false}
                >
                  Discover
                </Button>
                {(user?.role?.includes('ADMIN') || user?.email === 'office@ailinkb.com') && (
                  <Button
                    color="green"
                    variant="light"
                    leftSection={<IconDatabaseImport size={16}/>}
                    onClick={() => setCeidgModalOpen(true)}
                    fullWidth={false}
                  >
                    Import CEIDG (PL)
                  </Button>
                )}
              </Group>
            </Card>

            <Grid style={{ flex: 1, minHeight: 0 }}>
              {/* Responsive Map Column */}
              <Grid.Col span={{ base: 12, lg: 4 }}>
                <Card shadow="sm" radius="md" withBorder bg="#0a0a0a" p={0} style={{ height: '60vh', overflow: 'hidden' }}>
                  <Map leads={leads} onSelectLead={setSelectedLead} />
                </Card>
              </Grid.Col>
              
              {/* Responsive List Column */}
              <Grid.Col span={{ base: 12, lg: 8 }}>
                <Card shadow="sm" radius="md" withBorder bg="#0a0a0a" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Group justify="space-between" mb="md" px="md" pt="md">
                    <Group>
                      <Title order={4}>Customer Base</Title>
                      <Button 
                        size="xs" 
                        variant="light" 
                        color="indigo" 
                        leftSection={<IconPlus size={14}/>}
                        onClick={() => {
                          setEditingLead(null);
                          setLeadForm({ companyName: '', email: '', phone: '', website: '', address: '', status: 'NEW', category: 'GENERAL', taxId: '' });
                          setLeadModalOpened(true);
                        }}
                      >
                        Add Customer
                      </Button>
                    </Group>
                    <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
                  </Group>

                  <ScrollArea style={{ flex: 1 }}>
<Accordion 
                      variant="separated" 
                      value={leadsAccordionValue} 
                      onChange={setLeadsAccordionValue}
                    >
                      {Array.isArray(leads) && leads.map((lead) => (
                        <Accordion.Item key={lead.id} value={lead.id.toString()} style={{ background: selectedLead?.id === lead.id ? '#1a1b1e' : '#0a0a0a', borderBottom: '1px solid #1f2022' }}>
                          <Accordion.Control>
                            <Group justify="space-between" pr="md">
                              <Box style={{ flex: 1 }}>
                                <Group gap="xs">
                                  <Text fw={700} size="sm">{lead.companyName || '(No Name / Pending)'}</Text>
                                  {lead.optOut && <Badge size="xs" color="red" variant="unstyled">Opt-out</Badge>}
                                  {lead.seoScore != null && (
                                    <Badge size="xs" color={lead.seoScore >= 80 ? 'green' : (lead.seoScore >= 50 ? 'yellow' : 'red')} variant="unstyled">
                                      SEO: {lead.seoScore}/100
                                    </Badge>
                                  )}
                                  {lead.webAuditCompleted && (
                                    <Badge size="xs" color={lead.webAuditReport?.includes('KRYTYCZNY BRAK') ? 'red' : (lead.webAuditReport?.includes('POTrzebne OPTYMALIZACJE') ? 'yellow' : 'green')} variant="light">
                                      {lead.webAuditReport?.includes('KRYTYCZNY BRAK') ? 'Braki Social Media!' : 'Audyt Web'}
                                    </Badge>
                                  )}
                                </Group>
                                <Text size="xs" c="dimmed" truncate>{lead.email || lead.address || '(No Contact Info)'}</Text>
                              </Box>
                              <Badge size="xs" variant="light" color={lead.status === 'CLOSED' ? 'green' : (lead.status === 'NEW' ? 'blue' : 'orange')}>
                                {lead.status || 'NEW'}
                              </Badge>
                            </Group>
                          </Accordion.Control>
                          <Accordion.Panel>
                            <Stack p="xs" gap="md">
                              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                                <Box>
                                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">Email</Text>
                                  <Text size="sm">{lead.email || 'N/A'} {lead.websiteScraped && <ThemeIcon size="xs" variant="transparent" title="Enriched by AI">🤖</ThemeIcon>} {lead.emailStatus === 'VALID' && <span title="Zweryfikowany e-mail">✅</span>} {lead.emailStatus === 'INVALID' && <span title="Błędna domena e-mail">❌</span>}</Text>
                                </Box>
                                <Box>
                                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">Website</Text>
                                  {lead.website ? <Anchor size="sm" href={lead.website} target="_blank">🌐 Visit Website</Anchor> : <Text size="sm">N/A</Text>}
                                </Box>
                                <Box>
                                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">Tax ID (NIP)</Text>
                                  <Text size="sm">{lead.taxId || 'N/A'}</Text>
                                </Box>
                                <Box>
                                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">Status</Text>
                                  <Select 
                                    data={['NEW', 'CONTACTED', 'QUALIFIED', 'CLOSED']} 
                                    value={lead.status || 'NEW'} 
                                    onChange={(val) => fetch(`${API_BASE_URL}/api/leads/${lead.id}`, {method: 'PUT', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({ status: val })}).then(fetchLeads)}
                                    size="xs"
                                  />
                                </Box>
                              </SimpleGrid>

                              <Box>
                                <Text size="xs" fw={700} c="dimmed" tt="uppercase">Address</Text>
                                <Text size="sm">{lead.address || 'N/A'}</Text>
                              </Box>

                              {lead.webAuditCompleted && lead.webAuditReport && (
                                <Box mt="sm" p="sm" style={{ backgroundColor: lead.webAuditReport.includes('KRYTYCZNY BRAK') ? 'rgba(255, 0, 0, 0.1)' : '#141517', borderRadius: '8px', border: lead.webAuditReport.includes('KRYTYCZNY BRAK') ? '1px solid rgba(255, 0, 0, 0.3)' : '1px solid #2f3032' }}>
                                  <Text size="xs" fw={700} c={lead.webAuditReport.includes('KRYTYCZNY BRAK') ? 'red' : 'dimmed'} tt="uppercase" mb="xs">
                                    Raport z audytu & Braki (Social Media / SEO)
                                  </Text>
                                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{lead.webAuditReport}</Text>
                                </Box>
                              )}

                              <LeadSeoRowStats lead={lead} user={user} apiBaseUrl={API_BASE_URL} isExpanded={leadsAccordionValue === lead.id.toString()} />

                              <Divider />

                              <Group grow>
                                <Button size="xs" variant="light" color="indigo" leftSection={<IconEdit size={14}/>} onClick={() => {
                                  setEditingLead(lead);
                                  setLeadForm({ 
                                    companyName: lead.companyName || '', 
                                    contactPerson: lead.contactPerson || '',
                                    email: lead.email || '', 
                                    phone: lead.phone || '', 
                                    website: lead.website || '', 
                                    facebook: lead.facebook || '',
                                    linkedin: lead.linkedin || '',
                                    instagram: lead.instagram || '',
                                    address: lead.address || '',
                                    status: lead.status || 'NEW',
                                    detectedNeeds: lead.detectedNeeds || '',
                                    matchedServices: lead.matchedServices || '',
                                    projectDetails: lead.projectDetails || '',
                                    taxId: lead.taxId || '',
                                    category: lead.category || 'GENERAL'
                                  });
                                  setLeadModalOpened(true);
                                }}>Edit</Button>
                                
                                <Button size="xs" variant="light" color="cyan" loading={auditingLeadId === lead.id} leftSection={<IconStethoscope size={14}/>} onClick={() => {
                                  setAuditingLeadId(lead.id);
                                  fetch(`${API_BASE_URL}/api/web-audit/trigger`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ ownerEmail: user.email, leadId: lead.id })
                                  }).then(res => res.json()).then((data) => {
                                    setAuditingLeadId(null);
                                    if (data.success || data.report) {
                                      fetchLeads();
                                      if (data.report && !data.success) alert(data.report);
                                    } else {
                                      alert(data.error || 'Wystąpił błąd podczas audytu.');
                                    }
                                  }).catch(e => {
                                    setAuditingLeadId(null);
                                    alert('Wystąpił błąd sieci: ' + e.message);
                                  });
                                }}>Audyt (Social Media)</Button>

                                {lead.email && <Button size="xs" variant="unstyled" color="indigo" leftSection={<IconMail size={14}/>} onClick={() => selectLeadForEmail(lead)}>Write</Button>}
                                {lead.email && <Button size="xs" variant="unstyled" color="orange" leftSection={<IconMessage size={14}/>} onClick={() => { setInitiateChatTargetLead(lead); setInitiateChatModalOpened(true); setInitiateChatMessage(''); }}>Czat</Button>}

                                {lead.phone && <Button size="xs" variant="unstyled" color="green" leftSection={<IconMessages size={14}/>} onClick={() => { setWaComposeData({ to: lead.phone, text: '' }); setIsWaComposing(true); }}>WhatsApp</Button>}
                                
                                <Button size="xs" variant="light" color="gray" leftSection={<IconHistory size={14}/>} onClick={() => { 
                                  setHistoryLead(lead); 
                                  fetch(`${API_BASE_URL}/api/leads/${lead.id}/history`).then(r => r.json()).then(data => { setEmailHistory(data || []); setHistoryOpened(true); }); 
                                }}>Log</Button>

                                <ActionIcon variant="unstyled" color="red" size="lg" onClick={() => {
                                  if(window.confirm(`Delete customer ${lead.companyName}?`)) {
                                    fetch(`${API_BASE_URL}/api/leads/${lead.id}`, {method: 'DELETE'}).then(() => { fetchLeads(); fetchStats(); });
                                  }
                                }}>
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Group>
                            </Stack>
                          </Accordion.Panel>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </ScrollArea>
                </Card>
              </Grid.Col>
            </Grid>
            </Flex>
            </Tabs.Panel>

            <Tabs.Panel value="public-discovery">
              <Card shadow="sm" p={0} radius="md" withBorder style={{ height: '78vh', display: 'flex', flexDirection: 'column' }}>
                <Box style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <PublicMap externalQuery={globalSearchQuery} />
                </Box>
              </Card>
            </Tabs.Panel>
            </Tabs>

            {/* CUSTOMER MODAL (ADD/EDIT) */}
            <Modal
              opened={initiateChatModalOpened}
              onClose={() => setInitiateChatModalOpened(false)}
              title={t('chat_initiate_title')}
              size="md"
            >
              <Stack gap="md">
                <Text size="sm">{t('chat_initiate_desc')} <b>{initiateChatTargetLead?.email}</b></Text>
                <Textarea 
                  placeholder={t('chat_initiate_placeholder')}
                  value={initiateChatMessage}
                  onChange={(e) => setInitiateChatMessage(e.target.value)}
                  minRows={4}
                  required
                />
                <Button 
                  color="orange" 
                  fullWidth 
                  loading={isInitiatingChat} 
                  onClick={handleInitiateChat}
                >
                  {t('chat_send_button')}
                </Button>
              </Stack>
            </Modal>

            <Modal 
              opened={leadModalOpened} 
              onClose={() => setLeadModalOpened(false)} 
              title={editingLead ? `Editing: ${editingLead.companyName}` : "Add New Customer"} 
              size="xl"
              radius="md"
            >
              <Tabs defaultValue="basic">
                <Tabs.List mb="md">
                  <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
                  <Tabs.Tab value="social">Social Media</Tabs.Tab>
                  <Tabs.Tab value="ai">AI Analysis & Notes</Tabs.Tab> <Tabs.Tab value="seo">SEO Stats</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="basic">
                  <Stack gap="md">
                    <SimpleGrid cols={3}>
                      <TextInput spellCheck={false} 
 label="Company Name" required value={leadForm.companyName} onChange={e => setLeadForm({...leadForm, companyName: e.target.value})} />
                      <TextInput spellCheck={false} 
 label="NIP (Tax ID)" placeholder="PL1234567890" value={leadForm.taxId} onChange={e => setLeadForm({...leadForm, taxId: e.target.value})} />
                      <TextInput spellCheck={false} 
 label="Primary Contact Person" placeholder="John Doe" value={leadForm.contactPerson} onChange={e => setLeadForm({...leadForm, contactPerson: e.target.value})} />
                    </SimpleGrid>

                    <SimpleGrid cols={3}>
                      <TextInput spellCheck={false} 
 label="Email Address" value={leadForm.email} onChange={e => setLeadForm({...leadForm, email: e.target.value})} />
                      <TextInput spellCheck={false} 
 label="Phone Number" value={leadForm.phone} onChange={e => setLeadForm({...leadForm, phone: e.target.value})} />
                      <Select label="Status" data={['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL', 'CLOSED']} value={leadForm.status} onChange={val => setLeadForm({...leadForm, status: val})} />
                      <Select label="Language" data={['pl', 'en', 'de', 'fr', 'es']} value={leadForm.language || 'pl'} onChange={val => setLeadForm({...leadForm, language: val})} />
                    </SimpleGrid>

                    <Autocomplete 
                      label="Customer Segment (Target Audience Group)" 
                      description="Choose a segment or type a new one for targeted mail campaigns"
                      data={Array.from(new Set(['GENERAL', 'TEST', 'VIP', 'PARTNER', 'real_estate_customers', 'IT_EMPLOYEES', 'BRAK_STRONY', ...(Array.isArray(leads) ? leads : []).map(l => l.category).filter(Boolean)]))
                        .map(c => ({ value: c, label: c === 'BRAK_STRONY' ? t('cat_no_website') : (c === 'SEO_MEDIUM_OR_POOR' ? t('cat_seo_poor') : c) }))}
                      value={leadForm.category} 
                      onChange={val => setLeadForm({...leadForm, category: val})} 
                      maxDropdownHeight={250}
                    />

                    <TextInput spellCheck={false} 
 label="Website URL" value={leadForm.website} onChange={e => setLeadForm({...leadForm, website: e.target.value})} />
                    <Textarea label="Business Address" minRows={2} value={leadForm.address} onChange={e => setLeadForm({...leadForm, address: e.target.value})} />
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="social">
                  <Stack gap="md">
                    <TextInput spellCheck={false} 
 label="Facebook URL" value={leadForm.facebook} onChange={e => setLeadForm({...leadForm, facebook: e.target.value})} />
                    <TextInput spellCheck={false} 
 label="LinkedIn URL" value={leadForm.linkedin} onChange={e => setLeadForm({...leadForm, linkedin: e.target.value})} />
                    <TextInput spellCheck={false} 
 label="Instagram URL" value={leadForm.instagram} onChange={e => setLeadForm({...leadForm, instagram: e.target.value})} />
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="ai">
                  <Stack gap="md">
                    <Textarea label="Detected Needs (AI)" minRows={3} value={leadForm.detectedNeeds} onChange={e => setLeadForm({...leadForm, detectedNeeds: e.target.value})} />
                    <Textarea label="Matched Services" minRows={3} value={leadForm.matchedServices} onChange={e => setLeadForm({...leadForm, matchedServices: e.target.value})} />
                    <Textarea label="Project Details" minRows={4} value={leadForm.projectDetails} onChange={e => setLeadForm({...leadForm, projectDetails: e.target.value})} />
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="seo">
                  <Stack gap="md" mt="md">
                    {leadSeoStatsLoading ? (
                      <Text c="dimmed">Loading SEO Stats...</Text>
                    ) : leadSeoStats && leadSeoStats.hasSeo ? (
                      <SimpleGrid cols={2}>
                        <Card withBorder padding="sm" radius="md" bg="#0a0a0a">
                          <Group justify="space-between">
                            <Text size="xs" c="dimmed" fw={700} tt="uppercase">SEO Score</Text>
                          </Group>
                          <Text size="xl" fw={700} c={leadSeoStats.score >= 90 ? 'green' : leadSeoStats.score >= 50 ? 'yellow' : 'red'}>{leadSeoStats.score} / 100</Text>
                        </Card>
                        <Card withBorder padding="sm" radius="md" bg="#0a0a0a">
                          <Group justify="space-between">
                            <Text size="xs" c="dimmed" fw={700} tt="uppercase">Audited At</Text>
                          </Group>
                          <Text size="sm" fw={500}>{leadSeoStats.auditedAt.substring(0, 16).replace('T', ' ')}</Text>
                        </Card>
                        <Card withBorder padding="sm" radius="md" bg="#0a0a0a">
                          <Group justify="space-between">
                            <Text size="xs" c="dimmed" fw={700} tt="uppercase">Google Status</Text>
                          </Group>
                          <Text size="sm" fw={500}>{leadSeoStats.googleStatus}</Text>
                        </Card>
                        <Card withBorder padding="sm" radius="md" bg="#0a0a0a">
                          <Group justify="space-between">
                            <Text size="xs" c="dimmed" fw={700} tt="uppercase">Mobile Status</Text>
                          </Group>
                          <Text size="sm" fw={500}>{leadSeoStats.mobileStatus}</Text>
                        </Card>
                      </SimpleGrid>
                    ) : (
                      <Text c="dimmed">No SEO Audit performed yet for this customer's website.</Text>
                    )}
                  </Stack>
                </Tabs.Panel>
              </Tabs>

              <Group justify="flex-end" mt="xl">
                <Button variant="subtle" color="gray" onClick={() => setLeadModalOpened(false)}>Cancel</Button>
                <Button color="indigo" onClick={handleSaveLead} leftSection={<IconRefresh size={16}/>} disabled={isWaSending}>
                  {editingLead ? "Save Changes" : "Create Customer"}
                </Button>
              </Group>
            </Modal>
          </Flex>
        )}

        {activeTab === 'calendar' && (() => {
          // Generate strip of dates: 7 days ago to 21 days ahead
          const dates = [];
          const start = new Date();
          start.setDate(start.getDate() - 7);
          for (let i = 0; i < 30; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            dates.push(d);
          }

          const combinedEvents = [
            ...meetings.map(m => ({ ...m, type: 'meeting' })),
            ...reminders.map(r => {
              const rStart = new Date(r.reminderDate);
              const rEnd = new Date(rStart.getTime() + 60 * 60 * 1000); // Assume 1 hr for display
              return {
                id: `rem-${r.id}`,
                type: 'reminder',
                title: r.title,
                description: r.description,
                startTime: rStart.toISOString(),
                endTime: rEnd.toISOString(),
                status: r.isCompleted ? 'CONFIRMED' : 'PENDING'
              };
            })
          ];

          const hasMeetingsOnDate = (date) => {
            return combinedEvents.some(m => {
              const mDate = new Date(m.startTime);
              return mDate.toDateString() === date.toDateString();
            });
          };

          const filteredMeetings = Array.from(combinedEvents)
            .filter(m => {
              const mDate = new Date(m.startTime);
              return mDate.toDateString() === selectedCalendarDate.toDateString();
            })
            .sort((a,b) => new Date(a.startTime) - new Date(b.startTime));

          // Timeline settings
          const startHour = 7;
          const endHour = 22;
          const hourHeight = 60; // pixels
          const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);

          return (
            <Stack gap="md" h="100%">
              <Group justify="space-between" px="md" pt="md">
                <Title order={4}>{t('cal_daily_appointments')}</Title>
                <Button color="indigo" size="xs" leftSection={<IconPlus size={14}/>} onClick={() => setMeetingModalOpened(true)}>{t('cal_new_appointment')}</Button>
              </Group>

              {/* HORIZONTAL DATE STRIP */}
              <Box px="md">
                <ScrollArea type="never" viewportRef={calendarViewportRef}>
                  <Group wrap="nowrap" gap="xs" pb="sm">
                    {dates.map((date, idx) => {
                      const isSelected = selectedCalendarDate && selectedCalendarDate.toDateString() === date.toDateString();
                      const hasMeetings = hasMeetingsOnDate(date);
                      const isToday = date.toDateString() === new Date().toDateString();

                      return (
                        <Button
                          key={idx}
                          ref={isToday ? todayButtonRef : null}
                          variant={isSelected ? 'filled' : (isToday ? 'light' : 'subtle')}
                          color={isSelected ? 'indigo' : (isToday ? 'blue' : 'gray')}
                          onClick={() => setSelectedCalendarDate(date)}
                          style={{ 
                            minWidth: '60px', 
                            height: '60px', 
                            border: hasMeetings && !isSelected ? '1px solid #4f46e5' : 'none',
                            padding: '4px'
                          }}
                        >
                          <Stack gap={0} align="center">
                            <Text size="10px" tt="uppercase" fw={700}>{date.toLocaleString('default', { weekday: 'short' })}</Text>
                            <Text size="lg" fw={800} lh={1}>{date.getDate()}</Text>
                            {hasMeetings && !isSelected && <Box style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#4f46e5', marginTop: '2px' }} />}
                          </Stack>
                        </Button>
                      );
                    })}
                  </Group>
                </ScrollArea>
              </Box>

              <ScrollArea h="calc(100vh - 280px)" type="hover">
                <Box pl={80} pr="md" pb="xl">
                   <Box style={{ position: 'relative', borderLeft: '2px solid #818cf8' }}>
                      {/* HOUR LINES */}
                      {hours.map(h => (
                        <Box key={h} style={{ height: hourHeight, borderBottom: '1px dashed #cbd5e1', position: 'relative' }}>
                           <Group 
                             gap="xs" 
                             wrap="nowrap" 
                             style={{ position: 'absolute', top: -10, left: -65, width: '60px', justifyContent: 'flex-end', alignItems: 'center' }}
                           >
                             <Text size="sm" fw={800} c="indigo.6">
                                {h < 10 ? `0${h}:00` : `${h}:00`}
                             </Text>
                             <Box style={{ width: '8px', height: '2px', background: '#818cf8' }} />
                           </Group>
                        </Box>
                      ))}

                      {/* MEETING BLOCKS */}
                      {filteredMeetings.map(m => {
                        const mStart = new Date(m.startTime);
                        const mEnd = new Date(m.endTime);
                        const startMins = (mStart.getHours() - startHour) * 60 + mStart.getMinutes();
                        const durationMins = (mEnd.getTime() - mStart.getTime()) / (1000 * 60);
                        
                        const top = (startMins / 60) * hourHeight;
                        const height = (durationMins / 60) * hourHeight;

                        return (
                          <Card 
                            key={m.id}
                            shadow="sm"
                            p="xs"
                            onClick={() => {
                             if (m.type === 'meeting') setSelectedMeetingDetails(m);
                               else alert(`${t('cal_reminder').toUpperCase()}: ${m.title}\n\n${m.description}`);
                            }}
                            style={{ 
                              position: 'absolute',
                              top: top,
                              height: Math.max(height, 30),
                              left: '10px',
                              right: '10px',
                              backgroundColor: m.type === 'reminder' ? '#dcfce7' : (m.status === 'CONFIRMED' ? '#eef2ff' : '#fffbeb'),
                              borderLeft: `4px solid ${m.type === 'reminder' ? '#22c55e' : (m.status === 'CONFIRMED' ? '#4f46e5' : '#f59e0b')}`,
                              cursor: 'pointer',
                              zIndex: 10,
                              overflow: 'hidden'
                            }}
                          >
                             <Text fw={700} size="xs" truncate>{m.title} {m.type === 'reminder' && `(${t('cal_reminder')})`}</Text>
                             <Text size="10px" c="dimmed">{mStart.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})} - {mEnd.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</Text>
                          </Card>
                        );
                      })}

                      {/* CURRENT TIME LINE (if today) */}
                      {selectedCalendarDate.toDateString() === new Date().toDateString() && (() => {
                        const now = new Date();
                        if (now.getHours() >= startHour && now.getHours() <= endHour) {
                           const nowMins = (now.getHours() - startHour) * 60 + now.getMinutes();
                           const nowTop = (nowMins / 60) * hourHeight;
                           return <Box style={{ position: 'absolute', top: nowTop, left: 0, right: 0, height: '2px', background: 'red', zIndex: 20, pointerEvents: 'none' }}>
                                    <Box style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'red', position: 'absolute', top: -3, left: -4 }} />
                                  </Box>;
                        }
                        return null;
                      })()}
                   </Box>
                </Box>
              </ScrollArea>

              {/* MEETING DETAILS MODAL */}
              <Modal opened={!!selectedMeetingDetails} onClose={() => setSelectedMeetingDetails(null)} title={t('cal_meeting_details')} centered>
                {selectedMeetingDetails && (
                  <Stack>
                    <Group justify="space-between">
                      <Title order={4}>{selectedMeetingDetails.title}</Title>
                      <Badge color={selectedMeetingDetails.status === 'CONFIRMED' ? 'green' : 'orange'}>{selectedMeetingDetails.status}</Badge>
                    </Group>
                    
                    <Box>
                      <Text size="xs" fw={700} c="dimmed" tt="uppercase">{t('cal_time')}</Text>
                      <Text size="sm">{new Date(selectedMeetingDetails.startTime).toLocaleString()} - {new Date(selectedMeetingDetails.endTime).toLocaleTimeString()}</Text>
                    </Box>

                    <Box>
                      <Text size="xs" fw={700} c="dimmed" tt="uppercase">{t('cal_agenda')}</Text>
                      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{selectedMeetingDetails.description || t('cal_no_notes')}</Text>
                    </Box>

                    <Divider />

                    <Group grow>
                      {selectedMeetingDetails.status === 'PENDING' && (
                        <Button color="green" leftSection={<IconCheck size={16}/>} onClick={() => {
                          fetch(`${API_BASE_URL}/api/meetings/${selectedMeetingDetails.id}/confirm`, {method:'POST'}).then(() => { fetchMeetings(); setSelectedMeetingDetails(null); });
                        }}>{t('cal_confirm')}</Button>
                      )}
                      <Button variant="outline" color="red" leftSection={<IconTrash size={16}/>} onClick={() => {
                        if(window.confirm(t('cal_delete_confirm'))) {
                           fetch(`${API_BASE_URL}/api/meetings/${selectedMeetingDetails.id}`, {method:'DELETE'}).then(() => { fetchMeetings(); setSelectedMeetingDetails(null); });
                        }
                      }}>{t('cal_delete')}</Button>
                    </Group>
                  </Stack>
                )}
              </Modal>

              <Modal opened={meetingModalOpened} onClose={() => setMeetingModalOpened(false)} title={t('cal_schedule_new')} size="lg">
                  <Stack gap="md">
                    <TextInput spellCheck={false} label={t('cal_meeting_title')} required value={newMeeting.title} onChange={e => setNewMeeting({...newMeeting, title: e.target.value})} />
                    <Select 
                     label={t('cal_associated_lead')} 
                     placeholder={t('cal_search_lead')} 
                     searchable
                     onSearchChange={(val) => {
                       if (val.length > 2) {
                         fetch(`${API_BASE_URL}/api/leads/search?query=${encodeURIComponent(val)}&ownerEmail=${user.email}`)
                           .then(r => r.json())
                           .then(data => {
                             setMeetingSearchLeads(data.content || []);
                           });
                       }
                     }}
                     data={[
                       ...(Array.isArray(leads) ? leads.map(l => ({ value: l.id.toString(), label: l.companyName })) : []),
                       ...(Array.isArray(meetingSearchLeads) ? meetingSearchLeads.map(l => ({ value: l.id.toString(), label: l.companyName })) : [])
                     ].filter((v, i, a) => a.findIndex(item => item.value === v.value) === i)}
                     value={newMeeting.leadId} 
                     onChange={val => setNewMeeting({...newMeeting, leadId: val})} 
                     nothingFoundMessage={t('cal_no_leads')}
                    />
                    <SimpleGrid cols={2}>
                      <TextInput spellCheck={false} label={t('cal_start_time')} type="datetime-local" required value={newMeeting.startTime} onChange={e => setNewMeeting({...newMeeting, startTime: e.target.value})} />
                      <TextInput spellCheck={false} label={t('cal_end_time')} type="datetime-local" required value={newMeeting.endTime} onChange={e => setNewMeeting({...newMeeting, endTime: e.target.value})} />
                    </SimpleGrid>
                    <Textarea label={t('cal_agenda')} minRows={3} spellCheck={false} value={newMeeting.description} onChange={e => setNewMeeting({...newMeeting, description: e.target.value})} />
                    <Group justify="flex-end" mt="md">
                      <Button variant="subtle" onClick={() => setMeetingModalOpened(false)}>{t('cal_cancel')}</Button>
                      <Button color="indigo" onClick={handleAddMeeting}>{t('cal_schedule')}</Button>
                    </Group>
                  </Stack>
                </Modal>
            </Stack>
          );
        })()}

        {activeTab === 'crm-whatsapp' && (
          <Grid gutter={0} h="calc(100vh - 180px)" style={{ border: '1px solid #2f3032', borderRadius: '12px', overflow: 'hidden',  }}>
            {/* Conversations List */}
            <Grid.Col span={4} style={{ borderRight: '1px solid #2f3032' }}>
              <Stack gap={0} h="100%">
                <Box p="md" style={{ borderBottom: '1px solid #2f3032',  }}>
                  <Group justify="space-between" mb="xs">
                    <Title order={4}>Chats</Title>
                    <Button size="xs" color="green" variant="light" leftSection={<IconPlus size={14}/>} onClick={() => setIsWaComposing(true)}>New</Button>
                  </Group>
                  <TextInput size="xs" placeholder="Search chats..." leftSection={<IconSearch size={14}/>} value={crmSearch} onChange={e => setCrmSearch(e.target.value)} />
                </Box>
                <ScrollArea style={{ flex: 1 }}>
                  {(() => {
                    const waMsgs = Array.isArray(crmMessages) ? crmMessages.filter(m => m.channel === 'WHATSAPP') : [];
                    const chatMap = {};
                    waMsgs.forEach(m => {
                      const id = m.incoming ? m.sender : m.recipient;
                      if (!chatMap[id] || new Date(m.receivedAt) > new Date(chatMap[id].lastMsg.receivedAt)) {
                        chatMap[id] = {
                          id: id,
                          lastMsg: m,
                          unread: waMsgs.filter(msg => (msg.sender === id) && !msg.read).length
                        };
                      }
                    });

                    const chats = Object.values(chatMap).sort((a, b) => new Date(b.lastMsg.receivedAt) - new Date(a.lastMsg.receivedAt));

                    return chats.length === 0 ? (
                      <Text ta="center" c="dimmed" mt="xl" size="sm">No WhatsApp messages yet.</Text>
                    ) : chats.map(chat => {
                      const isTechnicalId = chat.id.includes('@') || chat.id.length > 15;
                      const displayId = isTechnicalId ? chat.id.split('@')[0] : `+${chat.id}`;
                      
                      return (
                        <Box
                          key={chat.id}
                          p="md"
                          onClick={() => { setSelectedWaChat(chat.id); setSelectedMsg(chat.lastMsg); }}
                          style={{
                            cursor: 'pointer',
                            borderBottom: '1px solid #f1f3f5',
                            background: selectedWaChat === chat.id ? '#f0fdf4' : 'transparent',
                            transition: 'background 0.2s'
                          }}
                        >
                          <Group justify="space-between" wrap="nowrap">
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text fw={700} size="sm" truncate>{displayId}</Text>
                              <Text size="xs" c="dimmed" truncate>{chat.lastMsg.body}</Text>
                            </Box>
                            <Stack align="flex-end" gap={4}>
                              <Text size="10px" c="dimmed">{new Date(chat.lastMsg.receivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Text>
                              {chat.unread > 0 && <Badge size="xs" color="green" circle>{chat.unread}</Badge>}
                            </Stack>
                          </Group>
                        </Box>
                      );
                    });
                  })()}
                </ScrollArea>
              </Stack>
            </Grid.Col>

            {/* Message History */}
            <Grid.Col span={8} style={{ background: '#0a0a0a' }}>
              {selectedWaChat ? (
                <Stack gap={0} h="100%">
                  <Box p="md"  style={{ borderBottom: '1px solid #2f3032' }}>
                    <Group justify="space-between">
                      <Group>
                        <ThemeIcon color="green" radius="xl"><IconMessages size={16}/></ThemeIcon>
                        <Text fw={700}>{selectedWaChat.includes('@') ? selectedWaChat.split('@')[0] : `+${selectedWaChat}`}</Text>
                      </Group>
                    </Group>
                  </Box>
                  
                  <ScrollArea style={{ flex: 1 }} p="md">
                    <Stack gap="xs">
                      {crmMessages
                        .filter(m => (m.sender === selectedWaChat || m.recipient === selectedWaChat) && m.channel === 'WHATSAPP')
                        .sort((a, b) => new Date(a.receivedAt) - new Date(b.receivedAt))
                        .map(m => (
                          <Flex key={m.id} justify={m.incoming ? 'flex-start' : 'flex-end'}>
                            <Box
                              p="sm"
                              style={{
                                maxWidth: '80%',
                                background: m.incoming ? '#141517' : '#164e2a', color: m.incoming ? '#e4e5e8' : '#e4e5e8',
                                borderRadius: '8px',
                                boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
                                position: 'relative'
                              }}
                            >
                              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{m.body}</Text>
                              <Text size="10px" c="dimmed" ta="right" mt={4}>
                                {new Date(m.receivedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Text>
                            </Box>
                          </Flex>
                        ))}
                    </Stack>
                  </ScrollArea>

                  <Box p="md"  style={{ borderTop: '1px solid #e2e8f0' }}>
                    <Group align="flex-end">
                      <Textarea
                        placeholder="Type a message..."
                        style={{ flex: 1 }}
                        minRows={1}
                        autosize
                        maxRows={5}
                        value={waComposeData.text}
                        onChange={e => setWaComposeData({ ...waComposeData, to: selectedWaChat, text: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendWhatsApp(); } }}
                      />
                      <ActionIcon size="lg" color="green" variant="unstyled" onClick={handleSendWhatsApp} loading={isWaSending} disabled={!waComposeData.text}>
                        <IconSend size={18} />
                      </ActionIcon>
                    </Group>
                  </Box>
                </Stack>
              ) : (
                <Flex h="100%" align="center" justify="center" direction="column" c="dimmed">
                  <IconMessages size={60} stroke={1} />
                  <Text mt="md">Select a chat to start messaging</Text>
                </Flex>
              )}
            </Grid.Col>
          </Grid>
        )}

        {activeTab === 'crm-email' && (
          <Box style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {!isEmailConfigured() && (
              <Alert color="red" title={t('email_sync_disabled')} icon={<IconBan size={16}/>} mb="md">
                {t('configure_imap_prompt')}
                <Anchor ml="xs" fw={700} onClick={() => setActiveTab('settings')}>{t('go_to_settings')}</Anchor>
              </Alert>
            )}

            <Grid gutter="lg" style={{ margin: 0, flex: 1, minHeight: 0 }}>
              {/* LEFT COLUMN: THUNDERBIRD FOLDER TREE & SMART FOLDERS */}
              <Grid.Col span={{ base: 12, md: 3, lg: 2.5 }} style={{ padding: 0 }}>
                <Paper p="md" shadow="sm" radius="md" withBorder style={{ height: '100%', backgroundColor: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
                  <ScrollArea style={{ flex: 1 }} offsetScrollbars>
                    <Box pr="sm">
                  <Button 
                    fullWidth 
                    radius="md" 
                    size="md" 
                    variant="gradient" 
                    gradient={{ from: 'indigo', to: 'cyan' }} 
                    leftSection={<IconEdit size={18}/>} 
                    onClick={() => { setIsComposing(true); setSelectedMsg(null); }} 
                    mb="lg"
                    style={{ boxShadow: '0 4px 12px rgba(34, 139, 230, 0.25)' }}
                  >
                    {t('btn_compose')}
                  </Button>
                  <Button 
                    fullWidth 
                    radius="md" 
                    size="sm" 
                    variant="light" 
                    color="gray"
                    leftSection={<IconPhoto size={16}/>} 
                    onClick={() => setActiveTab('templates')} 
                    mb="lg"
                  >
                    {t('tab_email_templates')}
                  </Button>

                  <Select
                    placeholder="Wybierz konto"
                    data={[
                      { label: t('all_accounts'), value: 'ALL' },
                      { label: user.email + ' (Główne)', value: user.email },
                      
                      ...((() => {
                        try {
                          return JSON.parse(config['mail.extra.accounts'] || '[]')
                            
                            .map(a => ({ label: a.email, value: a.email }));
                        } catch(e) { return []; }
                      })())
                    ].filter((item, index, self) => item && item.value && index === self.findIndex((t) => t && t.value === item.value))}
                    value={crmAccountFilter}
                    onChange={(val) => { setCrmAccountFilter(val || 'ALL'); setCrmPage(1); fetchCRM(); }}
                    size="sm"
                    mb="lg"
                    w="100%"
                  />

                  <Text size="xs" fw={800} c="dimmed" tt="uppercase" mb="xs" style={{ letterSpacing: '0.5px' }}>
                    {t('folders_main_title')}
                  </Text>
                  <Stack gap={4}>
                    {[
                      { id: 'INBOX', label: t('folder_inbox'), icon: IconInbox, badge: folderCounts.INBOX !== undefined && folderCounts.INBOX !== null ? folderCounts.INBOX : (dashboardStats?.unreadEmails || 0) },
                      { id: 'SENT', label: t('folder_sent'), icon: IconSend },
                      { id: 'STARRED', label: t('folder_starred'), icon: IconStar, badge: folderCounts.STARRED || 0 },
                      { id: 'SPAM', label: t('folder_spam'), icon: IconBan, badge: folderCounts.SPAM || 0 },
                      { id: 'TRASH', label: t('folder_trash'), icon: IconTrash },
                      { id: 'ALL', label: t('folder_all'), icon: IconFolder }
                    ].map(f => {
                      const isActive = crmFilter === f.id;
                      return (
                        <Group 
                          key={f.id} 
                          justify="space-between" 
                          p="xs" 
                          onClick={() => { setCrmFilter(f.id); setCrmPage(1); }}
                          style={{ 
                            borderRadius: '8px', 
                            backgroundColor: isActive ? '#e8f0fe' : 'transparent',
                            color: isActive ? '#1a73e8' : '#495057',
                            cursor: 'pointer',
                            fontWeight: isActive ? 700 : 500,
                            transition: 'all 0.2s ease',
                            border: isActive ? '1px solid #d2e3fc' : '1px solid transparent'
                          }}
                        >
                          <Text size="sm" style={{ userSelect: 'none' }}>{f.label}</Text>
                          {f.badge > 0 && <Badge size="sm" color="blue" variant="unstyled">{f.badge}</Badge>}
                        </Group>
                      );
                    })}
                  </Stack>

                  <Divider my="md" />

                  <Group justify="space-between" mb="xs">
                    <Text size="xs" fw={800} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                      {t('custom_folders_title')}
                    </Text>
                    <Tooltip label={t('add_custom_folder')}>
                      <ActionIcon variant="light" color="indigo" size="xs" onClick={() => setAddFolderModalOpen(true)}>
                        <IconPlus size={14}/>
                      </ActionIcon>
                    </Tooltip>
                  </Group>

                  <Stack gap={6}>
                    {customMailFolders.length === 0 && (
                      <Text size="xs" c="dimmed" italic py="sm">{t('no_custom_folders_hint')}</Text>
                    )}
                    {customMailFolders.map(folder => {
                      const isActive = crmFilter === `CUSTOM:${folder.id}`;
                      const matchCount = folderCounts[folder.id] || folderCounts[folder.id.toUpperCase()] || folderCounts[folder.id.toLowerCase()] || 0;

                      return (
                        <Group 
                          key={folder.id} 
                          justify="space-between" 
                          p="xs" 
                          onClick={() => { setCrmFilter(`CUSTOM:${folder.id}`); setCrmPage(1); }}
                          style={{ 
                            borderRadius: '8px', 
                            backgroundColor: isActive ? '#f8f0fc' : 'transparent',
                            color: isActive ? '#862e9c' : '#343a40',
                            cursor: 'pointer',
                            fontWeight: isActive ? 700 : 500,
                            transition: 'all 0.2s ease',
                            border: isActive ? '1px solid #eeb4ff' : '1px solid transparent'
                          }}
                          noWrap
                        >
                          <Group gap="xs" noWrap style={{ flex: 1, minWidth: 0 }}>
                            <Box style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: folder.color || '#228be6', flexShrink: 0 }} />
                            <Text size="sm" truncate style={{ flex: 1 }}>{folder.name}</Text>
                          </Group>
                          <Group gap={4} noWrap>
                            {matchCount > 0 && <Badge size="sm" color="blue" variant="unstyled">{matchCount}</Badge>}
                            <Tooltip label={t('btn_delete_folder')}>
                              <ActionIcon size="xs" color="red" variant="outline" onClick={(e) => {
                                e.stopPropagation();
                                if(window.confirm(t('folder_delete_confirm'))) {
                                  const upd = customMailFolders.filter(f => f.id !== folder.id);
                                  saveCustomFoldersToStorage(upd);
                                  if (isActive) setCrmFilter('INBOX');
                                }
                              }}>
                                <IconTrash size={12}/>
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Group>
                      );
                    })}
                      </Stack>
                    </Box>
                  </ScrollArea>
                </Paper>
              </Grid.Col>

              {/* RIGHT COLUMN: MAIL LIST & VIEW */}
              <Grid.Col span={{ base: 12, md: 9, lg: 9.5 }} style={{ padding: '0 0 0 16px', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Stack gap="md" style={{ flex: 1, minHeight: 0 }}>
                  <Group justify="space-between" px="xs">
                    <Group gap="sm">
                      <Title order={4} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {(() => {
                          if (crmFilter === 'INBOX') return t('folder_inbox');
                          if (crmFilter === 'SENT') return t('folder_sent');
                          if (crmFilter === 'STARRED') return t('folder_starred');
                          if (crmFilter === 'SPAM') return t('folder_spam');
                          if (crmFilter === 'TRASH') return t('folder_trash');
                          if (crmFilter === 'ALL') return t('folder_all');
                          if (crmFilter.startsWith('CUSTOM:')) {
                            const cId = crmFilter.replace('CUSTOM:', '');
                            const cFolder = customMailFolders.find(f => f.id === cId);
                            if (cFolder) {
                              return (
                                <Group gap="xs">
                                  <Text span c={cFolder.color} fw={800}>🗂️ {cFolder.name}</Text>
                                  <Badge color={cFolder.color} variant="light" size="md">{t('filtered_by_keywords')} {cFolder.keywords}</Badge>
                                </Group>
                              );
                            }
                          }
                          return 'E-mail';
                        })()}
                      </Title>
                    </Group>
                    <Group gap="xs">
                      {crmFilter !== 'TRASH' && crmFilter !== 'ALL' && (
                        <Button
                          variant="light"
                          color="orange"
                          size="xs"
                          leftSection={<IconTrash size={14}/>}
                          onClick={() => {
                            if (window.confirm(t('confirm_clear_to_trash'))) {
                              const targetCat = crmFilter.startsWith('CUSTOM:') ? crmFilter.replace('CUSTOM:', '') : (['STARRED','SPAM'].includes(crmFilter) ? crmFilter : '');
                              const targetFolder = crmFilter.startsWith('CUSTOM:') ? '' : crmFilter;
                              fetch(`${API_BASE_URL}/api/crm/clear-folder?ownerEmail=${encodeURIComponent(user.email)}&category=${encodeURIComponent(targetCat)}&folder=${encodeURIComponent(targetFolder)}`, { method: 'POST' })
                                .then(() => {
                                  fetchCRM();
                                  fetchDashboardStats();
                                })
                                .catch(e => console.error(e));
                            }
                          }}
                        >
                          {t('btn_clear_to_trash')}
                        </Button>
                      )}
                      {crmFilter === 'TRASH' && (
                        <Button
                          variant="unstyled"
                          color="red"
                          size="xs"
                          leftSection={<IconTrash size={14}/>}
                          onClick={() => {
                            if(window.confirm(t('empty_trash_confirm'))) {
                              fetch(`${API_BASE_URL}/api/crm/clear-folder?ownerEmail=${encodeURIComponent(user.email)}&folder=TRASH`, { method: 'POST' })
                                .then(() => {
                                  fetchCRM();
                                  fetchDashboardStats();
                                })
                                .catch(e => console.error(e));
                            }
                          }}
                        >
                          {t('empty_trash_btn')}
                        </Button>
                      )}
                      <Button 
                          variant="light" 
                          size="xs"
                          color="indigo" 
                          leftSection={<IconRefresh size={14}/>} 
                          disabled={!isEmailConfigured()}
                          onClick={() => {
                              setIsSyncing(true);
                              setSyncMessage(t('sync_in_progress'));
                              fetch(`${API_BASE_URL}/api/crm/sync?ownerEmail=${user.email}`, {method:'POST'}).then(() => {
                                  setSyncMessage(t('sync_completed_refreshing'));
                                  setTimeout(() => {
                                      fetchCRM();
                                      setTimeout(() => setIsSyncing(false), 2000);
                                  }, 3000);
                              });
                          }}
                        >
                          {t('btn_sync')}
                      </Button>
                      <Button
                        variant="outline"
                        color="gray"
                        size="xs"
                        leftSection={<IconCheck size={14}/>}
                        onClick={() => {
                          if(window.confirm(t('mark_all_read_confirm'))) {
                            fetch(`${API_BASE_URL}/api/crm/messages/read-all?ownerEmail=${user.email}`, {method: 'POST'})
                              .then(() => {
                                fetchCRM();
                                fetchDashboardStats();
                              });
                          }
                        }}
                      >
                        {t('mark_all_read_btn')}
                      </Button>
                    </Group>
                  </Group>

                  <Card shadow="sm" radius="md" withBorder p="sm" >
                    <Group wrap="wrap" gap="xs">
                      <TextInput spellCheck={false} 
                        placeholder={t('search_messages_placeholder')} 
                        value={crmSearch} 
                        onChange={e => { setCrmSearch(e.target.value); setCrmPage(1); }} 
                        leftSection={<IconSearch size={14}/>}
                        style={{ flex: 1, minWidth: '220px' }}
                      />
                      
                        <Button 
                        variant={filterUnreadOnly ? "filled" : "light"} 
                        color={filterUnreadOnly ? "blue" : "gray"} 
                        size="xs" 
                        onClick={() => { setFilterUnreadOnly(!filterUnreadOnly); setCrmPage(1); }} 
                        style={{ fontWeight: filterUnreadOnly ? 800 : 600 }}
                      >
                        {filterUnreadOnly ? t('filter_unread_active') : t('filter_unread_btn')}
                      </Button>
                      <Button variant="light" color="grape" size="xs" leftSection={<IconRobot size={14}/>} onClick={handleAskAi}>{t('ask_ai_search_btn')}</Button>
                    </Group>
                  </Card>

                  
                  <Box style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
                    <ScrollArea style={{ flex: 1 }} type="hover">

                    {(() => {
                      const currentAccFilter = crmAccountFilter || 'ALL';
                      const filtered = (Array.isArray(crmMessages) ? crmMessages : [])
                        .filter(msg => currentAccFilter === 'ALL' || (msg.recipient && String(msg.recipient).toLowerCase().includes(String(currentAccFilter).toLowerCase())) || (msg.sender && String(msg.sender).toLowerCase().includes(String(currentAccFilter).toLowerCase())))
                        .filter(msg => !filterUnreadOnly || (!msg.read && !msg.draftDiscarded))
                        .filter(msg => crmSelectedCategories.length === 0 || crmSelectedCategories.includes(msg.category))
                        .filter(msg => {
                          if (crmFilter === 'INBOX') return msg.incoming && msg.category !== 'TRASH' && msg.category !== 'SPAM' && !msg.draftDiscarded;
                          if (crmFilter === 'SENT') return !msg.incoming && msg.category !== 'TRASH' && !msg.draftDiscarded;
                          if (crmFilter === 'STARRED') return msg.category === 'IMPORTANT' || (msg.subject && msg.subject.toLowerCase().includes('pilne')) || msg.starred;
                          if (crmFilter === 'SPAM') return msg.category === 'SPAM' || (msg.subject && /reklama|newsletter|promocja|oferta handlowa/i.test(msg.subject));
                          if (crmFilter === 'TRASH') return msg.category === 'TRASH' || msg.draftDiscarded;
                          if (crmFilter === 'ALL') return msg.category !== 'TRASH' && !msg.draftDiscarded;
                          if (crmFilter.startsWith('CUSTOM:')) {
                            if (msg.category === 'TRASH' || msg.draftDiscarded) return false;
                            const fId = crmFilter.replace('CUSTOM:', '');
                            const customFolder = customMailFolders.find(f => f.id === fId);
                            if (!customFolder) return true;
                            const kws = (customFolder.keywords || '').split(',').map(k => k.trim().toLowerCase()).filter(Boolean);
                            if (kws.length === 0) return true;
                            const text = `${msg.subject || ''} ${msg.sender || ''} ${msg.body || ''}`.toLowerCase();
                            return kws.some(kw => text.includes(kw));
                          }
                          return true;
                        });
                      
                      const grouped = {};
                      filtered.forEach(msg => {
                        const dateObj = msg.receivedAt ? new Date(msg.receivedAt) : new Date();
                        const dateKey = dateObj.toLocaleDateString(i18n.language || 'pl', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                        const sortKey = dateObj.toISOString().split('T')[0];
                        const key = sortKey + "|" + dateKey;
                        if (!grouped[key]) grouped[key] = [];
                        grouped[key].push(msg);
                      });
                      
                      const sortedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
                      const bgColors = ['#0a0a0a', '#101113', '#141517', '#1a1b1e'];
                      
                      if (sortedKeys.length === 0) return (
                        <Paper p="xl" radius="md" withBorder ta="center" mt="md" bg="#141517">
                          <IconInbox size={48} color="#adb5bd" style={{ margin: 'auto', display: 'block' }} mb="sm" />
                          <Text c="dimmed" fw={700} size="lg">{t('no_messages_found')}</Text>
                          <Text c="dimmed" size="xs" mt="xs">{t('sync_hint')}</Text>
                        </Paper>
                      );
                      
                      return sortedKeys.map((key, gIdx) => {
                        const dateLabel = key.split('|')[1];
                        return (
                          <Box key={key} mb="xl" p="md" style={{ backgroundColor: bgColors[gIdx % bgColors.length], borderRadius: '12px', border: '1px solid #2f3032', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <Text size="sm" fw={800} mb="md" c="indigo.7" tt="capitalize" style={{ letterSpacing: '0.5px' }}>📅 {dateLabel}</Text>
                            <Accordion 
                              variant="separated"
                              onChange={(val) => {
                                if (val) {
                                  const msg = crmMessages.find(m => String(m.id) === val);
                                  if (msg && !msg.read) {
                                    fetch(`${API_BASE_URL}/api/crm/messages/${msg.id}/read?ownerEmail=${user.email}`, {method:'POST'})
                                      .then(() => {
                                         setCrmMessages(prev => prev.map(m => m.id === msg.id ? {...m, read: true} : m));
                                         fetchDashboardStats();
                                      });
                                  }
                                }
                              }}
                            >
                              {grouped[key].map((msg, idx) => (
                                <Accordion.Item 
                                  key={msg.id || idx} 
                                  value={String(msg.id)} 
                                  style={{ 
                                    background: (!msg.read && !msg.draftDiscarded) ? '#1c202a' : '#141517',
                                    borderLeft: (!msg.read && !msg.draftDiscarded) ? '8px solid #2563eb' : '1px solid #e2e8f0',
                                    borderTop: (!msg.read && !msg.draftDiscarded) ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                                    borderRight: (!msg.read && !msg.draftDiscarded) ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                                    borderBottom: (!msg.read && !msg.draftDiscarded) ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                                    marginBottom: '10px',
                                    borderRadius: '10px',
                                    transition: 'all 0.2s ease',
                                    boxShadow: (!msg.read && !msg.draftDiscarded) ? '0 4px 14px rgba(37, 99, 235, 0.15)' : '0 1px 3px rgba(0,0,0,0.04)'
                                  }}
                                >
                                  <Accordion.Control>
                                    <Group justify="space-between" pr="md">
                                      <Box style={{ flex: 1 }}>
                                        <Group gap="xs" mb={4}>
                                           <Text size="xs" c="dimmed" fw={800} tt="uppercase">
                                             {msg.incoming ? '📥' : '📤'} {msg.aiAnalyzed && <span title="AI Analyzed">🤖</span>}
                                           </Text>
                                           {(!msg.read && !msg.draftDiscarded) && (
                                             <Badge size="md" color="blue" variant="unstyled" radius="xl" style={{ boxShadow: '0 0 10px rgba(37, 99, 235, 0.4)' }}>
                                               🔵 {t('badge_unread')}
                                             </Badge>
                                           )}
                                           {msg.category === 'IMPORTANT' && <Badge size="sm" color="yellow" variant="light" leftSection="⭐">{t('folder_starred')}</Badge>}
                                           {msg.category === 'SPAM' && <Badge size="sm" color="orange" variant="light">{t('folder_spam')}</Badge>}
                                           {msg.category === 'TRASH' && <Badge size="sm" color="red" variant="unstyled">{t('folder_trash')}</Badge>}
                                        </Group>
                                        <Text size="md" fw={(!msg.read && !msg.draftDiscarded) ? 900 : 600} c={(!msg.read && !msg.draftDiscarded) ? 'white' : 'dark.2'} truncate style={{ fontSize: (!msg.read && !msg.draftDiscarded) ? '1.05rem' : '0.95rem' }}>
                                          {msg.subject || t('no_subject')}
                                        </Text>
                                        <Text size="xs" c="dimmed" fw={500} truncate>
                                          {msg.incoming ? `${t('from_label')} ${getSenderName(msg.sender)}` : `${t('to_label')} ${msg.recipient}`}
                                        </Text>
                                      </Box>
                                      <Stack gap={4} align="flex-end">
                                        <Text size="xs" fw={700} c="dimmed">{msg.receivedAt ? new Date(msg.receivedAt).toLocaleTimeString(i18n.language || 'pl-PL', {hour: '2-digit', minute:'2-digit'}) : ''}</Text>
                                        {msg.category && msg.category !== 'IMPORTANT' && msg.category !== 'SPAM' && msg.category !== 'TRASH' && <Badge size="xs" variant="outline" color="indigo" fw={700}>{msg.category}</Badge>}
                                      </Stack>
                                    </Group>
                                  </Accordion.Control>
                                  <Accordion.Panel>
                            <Stack gap="md" p="xs">
                              <Group justify="space-between">
                                <Box>
                                  <Text size="xs" fw={700} c="dimmed">FROM: {msg.sender}</Text>
                                  <Text size="xs" fw={700} c="dimmed">TO: {msg.recipient}</Text>
                                </Box>
                                <Group gap="xs">
                                  <Tooltip label={t('star_message')}>
                                    <ActionIcon variant="light" color={msg.category === 'IMPORTANT' ? 'yellow' : 'gray'} size="md" onClick={() => {
                                      const nextCat = msg.category === 'IMPORTANT' ? 'GENERAL' : 'IMPORTANT';
                                      fetch(`${API_BASE_URL}/api/crm/messages/${msg.id}/category?category=${nextCat}&ownerEmail=${user.email}`, {method:'POST'}).then(() => {
                                        setCrmMessages(prev => prev.map(m => m.id === msg.id ? {...m, category: nextCat} : m));
                                      });
                                    }}>
                                      <IconStar size={16} fill={msg.category === 'IMPORTANT' ? '#fcc419' : 'none'}/>
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label={t('mark_spam')}>
                                    <ActionIcon variant="light" color="orange" size="md" onClick={() => {
                                      fetch(`${API_BASE_URL}/api/crm/messages/${msg.id}/category?category=SPAM&ownerEmail=${user.email}`, {method:'POST'}).then(() => {
                                        setCrmMessages(prev => prev.map(m => m.id === msg.id ? {...m, category: 'SPAM'} : m));
                                        if (crmFilter !== 'SPAM' && crmFilter !== 'ALL') setCrmMessages(prev => prev.filter(m => m.id !== msg.id));
                                      });
                                    }}>
                                      <IconBan size={16}/>
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label={crmFilter === 'TRASH' ? t('permanent_delete') : t('move_to_trash')}>
                                    <ActionIcon variant="unstyled" color="red" size="md" onClick={() => { 
                                      if (crmFilter === 'TRASH' || msg.category === 'TRASH' || msg.draftDiscarded) {
                                        if(window.confirm(t('confirm_permanent_delete'))) {
                                          fetch(`${API_BASE_URL}/api/crm/messages/${msg.id}?ownerEmail=${user.email}`, {method:'DELETE'}).then(() => {
                                            setCrmMessages(prev => prev.filter(m => m.id !== msg.id));
                                            fetchDashboardStats();
                                          });
                                        }
                                      } else {
                                        fetch(`${API_BASE_URL}/api/crm/messages/${msg.id}/category?category=TRASH&ownerEmail=${user.email}`, {method:'POST'}).then(() => {
                                          setCrmMessages(prev => prev.map(m => m.id === msg.id ? {...m, category: 'TRASH'} : m));
                                          if (crmFilter !== 'TRASH' && crmFilter !== 'ALL') setCrmMessages(prev => prev.filter(m => m.id !== msg.id));
                                          fetchDashboardStats();
                                        });
                                      }
                                    }}>
                                      <IconTrash size={16}/>
                                    </ActionIcon>
                                  </Tooltip>
                                  {msg.incoming && <Button size="xs" color="indigo" leftSection={<IconMailForward size={14}/>} onClick={() => replyToMessage(msg)}>{t('btn_reply')}</Button>}
                                </Group>
                              </Group>

                              <Divider />

                              <Box style={{ height: 'calc(100vh - 380px)', minHeight: '480px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                {msg.body && (msg.body.includes('<') || msg.body.includes('</')) 
                                  ? <EmailPreviewIframe html={msg.body} />
                                  : <Box p="md" style={{ flex: 1, overflowY: 'auto', backgroundColor: '#0a0a0a' }}><Text style={{ whiteSpace: 'pre-wrap', fontSize: '14px', lineHeight: 1.6, color: '#e4e5e8' }}>{msg.body}</Text></Box>}
                              </Box>

                              {msg.replied && (
                                <Box p="md" bg="green.0" style={{ borderRadius: '8px', border: '1px solid #dcfce7' }}>
                                  <Text fw={700} size="xs" c="green.8" mb="xs">REPLY SENT:</Text>
                                  {(() => {
                                    const replyMsg = (Array.isArray(crmMessages) ? crmMessages : []).find(m => m.id === msg.replyId);
                                    return replyMsg ? <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{replyMsg.body}</Text> : <Text size="xs" italic>View in Sent tab</Text>;
                                  })()}
                                </Box>
                              )}
                            </Stack>
                          </Accordion.Panel>
                                </Accordion.Item>
                              ))}
                            </Accordion>
                          </Box>
                        );
                      });
                    })()}
                  
                  <Flex justify="center" mt="md" pb="md">
                    <Pagination value={crmPage} onChange={setCrmPage} total={crmTotalPages} size="sm" />
                  </Flex>
                </ScrollArea>
                {(askAiResponse || isAskAiLoading) && (
                  <Box style={{ width: '350px', borderLeft: '1px solid #e9ecef', padding: '16px', display: 'flex', flexDirection: 'column', backgroundColor: '#fdfdfd' }}>
                     <Title order={5} mb="md" c="grape">🤖 {t('ai_summary_title')}</Title>
                     <ScrollArea style={{ flex: 1 }}>
                       {isAskAiLoading ? <Loader size="sm" /> : <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{askAiResponse}</Text>}
                     </ScrollArea>
                  </Box>
                )}
              </Box>
              </Stack>
            </Grid.Col>
            </Grid>

            {/* MODAL: DODAJ WŁASNY KATALOG INTELIGENTNY */}
            <Modal 
              opened={addFolderModalOpen} 
              onClose={() => setAddFolderModalOpen(false)} 
              title={<Group gap="xs"><ThemeIcon color="grape" size="md" radius="xl"><IconFolderPlus size={18}/></ThemeIcon><Text fw={700}>{t('modal_add_folder_title')}</Text></Group>}
              radius="md"
              size="md"
            >
              <Stack gap="md">
                <Text size="xs" c="dimmed">{t('smart_folder_explanation')}</Text>
                
                <TextInput 
                  label={t('folder_name_label')}
                  placeholder={t('folder_name_placeholder')}
                  value={newFolderName}
                  onChange={e => setNewFolderName(e.target.value)}
                  required
                  withAsterisk
                  styles={{ label: { fontWeight: 700 } }}
                />

                <TextInput 
                  label={t('folder_keywords_label')}
                  placeholder={t('folder_keywords_placeholder')}
                  value={newFolderKeywords}
                  onChange={e => setNewFolderKeywords(e.target.value)}
                  required
                  withAsterisk
                  styles={{ label: { fontWeight: 700 } }}
                  description={t('keywords_hint')}
                />

                <ColorInput 
                  label={t('folder_color_label')}
                  value={newFolderColor}
                  onChange={setNewFolderColor}
                  swatches={['#25262b', '#868e96', '#fa5252', '#e64980', '#be4bdb', '#7950f2', '#4c6ef5', '#228be6', '#15aabf', '#12b886', '#40c057', '#82c91e', '#fab005', '#fd7e14']}
                  styles={{ label: { fontWeight: 700 } }}
                />

                <Group justify="flex-end" mt="md">
                  <Button variant="default" onClick={() => setAddFolderModalOpen(false)}>{t('btn_cancel')}</Button>
                  <Button 
                    color="indigo" 
                    leftSection={<IconCheck size={16}/>}
                    disabled={!newFolderName.trim()}
                    onClick={() => {
                      const id = newFolderName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_') + '_' + Date.now().toString().slice(-4);
                      const newFolder = {
                        id,
                        name: newFolderName.trim(),
                        keywords: newFolderKeywords.trim(),
                        color: newFolderColor || '#228be6'
                      };
                      const updated = [...customMailFolders, newFolder];
                      saveCustomFoldersToStorage(updated);
                      setAddFolderModalOpen(false);
                      setNewFolderName('');
                      setNewFolderKeywords('');
                      setCrmFilter(`CUSTOM:${id}`);
                    }}
                  >
                    {t('btn_save_folder')}
                  </Button>
                </Group>
              </Stack>
            </Modal>
          </Box>
        )}

        {activeTab === 'ai-chat' && (
          <Stack gap="md" h="100%">
            <Tabs defaultValue="history">
  <Tabs.List mb="sm">
    <Tabs.Tab value="history">Chat History</Tabs.Tab>
    <Tabs.Tab value="templates">Chat Templates</Tabs.Tab>
  </Tabs.List>

  <Tabs.Panel value="history">
    <Group justify="space-between">
              <Title order={4}>Visitor Conversations</Title>
              <Group>
                <Button 
                  variant="unstyled" color="red" 
                  size="xs"
                  onClick={() => { if(window.confirm('Clear all chat logs?')) fetch(`${API_BASE_URL}/api/chat/clear`, {method:'DELETE'}).then(fetchChatSessions); }}
                >
                  Clear Logs
                </Button>
              </Group>
            </Group>

            

            <ScrollArea h="calc(100vh - 200px)" type="hover">
              {(() => {
                const groupedSessions = (Array.isArray(chatSessions) ? chatSessions : []).reduce((acc, session) => {
                  const email = session.visitoremail || session.visitorEmail || 'Anonimowi Użytkownicy';
                  if (!acc[email]) acc[email] = [];
                  acc[email].push(session);
                  return acc;
                }, {});

                return (
                  <Accordion variant="separated" multiple>
                    {Object.entries(groupedSessions).map(([email, sessions]) => (
                      <Accordion.Item key={email} value={email} style={{ marginBottom: '8px' }}>
                        <Accordion.Control>
                          <Group justify="space-between" style={{ flex: 1, paddingRight: '10px' }}>
                            <Group>
                              <ThemeIcon size="md" radius="xl" color="indigo" variant="light">
                                <IconMessages size={16} />
                              </ThemeIcon>
                              <Text fw={700} c="indigo">{email}</Text>
                              <Badge color="gray" variant="light">{sessions.length} {t('conversations')}</Badge>
                            </Group>
                            <Button 
                              size="xs" 
                              variant="subtle" 
                              color="gray"
                              leftSection={<IconCheck size={14}/>}
                              onClick={(e) => {
                                e.stopPropagation();
                                Promise.all(sessions.map(s => fetch(`${API_BASE_URL}/api/chat/history/${s.id}`)))
                                  .then(() => {
                                    fetchChatSessions();
                                    fetchDashboardStats();
                                  });
                              }}
                            >
                              {t('mark_as_read')}
                            </Button>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Accordion 
                            variant="contained" 
                            value={chatAccordionValue} 
                            onChange={(val) => {
                              setChatAccordionValue(val);
                              if (val) {
                                const sessionId = val;
                                setCurrentSessionId(sessionId);
                                fetchChatHistory(sessionId);
                              }
                            }}
                          >
                            {sessions.map((session) => (
                              <Accordion.Item key={session.id} value={session.id.toString()} style={{  }}>
                                <Accordion.Control>
                                  <Group justify="space-between" pr="md">
                                    <Box>
                                      <Group gap="xs">
                                        <Text fw={600} size="sm">{session.title || 'Untitled Chat'}</Text>
                                        {session.templateKey && <Badge size="xs" variant="light" color="grape">{session.templateKey}</Badge>}
                                      </Group>
                                      <Text size="xs" c="dimmed">{new Date(session.date).toLocaleString()}</Text>
                                    </Box>
                                    <ActionIcon size="sm" color="red" variant="outline" onClick={(e) => { e.stopPropagation(); if(window.confirm('Delete log?')) fetch(`${API_BASE_URL}/api/chat/sessions/${session.id}`, {method:'DELETE'}).then(fetchChatSessions); }}>
                                      <IconTrash size={14}/>
                                    </ActionIcon>
                                  </Group>
                                </Accordion.Control>
                                <Accordion.Panel>
                                  <Stack h={500} gap={0}>
                                    <ScrollArea style={{ flex: 1 }} p="md"  viewportRef={chatViewportRef} onScrollPositionChange={(pos) => { if (chatViewportRef.current) { isScrolledUpRef.current = (chatViewportRef.current.scrollHeight - pos.y - chatViewportRef.current.clientHeight) > 150; } }}>
                                      <Stack gap="md">
                                        {(Array.isArray(chatMessages) ? chatMessages : []).map((msg, idx) => (
                                          <Flex key={idx} justify={msg.role === 'USER' ? 'flex-start' : 'flex-start'}>
                                            <Box p="md" style={{ 
                                              maxWidth: '85%', 
                                              borderRadius: '16px', 
                                              backgroundColor: msg.role === 'USER' ? '#f1f5f9' : 'white', 
                                              color: '#1e293b', 
                                              border: '1px solid #e2e8f0' 
                                            }}>
                                              <Text size="xs" fw={700} mb={4} c={msg.role === 'USER' ? 'blue' : 'indigo'}>
                                                {msg.role === 'USER' ? 'Visitor' : 'AI Assistant'}
                                              </Text>
                                              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Text>
                                              <Text size="xs" mt={4} ta="right" c="dimmed">
                                                {msg.sentAt ? new Date(msg.sentAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                                              </Text>
                                            </Box>
                                          </Flex>
                                        ))}
                                      </Stack>
                                    </ScrollArea>
                                    <Group mt="md" p="xs">
                                      <TextInput 
                                        placeholder="Wpisz odpowiedź (przejęcie czatu)..." 
                                        style={{ flex: 1 }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            const val = e.currentTarget.value;
                                            if (!val.trim()) return;
                                            e.currentTarget.value = '';
                                            fetch(`${API_BASE_URL}/api/chat/reply`, {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({
                                                sessionId: session.id,
                                                ownerEmail: user.email,
                                                content: val
                                              })
                                            }).then(() => fetchChatHistory(session.id));
                                          }
                                        }}
                                      />
                                    </Group>
                                  </Stack>
                                </Accordion.Panel>
                              </Accordion.Item>
                            ))}
                          </Accordion>
                        </Accordion.Panel>
                      </Accordion.Item>
                    ))}
                    {chatSessions.length === 0 && (
                      <Text ta="center" c="dimmed" p="xl">No visitor conversations yet.</Text>
                    )}
                  </Accordion>
                );
              })()}
            </ScrollArea>
          </Tabs.Panel>

  <Tabs.Panel value="templates">
<Paper shadow="sm" p="md" radius="md" withBorder>
              <Group justify="space-between" mb="sm">
                <Title order={5}>AI Chat Templates (Contexts)</Title>
                <Button size="xs" onClick={() => { setNewTemplateKey(''); setNewTemplatePrompt(''); setTemplateModalOpen(true); }}>+ New Template</Button>
              </Group>
              <SimpleGrid cols={3}>
                {Object.entries(config).filter(([k]) => k.startsWith('ai.template.')).map(([k,v]) => {
                  const templateKey = k.replace('ai.template.', '');
                  return (
                    <Card key={k} shadow="xs" p="sm" withBorder>
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Text fw={600} style={{ wordBreak: 'break-all' }}>{templateKey}</Text>
                        <ActionIcon color="red" size="sm" onClick={() => {
                          if (window.confirm('Delete this template?')) {
                            fetch(`${API_BASE_URL}/api/config?key=${k}&ownerEmail=${user.email}`, { method: 'DELETE' })
                              .then(() => fetchConfig());
                          }
                        }}><IconTrash size={14}/></ActionIcon>
                      </Group>
                      <Text size="xs" c="dimmed" lineClamp={2} mt="xs">{v}</Text>
                      <Group gap="xs" mt="xs">
                        <Button size="compact-xs" variant="light" onClick={() => {
                          setNewTemplateKey(templateKey);
                          setNewTemplatePrompt(v);
                          setTemplateModalOpen(true);
                        }}>Edit</Button>
                        <Button size="compact-xs" variant="light" color="indigo" onClick={() => {
                          setInstallType('chat');
                          setInstallTemplateKey(templateKey);
                          setInstallModalOpened(true);
                        }}>Get Code</Button>
                      </Group>
                    </Card>
                  );
                })}
              </SimpleGrid>
            </Paper>

            <Modal opened={templateModalOpen} onClose={() => setTemplateModalOpen(false)} title="Manage AI Template">
              <Stack gap="md">
                <TextInput 
                  label="Template Key" 
                  placeholder="e.g. sales, support, demo" 
                  value={newTemplateKey} 
                  onChange={(e) => setNewTemplateKey(e.currentTarget.value)}
                  description="Use this key in the Chat initialization (e.g. &template=demo)"
                />
                <Textarea 
                  label="Context Prompt" 
                  placeholder="You are an AI assistant specialized in..." 
                  minRows={6}
                  value={newTemplatePrompt}
                  onChange={(e) => setNewTemplatePrompt(e.currentTarget.value)}
                />
                <Button onClick={() => {
                  if (!newTemplateKey || !newTemplatePrompt) return;
                  fetch(`${API_BASE_URL}/api/config`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ [`ai.template.${newTemplateKey}`]: newTemplatePrompt, ownerEmail: user.email })
                  }).then(() => {
                    fetchConfig();
                    setTemplateModalOpen(false);
                  });
                }}>Save Template</Button>
              </Stack>
            </Modal>
  </Tabs.Panel>
</Tabs>
</Stack>
        )}

        {activeTab === 'affiliate' && (
          <ScrollArea h="100%">
            <Stack gap="xl" p="md" maw={800} mx="auto">
              <Group justify="space-between">
                <Title order={3}>Affiliate Program</Title>
              </Group>
              
              <Card shadow="sm" radius="md" withBorder p="xl">
                <Stack>
                  <Text size="sm" c="dimmed">
                    Enable the affiliate program to allow users to sign up using your referral code. You will earn 30 PLN for every new customer that signs up using your code.
                  </Text>
                  
                  <Group justify="space-between" mt="md">
                    <Text fw={500}>Affiliate Program Status</Text>
                    <Button 
                      color={affiliateStats?.enabled ? 'red' : 'green'} 
                      onClick={() => {
                        fetch(`${API_BASE_URL}/api/config/affiliate-toggle`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ownerEmail: user.email, enabled: !affiliateStats?.enabled })
                        }).then(() => fetchStats());
                      }}
                    >
                      {affiliateStats?.enabled ? 'Disable Affiliate' : 'Enable Affiliate'}
                    </Button>
                  </Group>
                </Stack>
              </Card>

              {affiliateStats?.enabled && (
                <>
                  <Card shadow="sm" radius="md" withBorder p="xl">
                    <Title order={5} mb="md">Your Affiliate Link</Title>
                    <Text size="sm" c="dimmed" mb="md">
                      Share this link with your network. When they register via this link, they will be automatically assigned to your affiliate account.
                    </Text>
                    <Group>
                      <TextInput 
                        readOnly 
                        value={`https://ailinkb.com/?affiliate=${affiliateStats.code}`} 
                        style={{ flex: 1 }} 
                      />
                      <Button leftSection={<IconCopy size={16} />} onClick={() => navigator.clipboard.writeText(`https://ailinkb.com/?affiliate=${affiliateStats.code}`)}>
                        Copy Link
                      </Button>
                    </Group>
                    <Text mt="md" size="sm">Your direct code: <Badge>{affiliateStats.code}</Badge></Text>
                  </Card>

                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                    <Card shadow="sm" padding="lg" radius="md" withBorder style={{  }}>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" fw={700} c="dimmed" tt="uppercase">Total Referrals</Text>
                        <ThemeIcon color="indigo" variant="light" size="lg"><IconDatabase size={20}/></ThemeIcon>
                      </Group>
                      <Text size="32px" fw={900}>{affiliateStats.totalReferrals}</Text>
                    </Card>

                    <Card shadow="sm" padding="lg" radius="md" withBorder style={{ background: '#ecfdf5' }}>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm" fw={700} c="dimmed" tt="uppercase">Total Earnings</Text>
                        <ThemeIcon color="green" variant="light" size="lg"><IconCash size={20}/></ThemeIcon>
                      </Group>
                      <Text size="32px" fw={900} c="green.8">{affiliateStats.totalEarnings} PLN</Text>
                    </Card>
                  </SimpleGrid>
                </>
              )}
            </Stack>
          </ScrollArea>
        )}

        {activeTab === 'settings' && (
          <ScrollArea h="100%">
            <Stack gap="xl" p="md" maw={800} mx="auto">
              <Group justify="space-between">
                <Title order={3}>AILinkB Configuration</Title>
                <Button
                  color="indigo"
                  leftSection={<IconRefresh size={16}/>}
                  loading={isSavingConfig}
                  onClick={handleSaveConfig}
                >
                  Save All Settings
                </Button>
              </Group>

              {false && <Card withBorder shadow="sm" radius="md" p="xl" style={{ border: '2px solid #25D366' }}>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={4} c="green.9">WhatsApp Robot Status</Title>
                    <Group>
                      <Button variant="subtle" size="xs" color="gray" onClick={resetWhatsApp}>Reset Session</Button>
                      <Badge size="lg" color={waStatus?.status === 'open' ? 'green' : (waStatus?.status === 'connecting' ? 'orange' : 'red')}>
                        {waStatus?.status === 'open' ? 'ONLINE' : (typeof waStatus?.status === 'string' ? waStatus.status.toUpperCase() : 'DISCONNECTED')}
                      </Badge>
                    </Group>
                  </Group>
                  <Divider />

                  {waStatus?.status === 'open' ? (
                    <Alert color="green" icon={<IconRobot size={16}/>}>
                      Your WhatsApp Robot is active and ready to verify numbers and send messages.
                    </Alert>
                  ) : (
                    <Stack align="center" py="xl">
                      {!waQrCode && !isWaLoading && (
                        <Button color="green" onClick={createWaInstance}>Initialize WhatsApp Robot</Button>
                      )}
                      {isWaLoading && <Text size="sm">Connecting to robot engine...</Text>}
                      {waQrCode && (
                        <Stack align="center">
                          <Text fw={700} c="dimmed">Scan this QR Code with your WhatsApp app:</Text>
                          {waQrCode.startsWith('data:image') ? (
                            <img src={waQrCode} alt="WhatsApp QR Code" style={{ width: '250px', border: '10px solid white', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                          ) : (
                            <Box p="md" bg="dark.8" style={{ borderRadius: '8px', border: '1px dashed gray' }}>
                              <Text size="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>{waQrCode}</Text>
                            </Box>
                          )}
                          <Button variant="subtle" size="xs" onClick={fetchWaStatus}>I have scanned the code</Button>
                        </Stack>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Card>}

              <Card withBorder shadow="sm" radius="md" p="xl" style={{ border: '2px solid #6366f1' }}>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={4} c="indigo">AI Auto-Pilot (Social Media)</Title>
                  </Group>
                  <Divider />
                  
                  <Box>
                    <Text size="sm" fw={700} mb="xs">WhatsApp</Text>
                    <Switch
                      label="Włącz AI Auto-Reply dla WhatsApp"
                      description="System AI automatycznie wygeneruje i wyśle odpowiedź, gdy klient napisze do Ciebie na WhatsApp."
                      checked={config['whatsapp.auto_reply.enabled'] === 'true'}
                      onChange={e => setConfig({...config, 'whatsapp.auto_reply.enabled': e.currentTarget.checked ? 'true' : 'false'})}
                    />
                  </Box>
                  
                  <Box mt="md">
                    <Text size="sm" fw={700} mb="xs">Facebook Messenger</Text>
                    <Switch
                      label="Włącz AI Auto-Reply dla Facebook Messenger"
                      description="System AI automatycznie odpowie na nową wiadomość ze strony na Facebooku."
                      checked={config['facebook.auto_reply.enabled'] === 'true'}
                      onChange={e => setConfig({...config, 'facebook.auto_reply.enabled': e.currentTarget.checked ? 'true' : 'false'})}
                      mb="xs"
                    />
                    <TextInput
                      label="Facebook Page Access Token"
                      placeholder="EAA..."
                      type="password"
                      value={config['facebook.page.token'] || ''}
                      onChange={e => setConfig({...config, 'facebook.page.token': e.target.value})}
                      description="Token dostępu do strony z Meta for Developers."
                    />
                    <TextInput
                      label="Facebook Verify Token (Webhook)"
                      placeholder="ailinkb_secret_fb_token_2024"
                      value={config['facebook.verify.token'] || ''}
                      onChange={e => setConfig({...config, 'facebook.verify.token': e.target.value})}
                      description="Ten sam token musisz podać konfigurując Webhook w Meta for Developers (domyślnie 'ailinkb_secret_fb_token_2024')."
                      mt="xs"
                    />
                  </Box>

                </Stack>
              </Card>
              <Card withBorder shadow="sm" radius="md" p="xl">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={4} c="indigo">Billing & Subscription</Title>
                    <Badge size="lg" color={(user.email === 'office@ailinkb.com' || user.email === 'office@luxhousedesign.com') ? 'violet' : (user.subscriptionStatus === 'ACTIVE' ? 'green' : 'orange')}>
                      {(user.email === 'office@ailinkb.com' || user.email === 'office@luxhousedesign.com') ? 'LIFETIME FREE' : `Status: ${user.subscriptionStatus}`}
                    </Badge>
                    </Group>
                    <Divider />

                    {(user.email === 'office@ailinkb.com' || user.email === 'office@luxhousedesign.com') ? (

                    <Alert color="violet" icon={<IconRocket size={16}/>}>
                      This is the primary system account. You have **unlimited lifetime access** to all features without any charges.
                    </Alert>
                  ) : (
                    <>
                      {false && (
                        <Alert color="blue" icon={<IconClock size={16}/>}>
                          Your free trial ends on <strong>{new Date(user.trialEndDate).toLocaleDateString()}</strong>. 
                          You will not be charged until the trial period expires.
                        </Alert>
                      )}

                      <Group>
                        {user.subscriptionStatus === 'TRIAL' ? (
                          <Button 
                            size="md" 
                            color="indigo" 
                            leftSection={<IconCreditCard size={18}/>}
                            onClick={() => {
                              fetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ email: user.email })
                              }).then(r => r.json()).then(data => window.top.location.href = data.url);
                            }}
                          >
                            Subscribe Now
                          </Button>
                        ) : (
                          <Button 
                            variant="outline"
                            size="md" 
                            color="indigo" 
                            leftSection={<IconSettings size={18}/>}
                            onClick={() => {
                              fetch(`${API_BASE_URL}/api/stripe/customer-portal`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ email: user.email })
                              }).then(r => r.json()).then(data => window.top.location.href = data.url);
                            }}
                          >
                            Manage Billing
                          </Button>
                        )}
                      </Group>
                    </>
                  )}
                </Stack>
              </Card>

              <Card withBorder shadow="sm" radius="md" p="xl">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={4} c="indigo">Company Public Profile</Title>
                    <Button variant="light" size="xs" color="indigo" onClick={() => window.open('/?profile=' + window.btoa(user.email), '_blank')}>View Public Profile</Button>
                  </Group>
                  <Divider />
                  <Text size="sm" c="dimmed">Edit your public page design, AI image, colors, and business information using the visual builder.</Text>
                  <Button 
                    color="indigo" 
                    fullWidth 
                    leftSection={<IconEdit size={16} />}
                    onClick={() => {
                      setAuthCompanyName(config['company.name'] || '');
                      setCompanyDesc(config['company.description'] || '');
                      setAuthLocation(config['company.address'] || '');
                      setAuthMapLat(config['company.map.lat'] || '');
                      setAuthMapLng(config['company.map.lng'] || '');
                      setAuthAffiliateEnabled(config['company.affiliate.enabled'] === 'true');
                      setAuthHeaderColor(config['company.bg.color'] || '#000000');
                      setAuthPageBg(config['company.page.bg'] || '#0a0a0a');
                      setAuthHeaderImage(config['company.header.image'] || '');
                        setAuthLogo(config['company.logo'] || '');
                      window.open(window.location.pathname + '?edit_profile=true', '_blank');
                    }}
                  >
                    Open Visual Profile Builder
                  </Button>
                </Stack>
              </Card>

              <Card withBorder shadow="sm" radius="md" p="xl">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={4} c="indigo">Email Outbound (SMTP)</Title>
                    <Button 
                      size="xs" 
                      variant="light" 
                      color="indigo" 
                      leftSection={<IconRefresh size={14}/>}
                      onClick={() => {
                        fetch(`${API_BASE_URL}/api/config/test-email`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ownerEmail: user.email })
                        }).then(r => r.json()).then(data => alert(data.message));
                      }}
                    >
                      Test Connection
                    </Button>
                  </Group>
                  <Divider />

                  
                  <SimpleGrid cols={2}>
                    <TextInput spellCheck={false} 
                      label="Outgoing SMTP Host" 
                      placeholder="e.g. mail.yourserver.com" 
                      value={config['mail.smtp.host'] || ''} 
                      onChange={e => setConfig({...config, 'mail.smtp.host': e.target.value})}
                    />
                    <TextInput spellCheck={false} 
                      label="SMTP Port" 
                      placeholder="e.g. 465" 
                      value={config['mail.smtp.port'] || ''} 
                      onChange={e => setConfig({...config, 'mail.smtp.port': e.target.value})}
                    />
                  </SimpleGrid>

                  <SimpleGrid cols={2}>
                    <TextInput spellCheck={false} 
                      label="Email / Username" 
                      placeholder="office@yourcompany.com" 
                      value={config['mail.username'] || ''} 
                      onChange={e => setConfig({...config, 'mail.username': e.target.value})}
                    />
                    <TextInput spellCheck={false} 
                      label="Email Password" 
                      type="password"
                      placeholder="Your password" 
                      value={config['mail.password'] || ''} 
                      onChange={e => setConfig({...config, 'mail.password': e.target.value})}
                      leftSection={<IconPassword size={16}/>}
                    />
                  </SimpleGrid>

                  <Divider label="Incoming (IMAP)" labelPosition="center" />

                  <SimpleGrid cols={2}>
                    <TextInput spellCheck={false} 
                      label="Incoming IMAP Host" 
                      placeholder="e.g. mail.yourserver.com" 
                      value={config['mail.imap.host'] || ''} 
                      onChange={e => setConfig({...config, 'mail.imap.host': e.target.value})}
                    />
                    <TextInput spellCheck={false} 
                      label="IMAP Port" 
                      placeholder="e.g. 993" 
                      value={config['mail.imap.port'] || ''} 
                      onChange={e => setConfig({...config, 'mail.imap.port': e.target.value})}
                    />
                  </SimpleGrid>

                  <Divider label="Additional Email Accounts" labelPosition="center" mt="xl" />
                  <Stack gap="xs">
                    {(()=>{
                        let accounts = [];
                        try {
                            accounts = JSON.parse(config['mail.extra.accounts'] || '[]');
                        } catch(e) {}
                        
                        const updateAccount = (idx, field, val) => {
                          setConfig(prev => {
                            let prevAccs = [];
                            try { prevAccs = JSON.parse(prev['mail.extra.accounts'] || '[]'); } catch(e){}
                            if (!prevAccs[idx]) return prev;
                            prevAccs[idx][field] = val;
                            return { ...prev, 'mail.extra.accounts': JSON.stringify(prevAccs) };
                          });
                        };

                        return (
                          <React.Fragment>
                            {accounts.map((acc, index) => (
                               <Group key={index} align="flex-end" grow>
                                 <TextInput placeholder="Email" value={acc.email || ''} onChange={e => updateAccount(index, 'email', e.target.value)} />
                                 <TextInput type="password" placeholder="Pass" value={acc.password || ''} onChange={e => updateAccount(index, 'password', e.target.value)} />
                                 <TextInput placeholder="SMTP Host" value={acc.smtpHost || ''} onChange={e => updateAccount(index, 'smtpHost', e.target.value)} />
                                 <TextInput placeholder="SMTP Port" value={acc.smtpPort || ''} onChange={e => updateAccount(index, 'smtpPort', e.target.value)} />
                                 <TextInput placeholder="IMAP Host" value={acc.imapHost || ''} onChange={e => updateAccount(index, 'imapHost', e.target.value)} />
                                 <TextInput placeholder="IMAP Port" value={acc.imapPort || ''} onChange={e => updateAccount(index, 'imapPort', e.target.value)} />
                                   {acc.status === 'ERROR' && <Badge color="red" title={acc.error || "Failed to connect"}>ERROR</Badge>}
                                   {(acc.status === 'ACTIVE' || acc.status === 'OK') && <Badge color="green">OK</Badge>}
                                   {acc.status !== 'ERROR' && acc.status !== 'ACTIVE' && acc.status !== 'OK' && acc.failedAttempts > 0 && <Badge color="yellow">FAIL: {acc.failedAttempts}</Badge>}
                                   <Button size="xs" variant="light" color="blue" onClick={async () => {
                                      try {
                                          const res = await fetch(`${API_BASE_URL}/api/config/test-extra-account`, {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                                              body: JSON.stringify({ ownerEmail: user.email, index: index })
                                          });
                                          const data = await res.json();
                                          if (res.ok) {
                                              alert('Success: ' + data.message);
                                              setConfig(prev => ({...prev, 'mail.extra.accounts': data.accounts}));
                                          } else {
                                              alert('Failed: ' + data.message);
                                              if (data.accounts) setConfig(prev => ({...prev, 'mail.extra.accounts': data.accounts}));
                                          }
                                      } catch(e) {
                                          alert('Error: ' + e.message);
                                      }
                                   }}>Test</Button>
                                 <ActionIcon color="red" variant="unstyled" onClick={() => {
                                    setConfig(prev => {
                                      let prevAccs = [];
                                      try { prevAccs = JSON.parse(prev['mail.extra.accounts'] || '[]'); } catch(e){}
                                      prevAccs = prevAccs.filter((_, i) => i !== index);
                                      return { ...prev, 'mail.extra.accounts': JSON.stringify(prevAccs) };
                                    });
                                 }}><IconTrash size={16}/></ActionIcon>
                               </Group>
                            ))}
                            <Button size="xs" variant="light" color="indigo" onClick={() => {
                                setConfig(prev => {
                                  let prevAccs = [];
                                  try { prevAccs = JSON.parse(prev['mail.extra.accounts'] || '[]'); } catch(e){}
                                  prevAccs.push({email:'', password:'', smtpHost:'', smtpPort:'587', imapHost:'', imapPort:'993'});
                                  return { ...prev, 'mail.extra.accounts': JSON.stringify(prevAccs) };
                                });
                            }}>+ Add Email Account</Button>
                          </React.Fragment>
                        )
                    })()}
                  </Stack>
                </Stack>
              </Card>

              <Card withBorder shadow="sm" radius="md" p="xl">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={4} c="indigo">Google Ads API Integration</Title>
                    <Button 
                      size="xs" 
                      variant="light" 
                      color="indigo" 
                      onClick={() => window.open(`${API_BASE_URL}/api/google/oauth/url?email=${user.email}`, '_self')}
                    >
                      Authenticate Google Ads
                    </Button>
                  </Group>
                  <Divider />
                  <SimpleGrid cols={2}>
                    <TextInput spellCheck={false} 
                      label="Developer Token" 
                      placeholder="e.g. ABCdef123GHI456" 
                      value={config['google.ads.developer.token'] || ''} 
                      onChange={e => setConfig({...config, 'google.ads.developer.token': e.target.value})}
                    />
                    <TextInput spellCheck={false} 
                      label="Customer ID (Without dashes)" 
                      placeholder="e.g. 1234567890" 
                      value={config['google.ads.customer.id'] || ''} 
                      onChange={e => setConfig({...config, 'google.ads.customer.id': e.target.value})}
                    />
                  </SimpleGrid>
                </Stack>
              </Card>

              <Card withBorder shadow="sm" radius="md" p="xl">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={4} c="indigo">Inbox Integration (IMAP)</Title>
                    <Button 
                      size="xs" 
                      variant="light" 
                      color="indigo" 
                      leftSection={<IconRefresh size={14}/>}
                      onClick={() => {
                        fetch(`${API_BASE_URL}/api/config/test-imap`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ ownerEmail: user.email })
                        }).then(r => r.json()).then(data => alert(data.message));
                      }}
                    >
                      Test IMAP Connection
                    </Button>
                  </Group>
                  <Divider />

                  <Group justify="space-between">
                    <Box>
                      <Text fw={700}>Current Status: <Badge color={user.subscriptionStatus === 'ACTIVE' ? 'green' : 'blue'}>{user.subscriptionStatus || 'TRIAL'}</Badge></Text>
                      <Text size="xs" c="dimmed">Your subscription enables unlimited AI discovery and automated CRM replies.</Text>
                    </Box>
                    {user.subscriptionStatus === 'ACTIVE' ? (
                      <Button variant="light" color="indigo" onClick={() => {
                        fetch(`${API_BASE_URL}/api/stripe/customer-portal`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email: user.email })
                        }).then(r => r.json()).then(data => window.top.location.href = data.url);
                      }}>Manage Billing</Button>
                    ) : (
                      <Button color="indigo" onClick={() => {
                        fetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ email: user.email })
                        }).then(r => r.json()).then(data => window.top.location.href = data.url);
                      }}>Upgrade to PRO</Button>
                    )}
                  </Group>
                </Stack>
              </Card>

              <Card withBorder shadow="sm" radius="md" p="xl">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Title order={4} c="indigo">Automation & AI Behavior</Title>
                  </Group>
                  <Divider />

                  
                  <Select 
                    label="AI Response Strategy"
                    description="How should the system handle incoming emails?"
                    data={[
                      { value: 'MANUAL', label: 'Manual - Always wait for my approval' },
                      { value: 'SEMI_AUTO', label: 'Semi-Auto - AI replies to simple queries only' },
                      { value: 'FULL_AUTO', label: 'Full-Auto - AI replies to everything instantly' }
                    ]}
                    value={config['ai.response.mode'] || 'MANUAL'}
                    onChange={val => setConfig({...config, 'ai.response.mode': val})}
                  />

                  <Select
                    label="Auto-Reply Data Source"
                    description="The knowledge base the AI uses to draft email replies."
                    data={[
                      { value: 'general_only', label: 'Services & General Info Only (High Privacy)' }
                    ]}
                    value="general_only"
                    disabled
                  />

                  <Select 
                    label="AI Provider"
                    description="Locked Architecture: Only Local Ollama is permitted."
                    data={[
                      { value: 'ollama', label: 'Agentic AI' }
                    ]}
                    value={'ollama'}
                    disabled
                  />

                  <Select 
                   label="AI Model"
                   description="Available models from local provider."
                   data={availableModels}
                   value={config['ai.model'] || 'llama3.1'}
                   onChange={val => setConfig({...config, 'ai.model': val})}
                  />

                  {config['ai.provider'] === 'ollama' && (
                    <TextInput 
                      label="Ollama URL"
                      placeholder="http://ollama:11434"
                      description="Internal docker URL: http://ollama:11434"
                      value={config['ollama.url'] || 'http://ollama:11434'}
                      onChange={e => setConfig({...config, 'ollama.url': e.target.value})}
                    />
                  )}

                  {config['ai.provider'] === 'gemini' && (
                    <TextInput spellCheck={false}
                    label="Google Gemini API Key"
  
                      type="password"
                      placeholder="AIza..." 
                      description="Optional: Use your own key. If empty, trial key is used."
                      value={config['google.gemini.api.key'] || ''} 
                      onChange={e => setConfig({...config, 'google.gemini.api.key': e.target.value})}
                      leftSection={<IconRocket size={16}/>}
                    />
                  )}
                  <TextInput
                    label="Default Greeting Message"
                    description="The first message the AI sends to the user when they open the chat."
                    placeholder="Welcome! I am the AI assistant. How can I help you today?"
                    value={config['ai.greeting.message'] || ''}
                    onChange={e => setConfig({...config, 'ai.greeting.message': e.target.value})}
                    mt="md"
                  />
                  <Textarea
                    label="Chatbot Core Instructions (System Prompt)"
                    description="The foundational prompt telling the Chat AI how to behave. It will be combined with context, history, and calendar data dynamically."
                    placeholder="You are a professional AI Assistant representing the company. Your absolute priority is to answer questions ONLY using the information provided in the Context..."
                    minRows={8}
                    autosize
                    maxRows={15}
                    value={config['ai.system.prompt'] || ''}
                    onChange={e => setConfig({...config, 'ai.system.prompt': e.target.value})}
                    styles={{ input: { fontFamily: 'monospace', fontSize: '13px' } }}
                    mt="md"
                  />
                  <Textarea
                    label="Email Auto-Reply Instructions"
                    description="The prompt used when generating email drafts. The system will automatically append constraints ensuring a JSON response format."
                    minRows={6}
                    autosize
                    maxRows={15}
                    value={config['ai.autoreply.prompt'] || ''}
                    onChange={e => setConfig({...config, 'ai.autoreply.prompt': e.target.value})}
                    styles={{ input: { fontFamily: 'monospace', fontSize: '13px' } }}
                    mt="md"
                  />
                </Stack>
              </Card>

              {user.email === 'office@ailinkb.com' && (
                <>
              <Card withBorder shadow="sm" radius="md" p="xl">
                <Stack gap="md">
                  <Title order={4} c="indigo">{t("admin_system_settings_title", "System Global Settings (Admin Only)")}</Title>
                  <Divider />

                  <TextInput
                    label="Search Similarity Threshold"
                    description="Cosine similarity cutoff (0.0 to 1.0) for vector AI search. Lower means more results, higher means stricter matching. Default: 0.45."
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={config['search.similarity.threshold'] || '0.45'}
                    onChange={e => setConfig({...config, 'search.similarity.threshold': e.target.value})}
                  />

                  <Title order={4} c="indigo" mt="lg">External System Keys</Title>
                  <Divider />

                  {user?.email === 'office@ailinkb.com' && (
                    <React.Fragment>
                      <Title order={5} c="red" mt="md">Google OAuth 2.0 (Google Maps Publishing) [ADMIN ONLY]</Title>
                      <SimpleGrid cols={2}>
                        <TextInput spellCheck={false}
                          label="Google OAuth Client ID"
                          placeholder="e.g. 12345678-xxxx.apps.googleusercontent.com"
                          value={config['google.oauth.client.id'] || ''}
                          onChange={e => setConfig({...config, 'google.oauth.client.id': e.target.value})}
                        />
                        <TextInput spellCheck={false}
                          label="Google OAuth Client Secret"
                          type="password"
                          placeholder="e.g. GOCSPX-xxxx"
                          value={config['google.oauth.client.secret'] || ''}
                          onChange={e => setConfig({...config, 'google.oauth.client.secret': e.target.value})}
                          leftSection={<IconPassword size={16}/>}
                        />
                      </SimpleGrid>
                      <Divider mt="md" />
                    </React.Fragment>
                  )}

                  <Checkbox 
                    label="Stripe Live Mode (OFF = Test Mode)" 
                    mb="md"
                    checked={config['stripe.live.mode'] === 'true'} 
                    onChange={e => setConfig({...config, 'stripe.live.mode': e.currentTarget.checked ? 'true' : 'false'})}
                  />

                  <SimpleGrid cols={2}>
                    <Box>
                      <Text size="sm" fw={700} mb="xs" c="blue">Test Environment (sk_test_...)</Text>
                      <Stack gap="xs">
                        <TextInput spellCheck={false} label="Test Secret Key" type="password" placeholder="sk_test_..." value={config['stripe.test.api.key'] || ''} onChange={e => setConfig({...config, 'stripe.test.api.key': e.target.value})} />
                        <TextInput spellCheck={false} label="Test Price ID" placeholder="price_..." value={config['stripe.test.price.id'] || ''} onChange={e => setConfig({...config, 'stripe.test.price.id': e.target.value})} />
                        <TextInput spellCheck={false} label="Test Webhook Secret" type="password" placeholder="whsec_..." value={config['stripe.test.webhook.secret'] || ''} onChange={e => setConfig({...config, 'stripe.test.webhook.secret': e.target.value})} />
                      </Stack>
                    </Box>
                    <Box>
                      <Text size="sm" fw={700} mb="xs" c="green">Production Environment (sk_live_...)</Text>
                      <Stack gap="xs">
                        <TextInput spellCheck={false} label="Live Secret Key" type="password" placeholder="sk_live_..." value={config['stripe.live.api.key'] || ''} onChange={e => setConfig({...config, 'stripe.live.api.key': e.target.value})} />
                        <TextInput spellCheck={false} label="Live Price ID" placeholder="price_..." value={config['stripe.live.price.id'] || ''} onChange={e => setConfig({...config, 'stripe.live.price.id': e.target.value})} />
                        <TextInput spellCheck={false} label="Live Webhook Secret" type="password" placeholder="whsec_..." value={config['stripe.live.webhook.secret'] || ''} onChange={e => setConfig({...config, 'stripe.live.webhook.secret': e.target.value})} />
                      </Stack>
                    </Box>
                  </SimpleGrid>
                  <Text size="xs" c="dimmed">Get your Price ID from Stripe Dashboard &rarr; Products.</Text>
                </Stack>
              </Card>
            </>
          )}

          {/* DANGER ZONE - General function to delete profile */}
          <Title order={4} c="red" mt="xl">Danger Zone</Title>
          <Card withBorder shadow="sm" radius="md" p="xl" style={{ borderColor: 'red' }}>
            <Stack gap="md">
              <Text size="sm" fw={500} c="red">Delete Account</Text>
              <Text size="xs" c="dimmed">
                Once you delete your account, there is no going back. Please be certain.
              </Text>
              <Button 
                color="red" 
                variant="outline" 
                onClick={() => {
                  if (window.confirm("Are you sure you want to completely delete your account and all data? This action is irreversible!")) {
                    fetch(`${API_BASE_URL}/api/users/me?email=${user.email}`, { method: 'DELETE' })
                      .then(res => {
                        if(res.ok) {
                          alert("Account deleted.");
                          handleLogout();
                        } else {
                          alert("Failed to delete account. Please contact support.");
                        }
                      }).catch(console.error);
                  }
                }}
              >
                Delete My Account
              </Button>
            </Stack>
          </Card>

        </Stack>
      </ScrollArea>
    )}

        {activeTab === 'orders' && (
          <Box p="md">
            <Card shadow="sm" radius="md" withBorder p="xl">
              <Title order={4} mb="lg" c="indigo">Service Orders</Title>
              <Table verticalSpacing="md" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Service</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Customer</Table.Th>
                    <Table.Th>Attachment</Table.Th>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(Array.isArray(orders) ? orders : []).map((o) => (
                    <Table.Tr key={o.id}>
                      <Table.Td><Text fw={700}>{o.serviceName}</Text></Table.Td>
                      <Table.Td>{o.amount} {o.currency}</Table.Td>
                      <Table.Td>{o.visitorEmail}</Table.Td>
                      <Table.Td>
                        {o.attachment ? (
                          <ActionIcon color="indigo" variant="light" onClick={() => {
                            const win = window.open();
                            win.document.write(`<img src="${o.attachment}" style="max-width:100%; height:auto;">`);
                          }}>
                            <IconPhoto size={16}/>
                          </ActionIcon>
                        ) : <Text size="xs" c="dimmed">None</Text>}
                      </Table.Td>
                      <Table.Td>{new Date(o.createdAt).toLocaleString()}</Table.Td>
                      <Table.Td>
                        <Badge color={o.status === 'PAID' ? 'green' : 'orange'}>{o.status}</Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {orders.length === 0 && (
                    <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="xl">No orders found yet.</Text></Table.Td></Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Card>
          </Box>
        )}

        {activeTab === 'google-maps' && (
          <GoogleMapsTab 
            user={user} 
            apiBaseUrl={API_BASE_URL} 
          />
        )}

        {activeTab === 'automations' && (
          <Flex h="calc(100vh - 150px)" gap="md">
            {/* CAMPAIGN LIST */}
            <Card withBorder shadow="sm" radius="md" p={0} w={350} style={{ display: 'flex', flexDirection: 'column',  }}>
              <Box p="md" style={{ borderBottom: '1px solid #2f3032' }}>
                <Button 
                  fullWidth 
                  color="indigo" 
                  leftSection={<IconPlus size={16}/>}
                  onClick={() => {
                    setCampaignForm({ name: '', subject: '', htmlContent: '', frequency: 'ONCE', active: false, senderEmail: '', autoTranslate: false });
                    setCampaignAttachments([]);
                    setIsCampaignEditing(true);
                  }}
                >
                  {t('campaign_btn_create')}
                </Button>
              </Box>
              <ScrollArea style={{ flex: 1 }}>
                <Stack gap={0}>
                  {(Array.isArray(campaigns) ? campaigns : []).map((c) => (
                    <Box
 
                      key={c.id} 
                      p="md" 
                      onClick={() => {
                        setCampaignForm(c);
                        setCampaignAttachments([]);
                        setIsCampaignEditing(true);
                      }}
                      style={{ 
                        cursor: 'pointer', 
                        borderBottom: '1px solid #f1f5f9',
                        background: campaignForm.id === c.id ? '#f1f5f9' : 'transparent',
                        borderLeft: campaignForm.id === c.id ? '4px solid #4f46e5' : '4px solid transparent'
                      }}
                    >
                      <Group justify="space-between" mb={4}>
                        <Text fw={700} size="sm" truncate>{c.name}</Text>
                        <Badge size="xs" color={c.active ? 'green' : 'gray'}>{c.active ? 'ACTIVE' : 'PAUSED'}</Badge>
                      </Group>
                      <Text size="xs" c="dimmed" truncate>Sub: {c.subject}</Text>
                      <Group justify="space-between" mt="xs">
                        <Badge size="xs" variant="outline">{c.frequency}</Badge>
                        <Text size="10px" fw={700} c="indigo">Sent: {c.sentCount || 0}/{c.totalEligible || 0}</Text>
                      </Group>
                      <Group mt={4}>
                        {c.lastRun && <Text size="10px" c="dimmed">Last: {new Date(c.lastRun).toLocaleDateString()}</Text>}
                      </Group>
                    </Box>
                  ))}
                </Stack>
              </ScrollArea>
            </Card>

            {/* CAMPAIGN EDITOR */}
            <Card withBorder shadow="sm" radius="md" p="xl" style={{ flex: 1, display: 'flex', flexDirection: 'column',  }}>
              {isCampaignEditing ? (
                <Stack gap="md" style={{ flex: 1 }}>
                  {!isEmailConfigured() && (
                    <Alert color="red" title="Email Account Not Configured" icon={<IconBan size={16}/>}>
                      {t("admin_smtp_warning_text", "You cannot send campaigns or tests until you configure your SMTP settings.")} 
                      <Anchor ml="xs" fw={700} onClick={() => setActiveTab('settings')}>{t("admin_go_to_settings", "Go to Settings now")}</Anchor>
                    </Alert>
                  )}

                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Title order={4} style={{ whiteSpace: 'nowrap' }}>{campaignForm.id ? 'Edit Campaign' : 'New Campaign'}</Title>
                    <Flex gap="xs" wrap="wrap" justify="flex-end">
                      {campaignForm.id && (
                        <Button
                          variant="light"
                          color={campaignForm.active ? 'orange' : 'green'}
                          size="xs"
                          leftSection={campaignForm.active ? <IconPlayerStop size={14}/> : <IconPlayerPlay size={14}/>}
                          onClick={() => toggleCampaign(campaignForm.id)}
                          disabled={!isEmailConfigured()}
                        >
                          {campaignForm.active ? 'Pause' : 'Activate'}
                        </Button>
                      )}

                      <Button
                        variant="light"
                        color="blue"
                        size="xs"
                        leftSection={<IconSend size={14}/>}
                        disabled={!isEmailConfigured()}
                        onClick={() => {
                          fetch(`${API_BASE_URL}/api/leads/direct-send?ownerEmail=${user.email}`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ to: user.email, subject: "[TEST] " + campaignForm.subject, body: campaignForm.htmlContent })
                          }).then(() => alert("Test email sent to your inbox!"));
                        }}
                      >
                        Test Me
                      </Button>

                      <Button variant="light" size="xs" onClick={() => {
                        setIsTemplateGalleryOpen(true);
                      }}>{t('campaign_btn_templates')}</Button>

                      <Button color="indigo" size="xs" onClick={() => handleSaveCampaign(campaignAttachments)} disabled={!isEmailConfigured()}>{t('campaign_btn_save')}</Button>

                      {campaignForm.id && (
                        <ActionIcon variant="outline" color="red" size="sm" onClick={() => { if(window.confirm(t('campaign_delete_confirm'))) fetch(`${API_BASE_URL}/api/campaigns/${campaignForm.id}?ownerEmail=${user.email}`, {method:'DELETE'}).then(fetchCampaigns).then(()=>setIsCampaignEditing(false)); }}>
                          <IconTrash size={16}/>
                        </ActionIcon>
                      )}
                    </Flex>
                  </Group>

                  <SimpleGrid cols={6}>
                    <TextInput spellCheck={false} label={t('campaign_name')} required value={campaignForm.name} onChange={e => setCampaignForm({...campaignForm, name: e.target.value})} />
                    <Select label={t('campaign_from')} data={[config['mail.username'], ...((()=>{try{return JSON.parse(config['mail.extra.accounts']||'[]').map(a=>a.email);}catch(e){return [];}})())].filter(Boolean)} value={campaignForm.senderEmail || config['mail.username'] || ''} onChange={val => setCampaignForm({...campaignForm, senderEmail: val})} />
                    <Select label={t('campaign_freq')} data={['ONCE', 'DAILY', 'WEEKLY', 'MONTHLY']} value={campaignForm.frequency} onChange={val => setCampaignForm({...campaignForm, frequency: val})} />
                    <Select label={t('campaign_target')} searchable clearable data={Array.from(new Set(['ALL', 'GENERAL', 'TEST', 'VIP', 'PARTNER', 'real_estate_customers', 'IT_EMPLOYEES', 'SEO_MEDIUM_OR_POOR', 'BRAK_STRONY', ...(Array.isArray(leads) ? leads : []).map(l => l.category).filter(Boolean)]))
                      .map(c => ({ value: c, label: c === 'BRAK_STRONY' ? t('cat_no_website') : (c === 'SEO_MEDIUM_OR_POOR' ? t('cat_seo_poor') : c) }))} value={campaignForm.targetCategory} onChange={val => setCampaignForm({...campaignForm, targetCategory: val})} maxDropdownHeight={250} />
                    <NumberInput label={t('campaign_delay')} min={1} max={3600} value={campaignForm.sendDelaySeconds} onChange={val => setCampaignForm({...campaignForm, sendDelaySeconds: val})} />
                    <Box mt="xl">
                      <Switch label={t('campaign_auto_translate')} checked={campaignForm.autoTranslate || false} onChange={e => setCampaignForm({...campaignForm, autoTranslate: e.currentTarget.checked})} />
                    </Box>
                  </SimpleGrid>

                  <FileInput
                    multiple
                    clearable
                    label={t('compose_attachments')}
                    placeholder={t('compose_attachments_placeholder')}
                    value={campaignAttachments}
                    onChange={setCampaignAttachments}
                  />

                  <TextInput spellCheck={false} label={t('campaign_subject')} required value={campaignForm.subject} onChange={e => setCampaignForm({...campaignForm, subject: e.target.value})} />

                  <Box style={{ flex: 1, minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
                    <Group justify="space-between" mb={5}>
                      <Text size="sm" fw={500}>Email Content (HTML)</Text>
                      <Button variant="subtle" size="compact-xs" leftSection={<IconBolt size={12}/>} onClick={() => setIsCampaignHtmlMode(!isCampaignHtmlMode)}>
                        {isCampaignHtmlMode ? 'Switch to Preview' : 'Switch to HTML Code'}
                      </Button>
                    </Group>
                    <Box style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                      {isCampaignHtmlMode ? (
                        <Textarea 
                          spellCheck={false}
                          value={campaignForm.htmlContent}
                          onChange={(e) => setCampaignForm({ ...campaignForm, htmlContent: e.target.value })}
                          styles={{ 
                            root: { height: '100%', display: 'flex', flexDirection: 'column' },
                            wrapper: { flex: 1, display: 'flex', flexDirection: 'column' },
                            input: { flex: 1, border: 'none', fontFamily: 'monospace', fontSize: '13px' } 
                          }}
                          style={{ height: '100%' }}
                        />
                      ) : (
                        <NativeVisualEmailEditor 
                          value={campaignForm.htmlContent}
                          onChange={(content) => setCampaignForm({ ...campaignForm, htmlContent: content })}
                        />
                      )}
                    </Box>
                  </Box>
                </Stack>
              ) : (
                <Flex align="center" justify="center" h="100%" direction="column" c="dimmed">
                  <IconBolt size={60} stroke={1} />
                  <Text mt="md" size="lg">Select or create an automated email campaign</Text>
                  <Text size="sm">Campaigns will be sent to ALL customers in your database.</Text>
                </Flex>
              )}
            </Card>
          </Flex>
        )}
          {activeTab === 'templates' && (
          <Flex h="calc(100vh - 150px)" gap="md">
            {/* TEMPLATE LIST */}
            <Card withBorder shadow="sm" radius="md" p={0} w={350} style={{ display: 'flex', flexDirection: 'column',  }}>
              <Box p="md" style={{ borderBottom: '1px solid #2f3032' }}>
                <Button
                  fullWidth
                  color="indigo"
                  leftSection={<IconPlus size={16}/>}
                  onClick={() => {
                    setTemplateForm({ id: null, name: '', subject: '', htmlContent: '' });
                    setIsTemplateEditing(true);
                  }}
                >
                  New Template
                </Button>
              </Box>
              <ScrollArea style={{ flex: 1 }}>
                <Stack gap={0}>
                  <Divider label={t('template_system_ready')} labelPosition="center" my="xs" />
                  {PROFESSIONAL_TEMPLATES.map((tpl) => (
                    <Box
                      key={tpl.id}
                      p="md"
                      onClick={() => {
                        setTemplateForm({ id: null, name: tpl.name, subject: tpl.subject, htmlContent: tpl.html });
                        setIsTemplateEditing(true);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                        background: templateForm.name === tpl.name ? '#f1f5f9' : 'transparent',
                        borderLeft: templateForm.name === tpl.name ? '4px solid #4f46e5' : '4px solid transparent'
                      }}
                    >
                      <Box>
                        <Text fw={600} size="sm" truncate>{tpl.name}</Text>
                        <Text size="xs" c="indigo">{t('template_ready_pattern')}</Text>
                      </Box>
                    </Box>
                  ))}

                  <Divider label={t('template_my_templates')} labelPosition="center" my="xs" />
                  {(Array.isArray(emailTemplates) ? emailTemplates : []).map((tpl) => (
                    <Box
                      key={tpl.id}
                      p="md"
                      onClick={() => {
                        setTemplateForm(tpl);
                        setIsTemplateEditing(true);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderBottom: '1px solid #f1f5f9',
                        background: templateForm.id === tpl.id ? '#f1f5f9' : 'transparent',
                        borderLeft: templateForm.id === tpl.id ? '4px solid #4f46e5' : '4px solid transparent'
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Box style={{ overflow: 'hidden' }}>
                          <Text fw={600} size="sm" truncate>{tpl.name}</Text>
                          {tpl.subject ? (
                            <Text size="xs" c="dimmed" truncate>{t('template_subject_prefix')} {tpl.subject}</Text>
                          ) : (
                            <Text size="xs" c="dimmed">{t('template_saved_design')}</Text>
                          )}
                        </Box>
                        <ActionIcon variant="outline" color="red" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteEmailTemplate(tpl.id); }}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Box>
                  ))}
                  {emailTemplates.length === 0 && (
                    <Text ta="center" c="dimmed" p="xl" size="sm">{t('template_no_saved')}</Text>
                  )}
                </Stack>
              </ScrollArea>
            </Card>

            {/* TEMPLATE EDITOR */}
            <Card withBorder shadow="sm" radius="md" p="md" style={{ flex: 1, display: 'flex', flexDirection: 'column',  }}>
              {isTemplateEditing ? (
                <Stack h="100%" gap="md">
                  <Group justify="space-between">
                    <Title order={4}>{templateForm.id ? 'Edit Template' : 'New Template'}</Title>
                    <Group>
                      <Button variant="light" size="xs" leftSection={<IconPhoto size={14}/>} onClick={() => setIsTemplateGalleryOpen(true)}>
                        Gallery
                      </Button>
                      <Button variant="subtle" color="gray" size="xs" onClick={() => { setIsTemplateEditing(false); setTemplateForm({ id: null, name: '', subject: '', htmlContent: '' }); }}>Clear</Button>
                      {templateForm.id && (
                        <Button variant="unstyled" color="red" size="xs" leftSection={<IconTrash size={14}/>} onClick={() => handleDeleteEmailTemplate(templateForm.id)}>
                          Delete
                        </Button>
                      )}
                      <Button color="indigo" size="xs" onClick={handleSaveEmailTemplate}>Save Template</Button>
                    </Group>
                  </Group>

                  <SimpleGrid cols={{ base: 1, sm: 2 }}><TextInput spellCheck={false} label="Template Name" placeholder="e.g. Welcome Email" required value={templateForm.name} onChange={e => setTemplateForm({...templateForm, name: e.target.value})} /><TextInput spellCheck={false} label="Email Subject" placeholder="e.g. Special Offer!" required value={templateForm.subject || ''} onChange={e => setTemplateForm({...templateForm, subject: e.target.value})} /></SimpleGrid>

                  <Box style={{ flex: 1, minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
                    <Group justify="space-between" mb={5}>
                      <Text size="sm" fw={500}>Podgląd & Edycja</Text>
                      <Group>
                        <Button variant="subtle" size="compact-xs" leftSection={<IconBolt size={12}/>} onClick={() => setIsTemplateHtmlMode(!isTemplateHtmlMode)}>
                          {isTemplateHtmlMode ? 'Włącz Podgląd' : 'Edytuj HTML'}
                        </Button>
                      </Group>
                    </Group>
                    <Box style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                      {isTemplateHtmlMode ? (
                        <Textarea 
                          spellCheck={false}
                          value={templateForm.htmlContent}
                          onChange={(e) => setTemplateForm({ ...templateForm, htmlContent: e.target.value })}
                          styles={{ 
                            root: { height: '100%', display: 'flex', flexDirection: 'column' },
                            wrapper: { flex: 1, display: 'flex', flexDirection: 'column' },
                            input: { flex: 1, border: 'none', fontFamily: 'monospace', fontSize: '13px' } 
                          }}
                          style={{ height: '100%' }}
                        />
                      ) : (
                        <NativeVisualEmailEditor 
                          value={templateForm.htmlContent}
                          onChange={(content) => setTemplateForm({ ...templateForm, htmlContent: content })}
                        />
                      )}
                    </Box>
                  </Box>
                </Stack>
              ) : (
                <Flex align="center" justify="center" h="100%" direction="column" c="dimmed">
                  <IconPhoto size={60} stroke={1} />
                  <Text mt="md" size="lg">Select or create an email template</Text>
                  <Text size="sm">Design beautiful HTML emails once and reuse them in your campaigns.</Text>
                </Flex>
              )}
            </Card>
          </Flex>
        )}

        {activeTab === 'blacklist' && (
            <ScrollArea h="100%">
              <Stack gap="xl" p="md">
                <Title order={3}>Blacklist (Opt-out Requests)</Title>

              <Card withBorder shadow="sm" radius="md">
                <Table verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Company</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Action</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(Array.isArray(leads) ? leads : []).filter(l => l.optOut).map((lead) => (
                      <Table.Tr key={lead.id}>
                        <Table.Td>
                          <Text fw={700} size="sm">{lead.companyName}</Text>
                          <Text size="xs" c="dimmed">{lead.address}</Text>
                        </Table.Td>
                        <Table.Td>{lead.email} {lead.emailStatus === 'VALID' && <span title="Zweryfikowany e-mail">✅</span>} {lead.emailStatus === 'INVALID' && <span title="Błędna domena e-mail">❌</span>}</Table.Td>
                        <Table.Td>
                          <Badge color="red" leftSection={<IconBan size={12}/>}>OPT-OUT</Badge>
                        </Table.Td>
                        <Table.Td>
                          <Button size="xs" variant="light" color="blue" onClick={() => {
                            if(window.confirm(`Unblock ${lead.companyName}?`)) {
                              fetch(`${API_BASE_URL}/api/leads/${lead.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ optOut: false, status: 'NEW' })
                              }).then(fetchLeads);
                            }
                          }}>Restore</Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                {leads.filter(l => l.optOut).length === 0 && (
                  <Flex h={200} align="center" justify="center" direction="column" c="dimmed">
                    <IconBan size={40} stroke={1}/>
                    <Text mt="md">No opt-out requests found. Companies that ask to be removed will appear here.</Text>
                  </Flex>
                )}
              </Card>
            </Stack>
          </ScrollArea>
        )}

        {activeTab === 'social-media' && (
          <ScrollArea h="100%">
            <Container size="xl" p="md">
              <Stack gap="lg">
                <Paper withBorder p="lg" radius="md" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)', color: 'white', boxShadow: '0 8px 32px rgba(49, 46, 129, 0.25)' }}>
                  <Group justify="space-between" align="center">
                    <Box>
                      <Group gap="xs">
                        <Badge color="grape" variant="unstyled" size="lg">PRO OMNICHANNEL STUDIO</Badge>
                        <Badge color="blue" variant="light" c="blue.1">AI & AUTOMATIONS</Badge>
                      </Group>
                      <Title order={2} mt="xs" style={{ fontFamily: 'Outfit, Inter, sans-serif', fontWeight: 800 }}>
                        {t('social_studio_title', 'Social Media')}
                      </Title>
                    </Box>
                    <ThemeIcon size={60} radius="xl" variant="gradient" gradient={{ from: 'indigo', to: 'grape' }}>
                      <IconShare size={32} />
                    </ThemeIcon>
                  </Group>
                </Paper>

                {socialSubTab === 'upload' && (
                  <Card withBorder shadow="md" radius="md" p="xl">
                    <Title order={4} mb="md" c="indigo.8">{t('social_upload_media_label')}</Title>
                    <Grid gutter="xl">
                      <Grid.Col span={6}>
                        <FileInput
                          label={t('social_upload_media_label')}
                          placeholder={t('file_select')}
                          leftSection={<IconUpload size={18}/>}
                          accept="image/*,video/*"
                          clearable
                          value={socialMediaFile}
                          onChange={(file) => {
                            setSocialMediaFile(file);
                            if (file) {
                              if (file.type && file.type.startsWith('video/')) {
                                setSocialMediaType('VIDEO');
                              } else {
                                setSocialMediaType('IMAGE');
                              }
                              const reader = new FileReader();
                              reader.onload = (e) => {
                                setSocialMediaPreview(e.target.result);
                              };
                              reader.readAsDataURL(file);
                            } else {
                              setSocialMediaPreview(null);
                            }
                          }}
                          mb="md"
                        />

                        {socialMediaPreview ? (
                          <Box style={{ border: '2px dashed #6366f1', borderRadius: 12, padding: 12, textAlign: 'center',  }}>
                            <Badge color={socialMediaType === 'VIDEO' ? 'red' : 'green'} mb="xs">{socialMediaType === 'VIDEO' ? 'WIDEO / REEL PREVIEW' : 'FOTO PREVIEW'}</Badge>
                            {socialMediaType === 'VIDEO' ? (
                              <video controls src={socialMediaPreview} style={{ width: '100%', maxHeight: 300, borderRadius: 8 }} />
                            ) : (
                              <Image src={socialMediaPreview} height={260} fit="contain" radius="md" />
                            )}
                          </Box>
                        ) : (
                          <Flex h={220} style={{ border: '2px dashed #cbd5e1', borderRadius: 12,  }} align="center" justify="center" direction="column" c="dimmed">
                            <IconVideo size={48} stroke={1.2} color="#94a3b8"/>
                            <Text size="sm" mt="sm" fw={500}>{t('media_drop_zone')}</Text>
                          </Flex>
                        )}
                      </Grid.Col>

                      <Grid.Col span={6}>
                        <Stack gap="md">
                          <Box>
                            <Group justify="space-between" mb={6}>
                              <Text size="sm" fw={600}>{t('social_caption_label')}</Text>
                              <Button
                                variant="light"
                                color="indigo"
                                size="xs"
                                leftSection={<IconBolt size={14}/>}
                                onClick={() => {
                                  setSocialCaption("🔥 Rewolucja w Twojej firmie dzięki sztucznej inteligencji od AILinkB! Zapomnij o ręcznej obsłudze e-maili i pozyskiwaniu klientów. 🚀 Nasz inteligentny system AI zarządza sprzedażą 24/7 na automatycznym pilocie! 👉 Kliknij link u nas na profilu i przetestuj za DARMO! \n\n#AI #Biznes #Automatyzacja #AILinkB #Innowacje #Sukces #Rozwój");
                                }}
                              >
                                {t('social_ai_caption_btn')}
                              </Button>
                            </Group>
                            <Textarea
                              minRows={6}
                              placeholder={t('social_caption_placeholder')}
                              value={socialCaption}
                              onChange={(e) => setSocialCaption(e.target.value)}
                            />
                          </Box>

                          <Divider label={t('social_target_label')} labelPosition="center" />

                          <SimpleGrid cols={2} spacing="md">
                            {[
                              { id: 'TikTok', title: '🎵 TikTok Reels', token: socialConnectors.tiktokToken, color: 'dark' },
                              { id: 'Facebook', title: '📘 Facebook Meta', token: socialConnectors.facebookToken, color: 'blue' },
                              { id: 'Instagram', title: '📸 Instagram Feed', token: socialConnectors.instagramToken, color: 'pink' },
                              { id: 'YouTube', title: '📺 YouTube Shorts', token: socialConnectors.youtubeToken, color: 'red' },
                            ].map((chan) => (
                              <Paper key={chan.id} withBorder p="sm" radius="md" style={{ background: selectedSocialPlatforms.includes(chan.id) ? '#f0fdf4' : '#ffffff', borderColor: selectedSocialPlatforms.includes(chan.id) ? '#22c55e' : '#e2e8f0' }}>
                                <Group justify="space-between" wrap="nowrap">
                                  <Checkbox
                                    label={<Text fw={700} size="sm">{chan.title}</Text>}
                                    checked={selectedSocialPlatforms.includes(chan.id)}
                                    onChange={(e) => {
                                      const checked = e.currentTarget.checked;
                                      if (checked) {
                                        setSelectedSocialPlatforms([...selectedSocialPlatforms, chan.id]);
                                      } else {
                                        setSelectedSocialPlatforms(selectedSocialPlatforms.filter(p => p !== chan.id));
                                      }
                                    }}
                                  />
                                  <Badge size="xs" color={chan.token ? "green" : "gray"} variant="light">
                                    {chan.token ? t('social_connected') : t('social_not_connected')}
                                  </Badge>
                                </Group>
                              </Paper>
                            ))}
                          </SimpleGrid>

                          <Button
                            size="lg"
                            color="grape"
                            radius="md"
                            fullWidth
                            mt="sm"
                            leftSection={socialPublishing ? <Loader size={18} color="white"/> : <IconRocket size={20}/>}
                            onClick={handleOmniPublish}
                            disabled={socialPublishing}
                            style={{ boxShadow: '0 4px 14px rgba(168, 85, 247, 0.4)' }}
                          >
                            {socialPublishing ? t('social_publishing_inprogress') : t('social_publish_btn')}
                          </Button>
                        </Stack>
                      </Grid.Col>
                    </Grid>
                  </Card>
                )}

                {socialSubTab === 'skill_videos' && (
                  <Card withBorder shadow="md" radius="md" p="xl">
                    <Title order={4} mb="md" c="indigo.8">{t('social_skill_videos_title')}</Title>
                    {socialSkillVideos.length === 0 ? (
                      <Text c="dimmed">{t('social_skill_videos_empty')}</Text>
                    ) : (
                      <Grid gutter="md">
                        {socialSkillVideos.map(skill => (
                          <Grid.Col span={4} key={skill.id}>
                            <Card withBorder shadow="sm" radius="md" p="md">
                              <Text fw={600} mb="xs" truncate>{skill.title}</Text>
                              <video controls src={(skill.videoUrl?.startsWith('http') ? '' : API_BASE_URL) + skill.videoUrl} style={{ width: '100%', maxHeight: 200, borderRadius: 8, marginBottom: 12 }} />
                              <Button 
                                color="indigo" 
                                fullWidth 
                                leftSection={<IconUpload size={16}/>}
                                onClick={() => {
                                  setSocialMediaPreview((skill.videoUrl?.startsWith('http') ? '' : API_BASE_URL) + skill.videoUrl);
                                  setSocialMediaType('VIDEO');
                                  setSocialCaption(`Sprawdź mój nowy AI Skill: ${skill.title}\n\n${skill.description}\n\n#AI #Tech #AILinkB`);
                                  setSocialSubTab('upload');
                                }}
                              >
                                {t('social_publish_this_video', 'Publikuj to Wideo')}
                              </Button>
                            </Card>
                          </Grid.Col>
                        ))}
                      </Grid>
                    )}
                  </Card>
                )}

                {socialSubTab === 'connectors' && (
                  <Card withBorder shadow="md" radius="md" p="xl">
                    <Group justify="space-between" mb="lg">
                      <Box>
                        <Title order={4} c="blue.9">{t('social_tab_connectors')}</Title>
                        <Text size="sm" c="dimmed">{t('social_connectors_desc')}</Text>
                      </Box>
                      <Button color="blue" leftSection={<IconPlug size={16}/>} onClick={handleSaveSocialConnectors}>
                        {t('social_save_connectors')}
                      </Button>
                    </Group>

                    <Grid gutter="md">
                      <Grid.Col span={6}>
                        <Paper withBorder p="md" radius="md" style={{ borderLeft: '5px solid #000' }}>
                          <Group justify="space-between" mb="xs">
                            <Title order={5}>🎵 TikTok API Connector</Title>
                            <Badge color={socialConnectors.tiktokToken ? "green" : "gray"}>{socialConnectors.tiktokToken ? t('social_connected') : t('social_not_connected')}</Badge>
                          </Group>
                          <TextInput label="TikTok Client Key" placeholder="aw... (App ID)" mb="sm" value={socialConnectors.tiktokKey || ''} onChange={e => setSocialConnectors({...socialConnectors, tiktokKey: e.target.value})} />
                          <PasswordInput label="TikTok Client Secret" placeholder="Client Secret" mb="sm" value={socialConnectors.tiktokSecret || ''} onChange={e => setSocialConnectors({...socialConnectors, tiktokSecret: e.target.value})} />
                          <TextInput label="Advertiser ID / Username" placeholder="@twojfirmowytiktok" mb="sm" value={socialConnectors.tiktokAccount || ''} onChange={e => setSocialConnectors({...socialConnectors, tiktokAccount: e.target.value})} />
                          <Button fullWidth variant="filled" color="blue" onClick={() => {
                            if (!socialConnectors.tiktokKey) {
                              alert("Wpisz najpierw Client Key i zapisz ustawienia!");
                              return;
                            }
                            const redirectUri = window.location.origin + '/';
                            window.location.href = `https://www.tiktok.com/v2/auth/authorize/?client_key=${socialConnectors.tiktokKey}&response_type=code&scope=video.publish,user.info.basic&redirect_uri=${encodeURIComponent(redirectUri)}&state=123`;
                          }}>Zaloguj się do TikToka (Pobierz Token)</Button>
                        </Paper>
                      </Grid.Col>

                      <Grid.Col span={6}>
                        <Paper withBorder p="md" radius="md" style={{ borderLeft: '5px solid #1877F2' }}>
                          <Group justify="space-between" mb="xs">
                            <Title order={5}>📘 Facebook Meta Graph API</Title>
                            <Badge color={socialConnectors.facebookToken ? "green" : "gray"}>{socialConnectors.facebookToken ? t('social_connected') : t('social_not_connected')}</Badge>
                          </Group>
                          <TextInput label="Meta Page Access Token" placeholder="EAA..." mb="sm" value={socialConnectors.facebookToken || ''} onChange={e => setSocialConnectors({...socialConnectors, facebookToken: e.target.value})} />
                          <TextInput label="Facebook Page ID" placeholder="1029384756" value={socialConnectors.facebookPageId || ''} onChange={e => setSocialConnectors({...socialConnectors, facebookPageId: e.target.value})} />
                        </Paper>
                      </Grid.Col>

                      <Grid.Col span={6}>
                        <Paper withBorder p="md" radius="md" style={{ borderLeft: '5px solid #E4405F' }}>
                          <Group justify="space-between" mb="xs">
                            <Title order={5}>📸 Instagram Graph API Connector</Title>
                            <Badge color={socialConnectors.instagramToken ? "green" : "gray"}>{socialConnectors.instagramToken ? t('social_connected') : t('social_not_connected')}</Badge>
                          </Group>
                          <TextInput label="Instagram Graph API Token" placeholder="EAA... / IG Secret" mb="sm" value={socialConnectors.instagramToken || ''} onChange={e => setSocialConnectors({...socialConnectors, instagramToken: e.target.value})} />
                          <TextInput label={t("instagram_business_account_id", "Instagram Business Account ID")} placeholder="178414000000000" value={socialConnectors.instagramAccountId || ''} onChange={e => setSocialConnectors({...socialConnectors, instagramAccountId: e.target.value})} />
                        </Paper>
                      </Grid.Col>

                      <Grid.Col span={6}>
                        <Paper withBorder p="md" radius="md" style={{ borderLeft: '5px solid #FF0000' }}>
                          <Group justify="space-between" mb="xs">
                            <Title order={5}>📺 YouTube Data API v3 (Shorts & Wideo)</Title>
                            <Badge color={socialConnectors.youtubeToken ? "green" : "gray"}>{socialConnectors.youtubeToken ? t('social_connected') : t('social_not_connected')}</Badge>
                          </Group>
                          {socialConnectors.youtubeToken === 'OAUTH_CONNECTED' ? (
                            <Text size="sm" c="green" mb="sm" fw={500}>✅ Konto Google (YouTube) jest autoryzowane.</Text>
                          ) : (
                            <Button 
                              fullWidth 
                              variant="filled" 
                              color="red" 
                              mb="sm" 
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API_BASE_URL}/api/google/oauth/url?email=${user.email}&originTab=social`);
                                  if (res.ok) {
                                    const data = await res.json();
                                    window.location.href = data.url;
                                  }
                                } catch (e) {
                                  console.error(e);
                                }
                              }}
                            >
                              Zaloguj się do Google (Pobierz Token)
                            </Button>
                          )}
                          <TextInput label="YouTube Channel ID / Handle" placeholder="UCxxxxxxx / @TwoyKanal" value={socialConnectors.youtubeChannelId || ''} onChange={e => setSocialConnectors({...socialConnectors, youtubeChannelId: e.target.value})} />
                        </Paper>
                      </Grid.Col>
                    </Grid>
                  </Card>
                )}

                {socialSubTab === 'comments' && (
                  <Card withBorder shadow="md" radius="md" p="xl">
                    <Group justify="space-between" mb="lg" align="flex-start">
                      <Box style={{ maxWidth: 680 }}>
                        <Group gap="xs" mb={4}>
                          <Badge color="red" variant="unstyled">AUTO-HEART & LIKE (❤️/👍)</Badge>
                          <Badge color="dark" variant="unstyled">SPAM & HATE FIREWALL</Badge>
                        </Group>
                        <Title order={4} c="indigo.9">{t('social_comments_title')}</Title>
                        <Text size="sm" c="dimmed">{t('social_comments_desc')}</Text>
                      </Box>
                      <Button
                        size="md"
                        color="indigo"
                        variant="gradient"
                        gradient={{ from: 'indigo', to: 'grape' }}
                        leftSection={commentsScanning ? <Loader size={16} color="white"/> : <IconShieldLock size={20}/>}
                        onClick={handleScanSocialComments}
                        disabled={commentsScanning}
                        style={{ boxShadow: '0 4px 14px rgba(79, 70, 229, 0.4)' }}
                      >
                        {commentsScanning ? t('scanning_ai') : t('social_comments_scan_btn')}
                      </Button>
                    </Group>

                    <Paper withBorder p="md" radius="md" mb="xl" style={{ borderLeft: '5px solid #6366f1' }}>
                      <Title order={5} mb="sm" c="dark.9">⚙️ Centrum Konfiguracji i Kontroli Użytkownika</Title>
                      <Stack gap="md">
                        <Group justify="space-between" wrap="nowrap">
                          <Box style={{ flex: 1 }}>
                            <Text fw={700} size="sm">{t('social_toggle_autoban_label')}</Text>
                            <Text size="xs" c="dimmed">{t('social_toggle_autoban_desc')}</Text>
                          </Box>
                          <Switch size="md" color="red" checked={socialAutoBan} onChange={(e) => handleToggleAutoBan(e.currentTarget.checked)} label={socialAutoBan ? "AUTO-BAN WŁĄCZONY" : "DECYZJA RĘCZNA"} />
                        </Group>
                        <Divider />
                        <Group justify="space-between" wrap="nowrap">
                          <Box style={{ flex: 1 }}>
                            <Text fw={700} size="sm">{t('social_toggle_approval_label')}</Text>
                            <Text size="xs" c="dimmed">{t('social_toggle_approval_desc')}</Text>
                          </Box>
                          <Switch size="md" color="indigo" checked={socialRequireApproval} onChange={(e) => handleToggleRequireApproval(e.currentTarget.checked)} label={socialRequireApproval ? "WYMAGANE ZATWIERDZENIE" : "PEŁNY AUTOPILOT AI"} />
                        </Group>
                      </Stack>
                    </Paper>

                    <SegmentedControl
                      value={socialCommentsView}
                      onChange={setSocialCommentsView}
                      mb="md"
                      size="sm"
                      data={[
                        { value: 'active', label: <Text fw={600}>{t('social_comments_tab_active')}</Text> },
                        { value: 'banned', label: <Text fw={600} c="red">{t('social_comments_tab_banned')}</Text> },
                      ]}
                    />

                    {socialCommentsView === 'active' && (
                      Array.isArray(socialComments) && socialComments.filter(c => c.aiAction !== 'BLOCKED_AND_DELETED').length > 0 ? (
                        <Stack gap="md">
                          {socialComments.filter(c => c.aiAction !== 'BLOCKED_AND_DELETED').map(c => (
                            <Paper key={c.id} withBorder p="md" radius="md" style={{ borderLeft: c.sentiment === 'POSITIVE' ? '5px solid #22c55e' : c.aiAction === 'FLAGGED_FOR_MODERATION' ? '5px solid #f97316' : '5px solid #64748b', background: c.aiAction === 'PENDING_APPROVAL' ? '#fffbeb' : c.aiAction === 'FLAGGED_FOR_MODERATION' ? '#fff7ed' : '#ffffff' }}>
                              <Group justify="space-between" mb="xs">
                                <Group gap="sm">
                                  <Badge color={c.platform === 'TikTok' ? 'dark' : c.platform === 'Facebook' ? 'blue' : c.platform === 'Instagram' ? 'pink' : 'red'} variant="unstyled" size="sm">
                                    {c.platform}
                                  </Badge>
                                  <Text fw={700} size="sm">{t('social_author')} {c.authorUsername}</Text>
                                  <Text size="xs" c="dimmed">Post: {c.postTitle}</Text>
                                </Group>
                                <Group gap="xs">
                                  <Badge color={c.sentiment === 'POSITIVE' ? 'green' : c.sentiment === 'SPAM' ? 'yellow' : 'red'} variant="outline">
                                    {t('social_sentiment')} {c.sentiment}
                                  </Badge>
                                  <Badge color={c.aiAction === 'REPLIED_AND_LIKED' ? 'green' : c.aiAction === 'PENDING_APPROVAL' ? 'yellow' : 'orange'} variant="unstyled">
                                    {c.aiAction === 'REPLIED_AND_LIKED' ? t('social_action_replied_liked') : c.aiAction === 'PENDING_APPROVAL' ? t('social_status_pending_approval') : t('social_status_flagged_mod')}
                                  </Badge>
                                </Group>
                              </Group>
                              
                              <Box mb="sm" p="sm" style={{ borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                <Text size="sm" fs="italic" c="dark.9">"{c.content}"</Text>
                              </Box>

                              {c.aiAction === 'PENDING_APPROVAL' ? (
                                <Box mt="sm" p="sm" style={{ background: '#ffffff', borderRadius: 8, border: '1px solid #fde68a' }}>
                                  <Text size="xs" fw={700} c="amber.9" mb="4px">PROPOZYCJA ODPOWIEDZI AI (Możesz edytować przed wysłaniem):</Text>
                                  <Textarea size="sm" defaultValue={c.aiReplyText} id={`reply-edit-${c.id}`} minRows={2} mb="xs" />
                                  <Group justify="flex-end" gap="xs">
                                    <Button size="xs" color="gray" variant="subtle" onClick={() => fetch(`${API_BASE_URL}/api/social/comments/${c.id}`, {method: 'DELETE'}).then(fetchSocialPosts)}>
                                      {t('social_btn_ignore')}
                                    </Button>
                                    <Button size="xs" color="green" leftSection="✓ ❤️" onClick={() => {
                                      const el = document.getElementById(`reply-edit-${c.id}`);
                                      const text = el ? el.value : c.aiReplyText;
                                      handleApproveCommentReply(c.id, text);
                                    }}>
                                      {t('social_btn_approve_reply')}
                                    </Button>
                                  </Group>
                                </Box>
                              ) : c.aiAction === 'FLAGGED_FOR_MODERATION' ? (
                                <Box mt="sm" p="sm" style={{ background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca' }}>
                                  <Text size="xs" fw={700} c="red.9" mb="xs">{c.aiReplyText}</Text>
                                  <Group justify="flex-end" gap="xs">
                                    <Button size="xs" color="gray" variant="light" onClick={() => fetch(`${API_BASE_URL}/api/social/comments/${c.id}`, {method: 'DELETE'}).then(fetchSocialPosts)}>
                                      {t('social_btn_ignore')}
                                    </Button>
                                    <Button size="xs" color="red" leftSection="🛑" onClick={() => handleExecuteCommentBan(c.id)}>
                                      {t('social_btn_ban_user')}
                                    </Button>
                                  </Group>
                                </Box>
                              ) : (
                                <Group gap="xs">
                                  <ThemeIcon color="green" size="sm" radius="xl">❤️</ThemeIcon>
                                  <Text size="sm" fw={600} c="green.9">{c.aiReplyText}</Text>
                                </Group>
                              )}
                            </Paper>
                          ))}
                        </Stack>
                      ) : (
                        <Flex h={200} style={{ border: '2px dashed #cbd5e1', borderRadius: 12,  }} align="center" justify="center" direction="column" c="dimmed">
                          <IconShieldLock size={40} stroke={1.2} color="#94a3b8"/>
                          <Text mt="sm" fw={500}>{t('social_no_comments')}</Text>
                        </Flex>
                      )
                    )}

                    {socialCommentsView === 'banned' && (
                      Array.isArray(socialComments) && socialComments.filter(c => c.aiAction === 'BLOCKED_AND_DELETED').length > 0 ? (
                        <Stack gap="md">
                          {socialComments.filter(c => c.aiAction === 'BLOCKED_AND_DELETED').map(c => (
                            <Paper key={c.id} withBorder p="md" radius="md" style={{ borderLeft: '5px solid #dc2626', background: '#fef2f2' }}>
                              <Group justify="space-between" mb="xs">
                                <Group gap="sm">
                                  <Badge color={c.platform === 'TikTok' ? 'dark' : c.platform === 'Facebook' ? 'blue' : c.platform === 'Instagram' ? 'pink' : 'red'} variant="unstyled" size="sm">
                                    {c.platform}
                                  </Badge>
                                  <Text fw={800} size="sm" c="red.9">🚫 ZABLOKOWANE KONTO: {c.authorUsername}</Text>
                                </Group>
                                <Group gap="xs">
                                  <Badge color="red" variant="unstyled">{t('social_banned_user_notice')}</Badge>
                                  <Button size="xs" color="gray" variant="light" onClick={() => fetch(`${API_BASE_URL}/api/social/comments/${c.id}`, {method: 'DELETE'}).then(fetchSocialPosts)}>
                                    🗑️ Usuń wpis z archiwum
                                  </Button>
                                </Group>
                              </Group>
                              <Text size="xs" c="dimmed" mb="4px">Usunięta treść (Spam / Hejt):</Text>
                              <Box mb="xs" p="xs" style={{ background: '#ffffff', borderRadius: 6, border: '1px solid #fca5a5' }}>
                                <Text size="sm" fs="italic" c="red.9" style={{ textDecoration: 'line-through' }}>"{c.content}"</Text>
                              </Box>
                              <Text size="xs" fw={600} c="dark.7">{c.aiReplyText}</Text>
                            </Paper>
                          ))}
                        </Stack>
                      ) : (
                        <Flex h={200} style={{ border: '2px dashed #fecaca', borderRadius: 12, background: '#fef2f2' }} align="center" justify="center" direction="column" c="dimmed">
                          <Text fw={600} c="red.8">{t('social_no_banned_logs')}</Text>
                        </Flex>
                      )
                    )}

                  </Card>
                )}

                {socialSubTab === 'history' && (
                  <Card withBorder shadow="sm" radius="md" p="lg">
                    <Group justify="space-between" mb="lg">
                      <Title order={4}>{t('social_tab_history')}</Title>
                      <Button 
                        color="indigo" 
                        leftSection={<IconRocket size={16}/>} 
                        onClick={() => {
                          fetch(`${API_BASE_URL}/api/social/generate`, {method: 'POST'})
                            .then(() => { alert(t('gen_success')); fetchSocialPosts(); });
                        }}
                      >
                        {t('gen_new_posts')}
                      </Button>
                    </Group>
                    
                    <ScrollArea>
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Platforma</Table.Th>
                            <Table.Th>Data Utworzenia</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Treść (Fragment)</Table.Th>
                            <Table.Th>Akcje</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {Array.isArray(socialPosts) && socialPosts.map(post => (
                            <Table.Tr key={post.id}>
                              <Table.Td>
                                <Badge color={post.platform === 'TikTok' ? 'dark' : post.platform === 'Facebook' ? 'blue' : post.platform === 'Instagram' ? 'pink' : 'red'} variant="unstyled">
                                  {post.platform}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">{post.createdAt ? new Date(post.createdAt).toLocaleString() : ''}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge color={post.status === 'POSTED' ? 'green' : 'gray'} variant="light">
                                  {post.status === 'POSTED' ? 'PUBLICATION SENT' : post.status}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" lineClamp={2} style={{ maxWidth: 250 }}>{post.content}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Group gap="xs">
                                  <Button size="xs" variant="light" color="indigo" onClick={() => setSocialHistoryPreview(post)}>👁️ Podgląd</Button>
                                  <ActionIcon variant="outline" color="red" onClick={() => fetch(`${API_BASE_URL}/api/social/${post.id}`, {method: 'DELETE'}).then(fetchSocialPosts)}>
                                    <IconTrash size={16}/>
                                  </ActionIcon>
                                </Group>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                    
                    <Modal opened={!!socialHistoryPreview} onClose={() => setSocialHistoryPreview(null)} title="Podgląd Publikacji" size="lg">
                      {socialHistoryPreview && (
                        <Box>
                          <Group justify="space-between" mb="xs">
                            <Badge color={socialHistoryPreview.platform === 'TikTok' ? 'dark' : socialHistoryPreview.platform === 'Facebook' ? 'blue' : socialHistoryPreview.platform === 'Instagram' ? 'pink' : 'red'} variant="unstyled">
                              {socialHistoryPreview.platform}
                            </Badge>
                            <Badge color={socialHistoryPreview.status === 'POSTED' ? 'green' : 'gray'} variant="light">
                              {socialHistoryPreview.status === 'POSTED' ? 'PUBLICATION SENT' : socialHistoryPreview.status}
                            </Badge>
                          </Group>
                          
                          {socialHistoryPreview.mediaType === 'VIDEO' && socialHistoryPreview.mediaUrl && !socialHistoryPreview.mediaUrl.includes('TRUNCATED_FOR_DB') ? (
                            <video controls src={socialHistoryPreview.mediaUrl} style={{ width: '100%', maxHeight: 400, borderRadius: 8, marginBottom: 16 }} />
                          ) : socialHistoryPreview.mediaType === 'IMAGE' && socialHistoryPreview.mediaUrl && !socialHistoryPreview.mediaUrl.includes('TRUNCATED_FOR_DB') ? (
                            <Image src={socialHistoryPreview.mediaUrl} height={300} fit="contain" radius="md" mb="md" />
                          ) : socialHistoryPreview.mediaType ? (
                            <Badge color="grape" mb="md">{socialHistoryPreview.mediaType} MATERIAL UPLOADED</Badge>
                          ) : null}
                          
                          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{socialHistoryPreview.content}</Text>
                          
                          <Button variant="light" color="blue" fullWidth mt="xl" onClick={() => { navigator.clipboard.writeText(socialHistoryPreview.content); alert('Skopiowano treść!'); }}>
                            Skopiuj treść
                          </Button>
                        </Box>
                      )}
                    </Modal>

                    {Array.isArray(socialPosts) && socialPosts.length === 0 && (
                      <Flex h={300} align="center" justify="center" direction="column" c="dimmed">
                        <IconRocket size={48} stroke={1}/>
                        <Text mt="md">{t('no_social_posts')}</Text>
                      </Flex>
                    )}
                  </Card>
                )}

              </Stack>
            </Container>
          </ScrollArea>
        )}

        {activeTab === 'reports' && (
          <ScrollArea h="100%">
            <Stack gap="xl" p="md">
              <Group justify="space-between">
                <Title order={3}>AI Business Intelligence Reports</Title>
                <Button 
                  color="indigo" 
                  leftSection={<IconRocket size={16}/>} 
                  onClick={() => {
                    fetch(`${API_BASE_URL}/api/reports/generate`, {method: 'POST'})
                      .then(() => { alert("Report generation started!"); fetchReports(); });
                  }}
                >
                  Generate New Report
                </Button>
              </Group>

              <Grid gutter="xl">
                <Grid.Col span={4}>
                  <Card withBorder shadow="sm" radius="md">
                    <Title order={5} mb="md">Report History</Title>
                    <Stack gap="xs">
                      {Array.isArray(reports) && reports.map(r => (
                        <Box 
                          key={r.id} 
                          onClick={() => setSelectedReport(r)}
                          p="sm" 
                          style={{ 
                            cursor: 'pointer', 
                            borderRadius: '8px', 
                            background: selectedReport?.id === r.id ? '#eef2ff' : 'white',
                            border: selectedReport?.id === r.id ? '1px solid #4f46e5' : '1px solid #eee'
                          }}
                        >
                          <Text fw={600} size="sm">{r.title}</Text>
                          <Text size="xs" c="dimmed">{new Date(r.generatedAt).toLocaleString()}</Text>
                        </Box>
                      ))}
                    </Stack>
                  </Card>
                </Grid.Col>
                
                <Grid.Col span={8}>
                  {selectedReport ? (
                    <Card withBorder shadow="sm" radius="md" p="xl">
                      {(() => {
                        const data = JSON.parse(selectedReport.content);
                        return (
                          <Stack gap="xl">
                            <Group justify="space-between">
                              <Title order={2} c="indigo">{data.title}</Title>
                              <Badge size="lg">{new Date(selectedReport.generatedAt).toLocaleDateString()}</Badge>
                            </Group>
                            
                            <Box p="lg" bg="#141517" style={{ borderRadius: '12px', borderLeft: '5px solid #4f46e5' }}>
                              <Text style={{ fontStyle: 'italic', fontSize: '1.1rem' }}>{data.summary}</Text>
                            </Box>

                            <SimpleGrid cols={3}>
                              {Object.entries(data.metrics || {}).map(([key, val]) => (
                                <Card key={key} withBorder p="md" radius="md" bg="blue.0">
                                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{key}</Text>
                                  <Text size="xl" fw={700}>{val}</Text>
                                </Card>
                              ))}
                            </SimpleGrid>

                            <Grid>
                              <Grid.Col span={6}>
                                <Title order={4} mb="md">🔍 Key Findings</Title>
                                <List spacing="sm" size="sm" center icon={<ThemeIcon color="blue" size={20} radius="xl"><IconSearch size={12} /></ThemeIcon>}>
                                  {(data.findings || []).map((f, i) => <List.Item key={i}>{f}</List.Item>)}
                                </List>
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <Title order={4} mb="md">🚀 Recommendations</Title>
                                <List spacing="sm" size="sm" center icon={<ThemeIcon color="green" size={20} radius="xl"><IconRocket size={12} /></ThemeIcon>}>
                                  {(data.recommendations || []).map((r, i) => <List.Item key={i}>{r}</List.Item>)}
                                </List>
                              </Grid.Col>
                            </Grid>
                          </Stack>
                        );
                      })()}
                    </Card>
                  ) : (
                    <Flex h={400} align="center" justify="center" direction="column" c="dimmed">
                      <IconSearch size={48} stroke={1}/>
                      <Text mt="md">Select a report from the list or generate a new one.</Text>
                    </Flex>
                  )}
                </Grid.Col>
              </Grid>
            </Stack>
          </ScrollArea>
        )}

                {activeTab === 'publish' && (
          <Box p="md">
            <PublishSkillCard user={user} />
          </Box>
        )}
        
        {activeTab === 'knowledge-base' && (
          <MySkillsTab user={user} />
        )}


        {activeTab === 'admin' && (
          <AdminPanel user={user} apiBaseUrl={API_BASE_URL} />
        )}
        {activeTab === 'agents' && (
          <AgentManager />
        )}
        {activeTab === 'seo-audit' && (
          <AdminSeoAuditTab user={user} apiBaseUrl={API_BASE_URL} setActiveTab={setActiveTab} />
        )}
        {activeTab === 'phone-calls' && (
          <ScrollArea h="100%">
            <Container size="xl" p="md">
              <Box mb="md">
                <Title order={4} mb="xs"><IconPhone size={20} style={{ verticalAlign: 'middle', marginRight: 8 }}/>{t('phone_calls_title')}</Title>
                
                <Tabs defaultValue="config">
                  <Tabs.List mb="md">
                    <Tabs.Tab value="config" leftSection={<IconSettings size={16}/>}>{t('tab_voice_config')}</Tabs.Tab>
                    <Tabs.Tab value="history" leftSection={<IconList size={16}/>}>{t('tab_voice_history')}</Tabs.Tab>
                  </Tabs.List>
                  
                  <Tabs.Panel value="config">
                    <PhoneCallsTab user={user} API_BASE_URL={API_BASE_URL} />
                  </Tabs.Panel>
                  
                  <Tabs.Panel value="history">
                    <Group mb="md">
                  <TextInput
                    placeholder={t('search_by_number')}
                    value={searchPhone}
                    onChange={(e) => setSearchPhone(e.target.value)}
                    style={{ flex: 1, maxWidth: 300 }}
                    onKeyDown={(e) => { if (e.key === 'Enter') fetchPhoneCalls(); }}
                  />
                  <Button onClick={fetchPhoneCalls}><IconSearch size={16} /></Button>
                </Group>
                
                <Paper withBorder shadow="sm" radius="md">
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('phone_call_number')}</Table.Th>
                        <Table.Th>{t('phone_call_date')}</Table.Th>
                        <Table.Th>{t('phone_call_duration')}</Table.Th>
                        <Table.Th>{t('phone_call_summary')}</Table.Th>
                        <Table.Th></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {phoneCalls.map(call => (
                        <Table.Tr key={call.id}>
                          <Table.Td>
                            {call.leadId ? (
                              <Button variant="subtle" size="xs" onClick={() => { setActiveTab('leads'); setSelectedLead(leads.find(l => l.id === call.leadId)); }}>
                                {call.callerNumber} (Customer #{call.leadId})
                              </Button>
                            ) : (
                              call.callerNumber
                            )}
                          </Table.Td>
                          <Table.Td>{new Date(call.startedAt).toLocaleString()}</Table.Td>
                          <Table.Td>{call.durationSeconds}s</Table.Td>
                          <Table.Td>
                            <Text size="sm" lineClamp={2}>{call.summary}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Button size="xs" variant="light" onClick={() => setSelectedPhoneCall(call)}>{t('btn_view_report')}</Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                      {phoneCalls.length === 0 && (
                        <Table.Tr>
                          <Table.Td colSpan={5} align="center">No phone calls found.</Table.Td>
                        </Table.Tr>
                      )}
                    </Table.Tbody>
                  </Table>
                </Paper>
                  </Tabs.Panel>
                </Tabs>
              </Box>
            </Container>

            <Modal opened={!!selectedPhoneCall} onClose={() => setSelectedPhoneCall(null)} title={t('phone_calls_title')} size="lg">
              {selectedPhoneCall && (
                <Stack>
                  <Group>
                    <Text fw={700}>{t('phone_call_number')}:</Text>
                    <Text>{selectedPhoneCall.callerNumber}</Text>
                  </Group>
                  <Group>
                    <Text fw={700}>{t('phone_call_date')}:</Text>
                    <Text>{new Date(selectedPhoneCall.startedAt).toLocaleString()}</Text>
                  </Group>
                  <Group>
                    <Text fw={700}>{t('phone_call_duration')}:</Text>
                    <Text>{selectedPhoneCall.durationSeconds}s</Text>
                  </Group>
                  <Divider />
                  <Text fw={700}>{t('phone_call_summary')}:</Text>
                  <Text>{selectedPhoneCall.summary}</Text>
                  <Divider />
                  <Text fw={700}>{t('phone_call_transcript')}:</Text>
                  <Paper p="sm" withBorder style={{ maxHeight: 300, overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                      {selectedPhoneCall.transcript}
                    </pre>
                  </Paper>
                </Stack>
              )}
            </Modal>
          </ScrollArea>
        )}
        {activeTab === 'ad-campaigns' && (
          <AdCampaignsTab user={user} apiBaseUrl={API_BASE_URL} t={t} />
        )}
        {activeTab === 'announcements' && (
          <AnnouncementsTab user={user} apiBaseUrl={API_BASE_URL} t={t} />
        )}
        {activeTab === 'jobs' && (
          <JobOffersTab apiBaseUrl={API_BASE_URL} t={t} setActiveTab={setActiveTab} />
        )}
        {activeTab === 'bug_report' && (
          <BugReportTab user={user} apiBaseUrl={API_BASE_URL} t={t} />
        )}
        {activeTab === 'mcp-server' && (
          <MCPServerTab user={user} API_BASE_URL={API_BASE_URL} />
        )}

        {activeTab === 'system-logs' && (
          <AiLogsViewer user={user} apiBaseUrl={API_BASE_URL} />
        )}

        {activeTab === 'events' && (
          <EventsManager userEmail={user?.email} />
        )}
          </>
        )}
        </ScrollArea>
      </AppShell.Main>

      <Modal 
        opened={plansModalOpened || isAccountDeactivated} 
        onClose={() => { if (!isAccountDeactivated) setPlansModalOpened(false); }} 
        closeOnClickOutside={!isAccountDeactivated}
        closeOnEscape={!isAccountDeactivated}
        withCloseButton={!isAccountDeactivated}
        title={isAccountDeactivated ? <Title order={4} c="red.7">{t('trial_expired_title')}</Title> : <Title order={4} c="indigo.8">{t('btn_activate_subscription')}</Title>}
        size="xl" 
        centered
        radius="lg"
      >
        <Stack gap="md" p="xs">
          <Group justify="space-between">
            {isAccountDeactivated ? (
              <Button size="xs" variant="subtle" color="red" leftSection={<IconLogout size={14} />} onClick={handleLogout}>
                {t('logout')}
              </Button>
            ) : <Box />}
            <Select 
              value={i18n.language?.substring(0, 2) || 'en'}
              onChange={(val) => i18n.changeLanguage(val)}
              data={[
                { value: 'en', label: 'English' },
                { value: 'pl', label: 'Polski' },
                { value: 'de', label: 'Deutsch' },
                { value: 'fr', label: 'Français' },
                { value: 'es', label: 'Español' },
                { value: 'it', label: 'Italiano' },
                { value: 'pt', label: 'Português' },
                { value: 'nl', label: 'Nederlands' },
                { value: 'sv', label: 'Svenska' },
                { value: 'cs', label: 'Čeština' }
              ]}
              style={{ width: '120px' }}
              size="xs"
            />
          </Group>
          {isAccountDeactivated && (
            <Alert color="red" title={t('trial_expired_title')} icon={<IconShieldLock size={20}/>}>
              {t('trial_expired_desc')}
            </Alert>
          )}



          <Grid>
            <Grid.Col span={{ base: 12, md: 8 }} offset={{ md: 2 }}>
              <Card withBorder shadow="sm" p="lg" radius="md" h="100%" style={{ display: 'flex', flexDirection: 'column', justify: 'space-between', border: '1px solid #cbd5e1' }}>
                <Box>
                  <Group justify="space-between" mb="xs">
                    <Title order={4}>{t('plan_basic_title')}</Title>
                    <Badge color="blue" variant="light">BASIC</Badge>
                  </Group>
                  <Text size="28px" fw={900} c="blue.7" mb="md">{t('plan_basic_price')}</Text>
                  <Text size="sm" c="dimmed" mb="md">{t('plan_basic_desc')}</Text>
                  
                  <Stack gap="xs" mb="xl">
                    <Text size="sm" fw={500}>✅ {t('plan_basic_f1')}</Text>
                    <Text size="sm" fw={500}>✅ {t('plan_basic_f2')}</Text>
                    <Text size="sm" fw={500}>✅ {t('plan_basic_f3')}</Text>
                    <Text size="sm" c="dimmed">🔒 {t('plan_basic_f4')}</Text>
                  </Stack>
                </Box>

                <Button fullWidth size="md" color="blue" loading={isUpgrading} variant={user?.subscriptionPlan === 'BASIC_100' && user?.subscriptionStatus === 'ACTIVE' ? 'light' : 'filled'} onClick={() => handleUpgradePlan('BASIC_100')} disabled={user?.subscriptionPlan === 'BASIC_100' && user?.subscriptionStatus === 'ACTIVE'}>
                  {user?.subscriptionPlan === 'BASIC_100' && user?.subscriptionStatus === 'ACTIVE' ? t('btn_active_current') : `${t('btn_activate_subscription')} (100 PLN + 23% VAT)`}
                </Button>
              </Card>
            </Grid.Col>

            {/* 
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card withBorder shadow="lg" p="lg" radius="md" h="100%" style={{ display: 'flex', flexDirection: 'column', justify: 'space-between', border: '2px solid #4f46e5', position: 'relative',  }}>
                <Badge color="yellow" variant="unstyled" size="sm" style={{ position: 'absolute', top: '10px', right: '15px' }}>ZALECANE / MAX AI</Badge>
                <Box>
                  <Group justify="space-between" mb="xs">
                    <Title order={4} c="indigo.9">{t('plan_pro_title')}</Title>
                    <Badge color="indigo" variant="unstyled">PRO</Badge>
                  </Group>
                  <Text size="28px" fw={900} c="indigo.7" mb="md">{t('plan_pro_price')}</Text>
                  <Text size="sm" c="dimmed" mb="md">{t('plan_pro_desc')}</Text>
                  
                  <Stack gap="xs" mb="xl">
                    <Text size="sm" fw={600}>✅ {t('plan_pro_f1')}</Text>
                    <Text size="sm" fw={700} c="indigo.8">⚡ {t('plan_pro_f2')}</Text>
                    <Text size="sm" fw={700} c="indigo.8">⚡ {t('plan_pro_f3')}</Text>
                    <Text size="sm" fw={700} c="indigo.8">⚡ {t('plan_pro_f4')}</Text>
                    <Text size="sm" fw={700} c="indigo.8">⚡ {t('plan_pro_f5')}</Text>
                  </Stack>
                </Box>

                <Button fullWidth size="md" color="indigo" loading={isUpgrading} variant={isProUser && user?.subscriptionStatus === 'ACTIVE' && !isAdmin ? 'light' : 'gradient'} gradient={{ from: 'indigo', to: 'violet' }} onClick={() => handleUpgradePlan('PRO_2000')} disabled={isProUser && user?.subscriptionStatus === 'ACTIVE' && !isAdmin}>
                  {isProUser && user?.subscriptionStatus === 'ACTIVE' && !isAdmin ? t('btn_active_current') : t('btn_upgrade_to_pro')}
                </Button>
              </Card>
            </Grid.Col>
            */}
          </Grid>
        </Stack>
      </Modal>

      <Drawer opened={historyOpened} onClose={() => setHistoryOpened(false)} title={`History: ${historyLead?.companyName}`} position="right" size="lg">
        <ScrollArea h="calc(100vh - 80px)">
          <Stack>
            {emailHistory.length === 0 ? <Text c="dimmed" ta="center" mt="xl">No history.</Text> : emailHistory.map(entry => (
              <Card key={entry.id} withBorder shadow="sm">
                <Group justify="space-between" mb="xs"><Badge color="blue" variant="light">{new Date(entry.sentAt).toLocaleString()}</Badge><Text size="sm" fw={600}>{entry.subject}</Text></Group>
                <Divider mb="sm" />
                <Box style={{ height: '400px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                  <EmailPreviewIframe html={entry.body} />
                </Box>
              </Card>
            ))}
          </Stack>
        </ScrollArea>
      </Drawer>
      <AppShell.Footer px="md" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0' }}>
        <Text size="10px" c="dimmed">
          Copyright © {new Date().getFullYear()} AILinkB. QR-SYSTEM sp. z.o.o. Płaza ul. Graniczna 13B Kapitał zakładowy 10 000 PLN.
        </Text>
        <Text size="10px" fw={700} c="dimmed" style={{ letterSpacing: '1px' }}>
          {dashboardStats.vectorizedMessages}/{dashboardStats.totalMessages} CRM | {dashboardStats.unreadWhatsApp || 0} WA | {dashboardStats.totalLeads} CUST
        </Text>
      </AppShell.Footer>
      
      {isSyncing && (
        <Affix position={{ bottom: 20, right: 20 }} zIndex={9999}>
          <Alert color="blue" title={t('sync_in_background_title')} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <Group>
               <Loader size="sm" color="blue" />
               <Text size="sm">{syncMessage}</Text>
            </Group>
          </Alert>
        </Affix>
      )}
      
      <CeidgImportModal 
        opened={ceidgModalOpen} 
        onClose={() => setCeidgModalOpen(false)} 
        user={user} 
        apiBaseUrl={API_BASE_URL} 
        onImportSuccess={() => { setCeidgModalOpen(false); setLeadsCategoryFilter('poland_lead'); setPage(1); fetchLeads(); }} 
      />
    </AppShell>
  );
}

function CookieConsent() {
  const { t, i18n } = useTranslation();
  const [accepted, setAccepted] = useState(localStorage.getItem('cookieConsent') === 'true');

  if (accepted) return null;

  return (
    <Modal 
      opened={!accepted} 
      onClose={() => {}} 
      withCloseButton={false} 
      closeOnClickOutside={false} 
      closeOnEscape={false}
      centered
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    >
      <Stack gap="sm" p="sm">
        <Group justify="flex-end">
          <Select 
            value={i18n.language?.substring(0, 2) || 'en'}
            onChange={(val) => i18n.changeLanguage(val)}
            data={[
              { value: 'en', label: 'English' },
              { value: 'pl', label: 'Polski' },
              { value: 'de', label: 'Deutsch' },
              { value: 'fr', label: 'Français' },
              { value: 'es', label: 'Español' },
              { value: 'it', label: 'Italiano' },
              { value: 'pt', label: 'Português' },
              { value: 'nl', label: 'Nederlands' },
              { value: 'sv', label: 'Svenska' },
              { value: 'cs', label: 'Čeština' }
            ]}
            style={{ width: '120px' }}
            size="xs"
          />
        </Group>
        <Text size="lg" fw={700} ta="center">
          {t('cookie_title')}
        </Text>
        <Text size="sm" c="dimmed" ta="center">
          {t('cookie_desc')}
        </Text>
        <Button fullWidth mt="md" size="md" color="indigo" onClick={() => {
          localStorage.setItem('cookieConsent', 'true');
          setAccepted(true);
        }}>
          {t('cookie_accept')}
        </Button>
      </Stack>
    </Modal>
  );
}

function LanguageSwitcher() {
  const { i18n } = useTranslation();
  return (
    <Select 
      value={i18n.language?.substring(0, 2) || 'en'}
      onChange={(val) => i18n.changeLanguage(val)}
      data={[
        { value: 'en', label: 'EN' },
        { value: 'pl', label: 'PL' },
        { value: 'de', label: 'DE' },
        { value: 'fr', label: 'FR' },
        { value: 'es', label: 'ES' },
        { value: 'it', label: 'IT' },
        { value: 'pt', label: 'PT' },
        { value: 'nl', label: 'NL' },
        { value: 'sv', label: 'SV' },
        { value: 'cs', label: 'CS' }
      ]}
      size="xs"
      variant="unstyled"
      style={{ width: '70px' }}
      styles={{ 
        input: { cursor: 'pointer', backgroundColor: '#1a1b1e', color: '#fff', border: '1px solid #333', padding: '0 10px', borderRadius: '4px' },
        dropdown: { backgroundColor: '#1a1b1e', color: '#fff', border: '1px solid #333' },
        option: { color: '#fff' }
      }}
    />
  );
}

export default function App() {
  return (
    <Fragment>
      <CoreApp />
      <CookieConsent />
    </Fragment>
  );
}
















